/**
 * Vehicle Search — full search results page controller.
 *
 * Supports two modes on /search:
 * - Vehicle mode: ?make=X&model=Y&trim=Z → shows compatible wheels
 * - Text mode: ?q=JR36 → free-text search across brand/model/name
 * Both can be combined.
 */

import { getVehicle, saveVehicle, clearVehicle } from './vehicle-store.js';

const API_BASE_FALLBACK = 'https://admin.sbfwheels.no';

class VehicleSearchElement extends HTMLElement {
  connectedCallback() {
    this._api = API_BASE_FALLBACK;
    this._pageSize = parseInt(this.dataset.pageSize, 10) || 24;
    this._defaultBrand = this.dataset.defaultBrand || '';
    this._brandSelection = this._defaultBrand;
    this._modelSelection = '';

    // DOM refs
    this._header = this.querySelector('[data-role="header"]');
    this._headerSelector = this.querySelector('[data-role="header-selector"]');
    this._headerVehicle = this.querySelector('[data-role="header-vehicle"]');
    this._vehicleLabel = this.querySelector('[data-role="vehicle-label"]');
    this._grid = this.querySelector('[data-role="grid"]');
    this._loading = this.querySelector('[data-role="loading"]');
    this._empty = this.querySelector('[data-role="empty"]');
    this._countEl = this.querySelector('[data-role="count"]');
    this._pagination = this.querySelector('[data-role="pagination"]');
    this._diameterBar = this.querySelector('[data-role="diameter-bar"]');
    this._ensureProfessionalSearchStyles();
    this._sortSelect = this.querySelector('[data-role="sort"]');
    this._searchInput = this.querySelector('[data-role="search-input"]');
    this._searchBar = this.querySelector('[data-role="search-bar"]');

    // Inline vehicle selector refs
    this._hMake = this.querySelector('[data-role="h-make"]');
    this._hModel = this.querySelector('[data-role="h-model"]');
    this._hYear = this.querySelector('[data-role="h-year"]');
    this._hTrim = this.querySelector('[data-role="h-trim"]');
    this._hSearch = this.querySelector('[data-role="h-search"]');

    // Filter selects (dropdowns that remain as <select>)
    this._filters = {
      size: this.querySelector('[data-filter="size"]'),
      width: this.querySelector('[data-filter="width"]'),
      pcd: this.querySelector('[data-filter="pcd"]'),
    };

    // Accordion checkbox filters (replacing color/brand/model selects)
    this._accordions = {
      color: this.querySelector('[data-accordion="color"]'),
      brand: this.querySelector('[data-accordion="brand"]'),
      wheel_model: this.querySelector('[data-accordion="wheel_model"]'),
    };


    // Mobile-first brand selector, result toolbar and responsive layout helpers.
    this._initProfessionalSearchUi();

    // Range filter inputs
    this._rangeInputs = {
      min_et: this.querySelector('[data-filter="min_et"]'),
      max_et: this.querySelector('[data-filter="max_et"]'),
      min_bore: this.querySelector('[data-filter="min_bore"]'),
      max_bore: this.querySelector('[data-filter="max_bore"]'),
      min_price: this.querySelector('[data-filter="min_price"]'),
      max_price: this.querySelector('[data-filter="max_price"]'),
    };

    // Range sliders (ET and Navhull)
    this._sliders = {};
    for (const el of this.querySelectorAll('[data-slider]')) {
      const key = el.dataset.slider;
      this._sliders[key] = {
        container: el,
        thumbMin: el.querySelector('[data-role="thumb-min"]'),
        thumbMax: el.querySelector('[data-role="thumb-max"]'),
        fill: el.querySelector('[data-role="slider-fill"]'),
        valMin: el.querySelector('[data-role="slider-min"]'),
        valMax: el.querySelector('[data-role="slider-max"]'),
        rec: el.querySelector('[data-role="slider-rec"]'),
        recRange: el.querySelector('[data-role="slider-rec-range"]'),
      };
    }

    // Specs panel
    this._specsPanel = this.querySelector('[data-role="specs-panel"]');
    this._specsTable = this.querySelector('[data-role="specs-table"]');
    this._specsToggle = this.querySelector('[data-role="toggle-specs"]');

    // Axle filter (sidebar only)
    this._axleFilter = this.querySelector('[data-role="axle-filter"]');

    // Mobile filter drawer
    const filterToggle = this.querySelector('[data-role="filter-toggle"]');
    const filterBackdrop = this.querySelector('[data-role="filter-backdrop"]');
    const filterClose = this.querySelector('[data-role="filter-close"]');
    this._sidebarCount = this.querySelector('[data-role="sidebar-count"]');
    const sidebar = this.querySelector('[data-role="sidebar"]');
    if (filterToggle && sidebar) {
      const open = () => { sidebar.classList.add('vs__sidebar--open'); if (filterBackdrop) filterBackdrop.hidden = false; };
      const close = () => { sidebar.classList.remove('vs__sidebar--open'); if (filterBackdrop) filterBackdrop.hidden = true; };
      filterToggle.addEventListener('click', open);
      filterClose?.addEventListener('click', close);
      filterBackdrop?.addEventListener('click', close);
    }
    // Reset button(s) — header icon + NULLSTILL button
    const resetHandler = () => {
      Object.values(this._filters).forEach((sel) => { if (sel) sel.value = ''; });
      this._resetAccordions();
      this._modelSelection = '';
      this._brandSelection = this._defaultBrand || '';
      this._showAllBrands = false;
      this._mobileBrandChosenByUser = false;
      this._updateMobileBrandTrigger();
      if (this._axleSelectSidebar) this._axleSelectSidebar.value = 'all';
      this._axle = 'all';
      this._selectedDiameter = null;
      this._selectedDiameterIsCustom = false;
      this._selectedOemSetKey = '';
      this._resetOemFallbackState();
      if (this._vehicle?.specs) {
        this._buildDiameterButtons(this._vehicle.specs, this._vehicleSpecs);
      } else {
        this._diameterBar.querySelectorAll('.vs__diam-btn').forEach((b) => b.classList.remove('vs__diam-btn--active'));
      }
      this._page = 1;
      this._search();
    };
    this.querySelectorAll('[data-role="filter-reset"]').forEach(btn => btn.addEventListener('click', resetHandler));

    // Shared vehicle data arrays
    this._makes = [];
    this._models = [];
    this._trims = [];

    // State
    this._page = 1;
    this._sort = '';
    this._selectedDiameter = null;
    this._selectedDiameterIsCustom = false;
    this._selectedOemSetKey = '';
    this._oemFallbackContextKey = '';
    this._oemFallbackTriedDiameters = new Set();
    this._zeroResultOemSetKeys = new Set();
    this._vehicle = null;
    this._vehicleSpecs = null;
    this._queryText = '';
    this._axle = 'all';
    this._slidersInitialized = false;
    this._rangeDebounce = null;
    // Vehicle searches open in the Aventador Wheels premium collection by default.
    // This flag becomes true only when the customer explicitly chooses to see all brands.
    this._showAllBrands = false;

    // Cache and request guard for brand/vehicle-specific diameter availability.
    // This prevents OEM size buttons with zero matching products from being shown.
    this._diameterAvailabilityCache = new Map();
    this._diameterAvailabilityRequestId = 0;
    this._diameterAvailabilityResolvedKey = '';

    // Only the newest product search may update the UI.
    this._searchRequestId = 0;

    // Cache exact Shopify product images so the same handle is never fetched
    // repeatedly while filtering, paging or switching colours.
    this._exactFinishImageCache = new Map();

    // Cache supplemental Custom Finish rows per active brand/diameter.
    this._customFinishRowsCache = new Map();

    // Bind events
    this._sortSelect.addEventListener('change', () => {
      this._sort = this._sortSelect.value;
      this._page = 1;
      this._search();
    });
    // Size filter — special handler to sync with diameter buttons
    if (this._filters.size) {
      this._filters.size.addEventListener('change', () => {
        const sizeVal = parseInt(this._filters.size.value, 10);
        if (sizeVal) {
          const matchingDiameterButton = [...this._diameterBar.querySelectorAll(`[data-diam="${sizeVal}"]`)]
            .find((button) => !button.hidden && !button.disabled)
            || this._diameterBar.querySelector(`[data-diam="${sizeVal}"]`);
          if (matchingDiameterButton) {
            this._selectDiameterButton(matchingDiameterButton);
          } else {
            this._selectedDiameter = sizeVal;
            this._selectedDiameterIsCustom = false;
            this._selectedOemSetKey = '';
            this._syncDiameterButtonActiveState();
          }
        } else if (this._vehicle?.specs) {
          // A vehicle search must always retain one OEM choice.
          const preferredOemButton = this._getAvailableOemButtons()[0];
          if (preferredOemButton) this._selectDiameterButton(preferredOemButton);
        } else {
          this._selectedDiameter = null;
          this._selectedDiameterIsCustom = false;
          this._selectedOemSetKey = '';
          this._syncDiameterButtonActiveState();
        }
        this._resetOemFallbackState();
        this._customFinishRowsCache?.clear();
        this._page = 1;
        this._search();
      });
    }
    // Other filters — generic handler (dropdowns only: pcd, width)
    Object.entries(this._filters).forEach(([key, sel]) => {
      if (key === 'size' || !sel) return;
      sel.addEventListener('change', () => {
        this._page = 1;
        this._search();
      });
    });

    // Accordion toggle (expand/collapse)
    this.querySelectorAll('.vs__accordion-toggle').forEach(btn => {
      btn.addEventListener('click', () => {
        const body = btn.nextElementSibling;
        body.classList.toggle('is-collapsed');
        btn.querySelector('.vs__accordion-chevron').classList.toggle('is-rotated');
      });
    });

    // Accordion checkbox change — trigger search
    Object.entries(this._accordions).forEach(([key, acc]) => {
      if (!acc) return;
      acc.addEventListener('change', (e) => {
        if (e.target.type !== 'checkbox') return;
        // Brand→Model cascade: clear model checkboxes when brand changes
        if (key === 'brand') {
  this._modelSelection = '';

  // Brand is single-select. Selecting one brand removes all others before
  // the request is sent.
  if (e.target.checked) {
    acc.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      if (checkbox !== e.target) checkbox.checked = false;
    });
  }

  const checkedBrands = [
    ...acc.querySelectorAll('input[type="checkbox"]:checked')
  ].map(cb => cb.value);

  this._brandSelection =
    checkedBrands.length === 1 ? checkedBrands[0] : '';
  this._showAllBrands = checkedBrands.length === 0;

  const selectedNonAventadorBrand =
    checkedBrands.length > 0 &&
    !checkedBrands.includes('Aventador Wheels');

  // A brand switch must never inherit stale Custom/Aventador size state.
  // Rebuild the diameter selector from the vehicle before checking the new brand.
  if (selectedNonAventadorBrand) {
    this._selectedDiameter = null;
    this._selectedDiameterIsCustom = false;
    this._selectedOemSetKey = '';

    if (this._filters?.size) this._filters.size.value = '';

    this._resetOemFallbackState();
    this._diameterAvailabilityResolvedKey = '';
    this._diameterAvailabilityRequestId += 1;
    this._diameterAvailabilityCache.clear();

    if (this._vehicle?.specs) {
      this._buildDiameterButtons(this._vehicle.specs, this._vehicleSpecs);
    }
  }

  const modelAcc = this._accordions.wheel_model;

  if (modelAcc) {
    modelAcc
      .querySelectorAll('input[type="checkbox"]')
      .forEach(cb => {
        cb.checked = false;
      });
  }

  this._updateMobileBrandTrigger();
}
        this._page = 1;
        this._search();
      });
    });

    // Accordion search input (Modell) — uses event delegation on the accordion
    this.querySelectorAll('[data-role="accordion-search"]').forEach(searchInput => {
      searchInput.addEventListener('input', () => {
        const accKey = searchInput.closest('[data-accordion]')?.dataset.accordion;
        if (accKey) this._applyCheckboxFilter(accKey);
      });
    });

    // Range inputs — search on Enter or debounced input
    Object.values(this._rangeInputs).forEach((input) => {
      if (!input) return;
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          clearTimeout(this._rangeDebounce);
          this._page = 1;
          this._search();
        }
      });
      input.addEventListener('input', () => {
        clearTimeout(this._rangeDebounce);
        this._rangeDebounce = setTimeout(() => {
          this._page = 1;
          this._search();
        }, 600);
      });
    });

    // Range slider events — sync thumbs, fill bar, hidden inputs, trigger search
    this._initSlider('et', 'min_et', 'max_et');
    this._initSlider('bore', 'min_bore', 'max_bore');
    this._initSlider('price', 'min_price', 'max_price');

    // Axle toggle (sidebar select only)
    this._axleSelectSidebar = this.querySelector('[data-role="axle-select-sidebar"]');
    if (this._axleSelectSidebar) {
      this._axleSelectSidebar.addEventListener('change', () => {
        this._axle = this._axleSelectSidebar.value;
        this._selectedDiameter = null;
        this._selectedDiameterIsCustom = false;
        this._selectedOemSetKey = '';
        this._resetOemFallbackState();
        this._slidersInitialized = false;
        this._page = 1;
        const specs = this._vehicle?.specs;
        if (specs) this._buildDiameterButtons(specs, this._vehicleSpecs);
        this._search();
      });
    }

    this.querySelector('[data-role="change-vehicle"]')?.addEventListener(
      'click', () => this._changeVehicle()
    );
    this.querySelector('[data-role="clear-vehicle"]')?.addEventListener(
      'click', () => this._clearVehicle()
    );
    this._specsToggle?.addEventListener('click', () => this._toggleSpecsPanel());

    // Delegated click handler for diameter bar (once, avoids stacking)
    this._diameterBar.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-diam]');
      if (!btn || btn.disabled || btn.hidden) return;

      // Keep one choice selected at all times. Clicking the active choice does
      // not clear it; it simply remains active.
      this._selectDiameterButton(btn);
      this._resetOemFallbackState();
      this._customFinishRowsCache?.clear();
      this._updateMobileBrandTrigger();
      this._page = 1;
      this._search();
    });

    // Search input — submit on Enter or button click
    if (this._searchInput) {
      this._searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this._onTextSearch();
        }
      });
    }
    this.querySelector('[data-role="search-btn"]')?.addEventListener(
      'click', () => this._onTextSearch()
    );

    // Regnr refs (header)
    this._hRegnr = this.querySelector('[data-role="h-regnr"]');
    this._hRegnrSearch = this.querySelector('[data-role="h-regnr-search"]');
    if (this._hRegnr) {
      this._hRegnr.addEventListener('input', () => {
        const val = this._hRegnr.value.replace(/[\s-]/g, '');
        if (this._hRegnrSearch) {
          this._hRegnrSearch.disabled = val.length < 4;
          if (val.length >= 4) this._hRegnrSearch.dataset.revealed = '';
          else delete this._hRegnrSearch.dataset.revealed;
        }
      });
      this._hRegnr.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !this._hRegnrSearch?.disabled) this._onRegnrSearch();
      });
    }
    if (this._hRegnrSearch) this._hRegnrSearch.addEventListener('click', () => this._onRegnrSearch());

    // Inline vehicle selector events
    this._initInlineSelector();
    this._updateMobileSteps();
    this._init();
  }

  _ensureProfessionalSearchStyles() {
    const styleId = 'vs-professional-search-ui-v7';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* ---------- Diameter selector ---------- */
      vehicle-search [data-role="diameter-bar"] {
        display: flex !important;
        align-items: center !important;
        gap: 8px !important;
        flex-wrap: wrap !important;
        margin: 8px 0 14px !important;
        padding: 0 !important;
        background: transparent !important;
        border: 0 !important;
        box-shadow: none !important;
      }

      vehicle-search .vs-pro-diam-btn {
        position: relative !important;
        width: 64px !important;
        min-width: 64px !important;
        height: 54px !important;
        padding: 7px 8px 6px !important;
        margin: 0 !important;
        display: inline-flex !important;
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 5px !important;
        border: 1px solid #d8dde3 !important;
        border-radius: 8px !important;
        background: #fff !important;
        color: #111 !important;
        outline: 0 !important;
        box-shadow: 0 1px 2px rgba(15,23,42,.035) !important;
        appearance: none !important;
        -webkit-appearance: none !important;
        cursor: pointer !important;
        overflow: hidden !important;
        line-height: 1 !important;
        transition: border-color .16s ease, background .16s ease, box-shadow .16s ease !important;
      }

      vehicle-search .vs-pro-diam-btn::before,
      vehicle-search .vs-pro-diam-btn::after {
        content: none !important;
        display: none !important;
      }

      vehicle-search .vs-pro-diam-btn:hover {
        border-color: #c79b3b !important;
        background: #fffdf8 !important;
        box-shadow: 0 4px 14px rgba(15,23,42,.06) !important;
      }

      vehicle-search .vs-pro-diam-value,
      vehicle-search .vs-pro-diam-label {
        display: block !important;
        width: auto !important;
        height: auto !important;
        min-width: 0 !important;
        min-height: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
        border: 0 !important;
        border-radius: 0 !important;
        background: transparent !important;
        box-shadow: none !important;
        line-height: 1 !important;
      }

      vehicle-search .vs-pro-diam-value {
        color: #111 !important;
        font-size: 16px !important;
        font-weight: 800 !important;
        letter-spacing: -.02em !important;
      }

      vehicle-search .vs-pro-diam-label {
        color: #111 !important;
        font-size: 9px !important;
        font-weight: 750 !important;
        letter-spacing: .035em !important;
        text-transform: uppercase !important;
      }

      vehicle-search .vs-pro-diam-sublabel {
        display: block !important;
        width: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
        background: transparent !important;
        color: #6b7280 !important;
        font-size: 8px !important;
        font-weight: 700 !important;
        letter-spacing: .03em !important;
        text-transform: uppercase !important;
        line-height: 1 !important;
      }

      vehicle-search .vs__diam-btn--custom .vs-pro-diam-label {
        color: #b27b0d !important;
      }

      vehicle-search .vs__diam-btn--set {
        width: 126px !important;
        min-width: 126px !important;
        height: 62px !important;
        gap: 3px !important;
      }

      vehicle-search .vs__diam-btn--set .vs-pro-diam-value {
        font-size: 14px !important;
      }

      vehicle-search .vs-pro-diam-check {
        display: none !important;
        position: absolute !important;
        top: 4px !important;
        right: 6px !important;
        width: auto !important;
        height: auto !important;
        min-width: 0 !important;
        min-height: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
        align-items: center !important;
        justify-content: center !important;
        border: 0 !important;
        border-radius: 0 !important;
        background: transparent !important;
        color: #1db56d !important;
        font-family: Arial, sans-serif !important;
        font-size: 15px !important;
        font-weight: 900 !important;
        line-height: 1 !important;
        box-shadow: none !important;
        text-shadow: none !important;
      }

      vehicle-search .vs-pro-diam-btn.vs__diam-btn--active {
        border-color: #25b96f !important;
        background: #f2fbf6 !important;
        box-shadow: inset 0 0 0 1px rgba(37,185,111,.10), 0 3px 12px rgba(15,23,42,.04) !important;
      }

      vehicle-search .vs-pro-diam-btn.vs__diam-btn--active .vs-pro-diam-check {
        display: inline-flex !important;
      }

      vehicle-search .vs-pro-diam-btn.is-unavailable {
        display: none !important;
      }

      /* ---------- Specifications toggle ---------- */
      vehicle-search .vs-pro-specs-label-wrap {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 7px !important;
        white-space: nowrap !important;
      }

      vehicle-search .vs-pro-specs-chevron {
        width: 7px !important;
        height: 7px !important;
        display: inline-block !important;
        flex: 0 0 auto !important;
        border-right: 1.8px solid currentColor !important;
        border-bottom: 1.8px solid currentColor !important;
        transform: rotate(45deg) translateY(-1px) !important;
        transform-origin: center !important;
        transition: transform .18s ease !important;
      }

      vehicle-search [data-role="toggle-specs"][aria-expanded="true"] .vs-pro-specs-chevron,
      vehicle-search [data-vs-pro-action="specs"][aria-expanded="true"] .vs-pro-specs-chevron {
        transform: rotate(-135deg) translate(-1px, -1px) !important;
      }

      vehicle-search [data-vs-pro-action="specs"][aria-expanded="true"] {
        background: rgba(202,155,59,.16) !important;
        border-color: rgba(202,155,59,.95) !important;
      }

      /* ---------- Result toolbar ---------- */
      vehicle-search .vs-pro-results-toolbar {
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        gap: 12px !important;
        width: 100% !important;
        margin: 0 0 12px !important;
      }

      vehicle-search .vs-pro-results-toolbar [data-role="count"] {
        margin: 0 !important;
        color: #4b5563 !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        white-space: nowrap !important;
      }

      vehicle-search .vs-pro-results-toolbar [data-role="sort"] {
        width: auto !important;
        min-width: 138px !important;
        max-width: 180px !important;
        min-height: 38px !important;
        margin: 0 !important;
        padding: 7px 34px 7px 12px !important;
        border: 1px solid #d9dde2 !important;
        border-radius: 8px !important;
        background-color: #fff !important;
        box-sizing: border-box !important;
        font-size: 13px !important;
      }

      /* ---------- Product grid ---------- */
      vehicle-search [data-role="grid"] {
        display: grid !important;
        grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
        gap: 16px !important;
        align-items: stretch !important;
      }

      vehicle-search [data-role="grid"] .vs__card {
        min-width: 0 !important;
        height: 100% !important;
      }

      vehicle-search [data-role="grid"] .vs__card-img {
        width: 100% !important;
        aspect-ratio: 1 / 1 !important;
        object-fit: contain !important;
      }


      /* ---------- Aventador premium vehicle-search mode ---------- */
      vehicle-search.vs-aventador-mode {
        display: block !important;
        padding: 18px !important;
        border-radius: 0 !important;
        background: #080808 !important;
        color: #fff !important;
      }

      vehicle-search.vs-aventador-mode [data-role="header"] {
        border: 1px solid rgba(202,155,59,.28) !important;
        background: #151515 !important;
      }

      vehicle-search.vs-aventador-mode [data-role="diameter-bar"] {
        justify-content: center !important;
        margin: 15px 0 18px !important;
      }

      vehicle-search.vs-aventador-mode .vs-pro-diam-btn {
        border-color: rgba(202,155,59,.55) !important;
        background: #0d0d0d !important;
        color: #fff !important;
        box-shadow: none !important;
      }

      vehicle-search.vs-aventador-mode .vs-pro-diam-value {
        color: #fff !important;
      }

      vehicle-search.vs-aventador-mode .vs-pro-diam-label,
      vehicle-search.vs-aventador-mode .vs__diam-btn--custom .vs-pro-diam-label {
        color: #d7ac4b !important;
      }

      vehicle-search.vs-aventador-mode .vs-pro-diam-btn.vs__diam-btn--active {
        border-color: #e0bb5a !important;
        background: linear-gradient(135deg, #e2bf5e, #b98721) !important;
      }

      vehicle-search.vs-aventador-mode .vs-pro-diam-btn.vs__diam-btn--active .vs-pro-diam-value,
      vehicle-search.vs-aventador-mode .vs-pro-diam-btn.vs__diam-btn--active .vs-pro-diam-label {
        color: #080808 !important;
      }

      vehicle-search.vs-aventador-mode .vs-pro-diam-check {
        color: #07140d !important;
      }

      vehicle-search.vs-aventador-mode [data-role="sidebar"] {
        border-color: rgba(202,155,59,.20) !important;
        background: #0b0b0b !important;
        color: #fff !important;
      }

      vehicle-search.vs-aventador-mode [data-role="sidebar"] select,
      vehicle-search.vs-aventador-mode [data-role="sidebar"] input {
        border-color: rgba(202,155,59,.24) !important;
        background-color: #111 !important;
        color: #fff !important;
      }

      vehicle-search.vs-aventador-mode .vs__accordion-toggle,
      vehicle-search.vs-aventador-mode .vs__checkbox-name,
      vehicle-search.vs-aventador-mode .vs__checkbox-count {
        color: #fff !important;
      }

      vehicle-search.vs-aventador-mode .vs-pro-results-toolbar [data-role="count"] {
        color: #b8b8b8 !important;
      }

      vehicle-search.vs-aventador-mode .vs-pro-results-toolbar [data-role="sort"] {
        border-color: rgba(202,155,59,.28) !important;
        background-color: #101010 !important;
        color: #fff !important;
      }

      vehicle-search.vs-aventador-mode [data-role="grid"] {
        grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
        gap: 2px !important;
        border: 1px solid rgba(202,155,59,.22) !important;
        background: rgba(202,155,59,.22) !important;
      }

      .vs-aventador-hero {
        padding: 22px 12px 18px !important;
        text-align: center !important;
        background: #080808 !important;
      }

      .vs-aventador-hero__eyebrow {
        color: #d5a73c !important;
        font-size: 10px !important;
        font-weight: 800 !important;
        letter-spacing: .22em !important;
      }

      .vs-aventador-hero__title {
        margin: 8px 0 4px !important;
        color: #fff !important;
        font-size: clamp(24px, 3vw, 38px) !important;
        font-weight: 800 !important;
        letter-spacing: .14em !important;
      }

      .vs-aventador-hero__subtitle {
        margin: 0 !important;
        color: #9e7b28 !important;
        font-size: 10px !important;
        font-weight: 700 !important;
        letter-spacing: .14em !important;
      }

      .vs-aventador-hero__line {
        width: 46px !important;
        height: 1px !important;
        margin: 18px auto !important;
        background: #b78b28 !important;
      }

      .vs-aventador-hero__other {
        min-height: 38px !important;
        padding: 9px 18px !important;
        border: 1px solid #9e7722 !important;
        border-radius: 6px !important;
        background: transparent !important;
        color: #fff !important;
        font-size: 10px !important;
        font-weight: 800 !important;
        letter-spacing: .08em !important;
        cursor: pointer !important;
      }

      .vs-aventador-card {
        min-width: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        background: #0b0b0b !important;
        color: #fff !important;
        overflow: hidden !important;
      }

      .vs-aventador-card__media {
        position: relative !important;
        display: block !important;
        aspect-ratio: 1 / 1 !important;
        overflow: hidden !important;
        background: radial-gradient(circle at 50% 42%, #202020 0, #101010 42%, #090909 75%) !important;
      }

      .vs-aventador-card__image {
        width: 100% !important;
        height: 100% !important;
        display: block !important;
        object-fit: contain !important;
        transform: scale(1.02) !important;
        transition: transform .25s ease, opacity .18s ease !important;
      }

      .vs-aventador-card:hover .vs-aventador-card__image {
        transform: scale(1.055) !important;
      }

      .vs-aventador-card__content {
        min-height: 168px !important;
        padding: 15px 16px 17px !important;
        border-top: 1px solid rgba(202,155,59,.14) !important;
      }

      .vs-aventador-card__method {
        color: #c99a32 !important;
        font-size: 9px !important;
        font-weight: 800 !important;
        letter-spacing: .14em !important;
        text-transform: uppercase !important;
      }

      .vs-aventador-card__name {
        display: block !important;
        margin-top: 5px !important;
        color: #fff !important;
        font-size: 17px !important;
        font-weight: 800 !important;
        letter-spacing: .035em !important;
        text-decoration: none !important;
        text-transform: uppercase !important;
      }

      .vs-aventador-card__finish {
        margin-top: 4px !important;
        color: #d4d4d4 !important;
        font-size: 11px !important;
        text-transform: uppercase !important;
      }

      .vs-aventador-card__bottom {
        display: flex !important;
        align-items: flex-end !important;
        justify-content: space-between !important;
        gap: 12px !important;
        margin-top: 12px !important;
      }

      .vs-aventador-card__price {
        color: #fff !important;
        font-size: 15px !important;
        font-weight: 800 !important;
      }

      .vs-aventador-card__open {
        color: #d9aa3e !important;
        font-size: 10px !important;
        font-weight: 800 !important;
        letter-spacing: .08em !important;
        text-decoration: none !important;
        white-space: nowrap !important;
      }

      .vs-aventador-card__status {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        min-height: 30px !important;
        padding: 0 14px !important;
        border: 1px solid rgba(214,170,62,.85) !important;
        border-radius: 999px !important;
        background: rgba(7,7,7,.78) !important;
        font-size: 10.5px !important;
        font-weight: 800 !important;
        letter-spacing: .04em !important;
        line-height: 1 !important;
        white-space: nowrap !important;
        text-transform: uppercase !important;
        box-shadow: inset 0 0 0 1px rgba(255,255,255,.02) !important;
      }

      .vs-aventador-card__status--in {
        color: #44c06b !important;
      }

      .vs-aventador-card__status--order {
        color: #d9aa3e !important;
      }

      .vs-aventador-card__finishes {
        display: flex !important;
        align-items: center !important;
        gap: 7px !important;
        margin-top: 13px !important;
        overflow-x: auto !important;
        scrollbar-width: none !important;
      }

      .vs-aventador-card__finishes::-webkit-scrollbar {
        display: none !important;
      }

      .vs-aventador-finish {
        width: 28px !important;
        height: 28px !important;
        flex: 0 0 28px !important;
        padding: 2px !important;
        border: 1px solid rgba(255,255,255,.22) !important;
        border-radius: 50% !important;
        background: #151515 !important;
        cursor: pointer !important;
      }

      .vs-aventador-finish.is-active {
        border-color: #d8aa42 !important;
        box-shadow: 0 0 0 1px #d8aa42 !important;
      }

      .vs-aventador-finish img {
        width: 100% !important;
        height: 100% !important;
        display: block !important;
        border-radius: 50% !important;
        object-fit: cover !important;
      }

      .vs-aventador-finish__swatch {
        width: 100% !important;
        height: 100% !important;
        display: block !important;
        border-radius: 50% !important;
        background: linear-gradient(135deg, #3a3a3a, #050505) !important;
      }

      @media (max-width: 1120px) {
        vehicle-search.vs-aventador-mode [data-role="grid"] {
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        }
      }

      @media (max-width: 749px) {
        vehicle-search.vs-aventador-mode {
          padding: 10px !important;
        }

        vehicle-search.vs-aventador-mode [data-role="grid"] {
          grid-template-columns: 1fr !important;
        }

        .vs-aventador-hero {
          padding: 16px 6px 14px !important;
        }

        .vs-aventador-card__content {
          min-height: 150px !important;
        }
      }


      /* Keep “fra 7 999 kr” on one line */
      vehicle-search .vs-aventador-card__price {
        display: inline-block !important;
        white-space: nowrap !important;
        word-break: keep-all !important;
        overflow-wrap: normal !important;
        flex: 0 0 auto !important;
      }

      /* New-model badge */
      vehicle-search .vs-aventador-card__new-badge {
        position: absolute !important;
        top: 10px !important;
        left: 10px !important;
        z-index: 3 !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        min-height: 25px !important;
        padding: 0 10px !important;
        border: 1px solid #d9aa3e !important;
        border-radius: 999px !important;
        background: rgba(8,8,8,.88) !important;
        color: #ffffff !important;
        -webkit-text-fill-color: #ffffff !important;
        font-size: 9px !important;
        font-weight: 900 !important;
        letter-spacing: .10em !important;
        line-height: 1 !important;
        text-transform: uppercase !important;
        box-shadow: 0 3px 12px rgba(0,0,0,.35) !important;
      }

      @media (max-width: 749px) {
        vehicle-search .vs-aventador-card__price {
          font-size: 9px !important;
          letter-spacing: -0.01em !important;
        }

        vehicle-search .vs-aventador-card__bottom {
          flex-wrap: nowrap !important;
          align-items: center !important;
        }

        vehicle-search .vs-aventador-card__status {
          flex: 0 1 auto !important;
          min-width: 0 !important;
          padding-left: 6px !important;
          padding-right: 6px !important;
        }

        vehicle-search .vs-aventador-card__new-badge {
          top: 6px !important;
          left: 6px !important;
          min-height: 21px !important;
          padding: 0 7px !important;
          font-size: 7px !important;
        }
      }

      /* ---------- Mobile control stack ---------- */
      vehicle-search .vs-pro-mobile-controls {
        display: none;
      }

      /* ---------- Mobile brand sheet ---------- */
      .vs-pro-brand-backdrop {
        position: fixed !important;
        inset: 0 !important;
        z-index: 2147483000 !important;
        background: rgba(10,13,18,.48) !important;
        backdrop-filter: blur(2px) !important;
        opacity: 0 !important;
        pointer-events: none !important;
        transition: opacity .18s ease !important;
      }

      .vs-pro-brand-backdrop.is-open {
        opacity: 1 !important;
        pointer-events: auto !important;
      }

      .vs-pro-brand-sheet {
        position: fixed !important;
        left: 50% !important;
        bottom: 8px !important;
        z-index: 2147483001 !important;
        width: min(460px, calc(100vw - 16px)) !important;
        height: min(82vh, 720px) !important;
        max-height: calc(100vh - 20px) !important;
        display: flex !important;
        flex-direction: column !important;
        overflow: hidden !important;
        border: 1px solid rgba(15,23,42,.08) !important;
        border-radius: 18px !important;
        background: #fff !important;
        box-shadow: 0 24px 70px rgba(0,0,0,.26) !important;
        transform: translate(-50%, calc(100% + 28px)) !important;
        opacity: 0 !important;
        pointer-events: none !important;
        transition: transform .22s ease, opacity .18s ease !important;
      }

      .vs-pro-brand-sheet.is-open {
        transform: translate(-50%, 0) !important;
        opacity: 1 !important;
        pointer-events: auto !important;
      }

      .vs-pro-brand-sheet__handle {
        width: 38px !important;
        height: 4px !important;
        margin: 8px auto 2px !important;
        border-radius: 99px !important;
        background: #d7dbe0 !important;
      }

      .vs-pro-brand-sheet__header {
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        gap: 12px !important;
        padding: 10px 16px 8px !important;
      }

      .vs-pro-brand-sheet__title {
        margin: 0 !important;
        color: #111 !important;
        font-size: 18px !important;
        font-weight: 800 !important;
      }

      .vs-pro-brand-sheet__close {
        width: 36px !important;
        height: 36px !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        border: 0 !important;
        border-radius: 50% !important;
        background: #f3f4f6 !important;
        color: #111 !important;
        font-size: 22px !important;
        cursor: pointer !important;
      }

      .vs-pro-brand-sheet__scroll {
        flex: 1 1 auto !important;
        min-height: 0 !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
        -webkit-overflow-scrolling: touch !important;
        overscroll-behavior-y: contain !important;
        touch-action: pan-y !important;
        scrollbar-width: thin !important;
        padding-bottom: max(92px, env(safe-area-inset-bottom)) !important;
      }

      .vs-pro-brand-sheet__scroll::-webkit-scrollbar {
        width: 5px !important;
      }

      .vs-pro-brand-sheet__scroll::-webkit-scrollbar-thumb {
        border-radius: 99px !important;
        background: rgba(17,24,39,.22) !important;
      }

      .vs-pro-brand-sheet__list {
        min-height: 0 !important;
        overflow: visible !important;
        padding: 0 10px 10px !important;
      }

      .vs-pro-brand-sheet__section-title {
        padding: 10px 14px 6px !important;
        color: #6b7280 !important;
        font-size: 12px !important;
        font-weight: 800 !important;
        letter-spacing: .06em !important;
        text-transform: uppercase !important;
      }

      .vs-pro-brand-sheet__models {
        border-top: 1px solid #eceff2 !important;
        padding: 0 10px 14px !important;
        overflow: visible !important;
      }

      .vs-pro-brand-sheet__models-list {
        display: grid !important;
        grid-template-columns: 1fr !important;
      }

      .vs-pro-model-option {
        width: 100% !important;
        min-height: 44px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        gap: 10px !important;
        padding: 10px 12px !important;
        border: 0 !important;
        border-bottom: 1px solid #eceff2 !important;
        background: #fff !important;
        color: #111 !important;
        text-align: left !important;
        font-size: 14px !important;
        font-weight: 650 !important;
        cursor: pointer !important;
      }

      .vs-pro-model-option:last-child {
        border-bottom: 0 !important;
      }

      .vs-pro-model-option.is-selected {
        background: #f6fbf7 !important;
        color: #0f7c48 !important;
      }

      .vs-pro-model-option__mark {
        color: #1ba765 !important;
        font-size: 16px !important;
        font-weight: 900 !important;
      }

      .vs-pro-brand-sheet__empty {
        padding: 14px !important;
        color: #6b7280 !important;
        font-size: 13px !important;
      }

      .vs-pro-brand-sheet__scroll-hint {
        position: sticky !important;
        bottom: 0 !important;
        z-index: 2 !important;
        margin: 0 10px !important;
        padding: 8px 10px calc(8px + env(safe-area-inset-bottom)) !important;
        border-top: 1px solid rgba(229,231,235,.9) !important;
        background: linear-gradient(to bottom, rgba(255,255,255,.88), #fff 35%) !important;
        color: #6b7280 !important;
        text-align: center !important;
        font-size: 11px !important;
        font-weight: 650 !important;
      }

      .vs-pro-brand-option {
        width: 100% !important;
        min-height: 46px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        gap: 12px !important;
        padding: 10px 12px !important;
        border: 0 !important;
        border-bottom: 1px solid #eceff2 !important;
        border-radius: 0 !important;
        background: #fff !important;
        color: #111 !important;
        text-align: left !important;
        font-size: 14px !important;
        font-weight: 650 !important;
        cursor: pointer !important;
      }

      .vs-pro-brand-option:hover {
        background: #faf8f2 !important;
      }

      .vs-pro-brand-option.is-selected {
        background: #fff8e9 !important;
        color: #9d6c0b !important;
      }

      .vs-pro-brand-option__mark {
        color: #c38d1d !important;
        font-size: 17px !important;
        font-weight: 900 !important;
      }

      body.vs-pro-sheet-open {
        overflow: hidden !important;
      }

      @media (max-width: 1120px) and (min-width: 750px) {
        vehicle-search [data-role="grid"] {
          grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
        }
      }

      @media (max-width: 749px) {
        vehicle-search.vs-pro-has-vehicle [data-role="header"] {
          padding: 10px 12px 9px !important;
          border-radius: 12px !important;
          padding-bottom: 8px !important;
        }

        vehicle-search.vs-pro-has-vehicle [data-role="vehicle-label"] {
          font-size: 12px !important;
          font-weight: 800 !important;
          letter-spacing: .02em !important;
          line-height: 1.2 !important;
        }

        vehicle-search.vs-pro-has-vehicle [data-role="header"] [data-role="toggle-specs"],
        vehicle-search.vs-pro-has-vehicle [data-role="header"] [data-role="change-vehicle"],
        vehicle-search.vs-pro-has-vehicle [data-role="header"] [data-role="clear-vehicle"],
        vehicle-search.vs-pro-has-vehicle [data-role="header"] [data-role="filter-reset"] {
          display: none !important;
        }

        vehicle-search .vs-pro-mobile-controls {
          width: 100% !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 6px !important;
          margin-top: 7px !important;
        }

        vehicle-search .vs-pro-mobile-brand-trigger {
          width: 100% !important;
          min-height: 40px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          gap: 8px !important;
          padding: 8px 12px !important;
          border: 1px solid rgba(255,255,255,.14) !important;
          border-radius: 7px !important;
          background: rgba(255,255,255,.04) !important;
          color: #fff !important;
          box-sizing: border-box !important;
          font-size: 12px !important;
          font-weight: 700 !important;
          cursor: pointer !important;
        }

        vehicle-search .vs-pro-mobile-brand-trigger__left {
          min-width: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 9px !important;
        }

        vehicle-search .vs-pro-mobile-brand-trigger__label {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }

        vehicle-search .vs-pro-mobile-actions {
          display: grid !important;
          grid-template-columns: 1.2fr .9fr .9fr !important;
          gap: 6px !important;
        }

        vehicle-search .vs-pro-mobile-action {
          min-width: 0 !important;
          min-height: 38px !important;
          padding: 7px 6px !important;
          border: 1px solid rgba(255,255,255,.14) !important;
          border-radius: 7px !important;
          background: rgba(255,255,255,.04) !important;
          color: #fff !important;
          font-size: 10.5px !important;
          font-weight: 700 !important;
          line-height: 1.1 !important;
          cursor: pointer !important;
        }

        vehicle-search .vs-pro-mobile-action--specs {
          border-color: rgba(202,155,59,.75) !important;
        }

        /* Brand selector now lives in the black header. Hide the old mobile filter button. */
        vehicle-search.vs-pro-has-vehicle [data-role="filter-toggle"] {
          display: none !important;
        }

        /* Keep every diameter on one horizontal row. Swipe sideways only when needed. */
        vehicle-search [data-role="diameter-bar"] {
          display: flex !important;
          align-items: stretch !important;
          flex-wrap: nowrap !important;
          gap: 6px !important;
          width: 100% !important;
          max-width: 100% !important;
          margin: 10px 0 11px !important;
          padding: 0 0 3px !important;
          overflow-x: auto !important;
          overflow-y: hidden !important;
          scrollbar-width: none !important;
          -webkit-overflow-scrolling: touch !important;
          overscroll-behavior-inline: contain !important;
        }

        vehicle-search [data-role="diameter-bar"]::-webkit-scrollbar {
          display: none !important;
        }

        vehicle-search .vs-pro-diam-btn {
          width: 58px !important;
          min-width: 58px !important;
          flex: 0 0 58px !important;
          height: 52px !important;
          padding: 6px 4px !important;
          border-radius: 8px !important;
        }

        vehicle-search .vs-pro-diam-value {
          font-size: 14px !important;
        }

        vehicle-search .vs-pro-diam-label {
          font-size: 7.5px !important;
        }

        vehicle-search .vs-pro-diam-sublabel {
          font-size: 7px !important;
        }

        vehicle-search .vs__diam-btn--set {
          width: 104px !important;
          min-width: 104px !important;
          flex: 0 0 104px !important;
          height: 58px !important;
        }

        vehicle-search .vs__diam-btn--set .vs-pro-diam-value {
          font-size: 12px !important;
        }

        vehicle-search .vs-pro-diam-check {
          width: auto !important;
          height: auto !important;
          top: 4px !important;
          right: 6px !important;
          font-size: 14px !important;
        }

        vehicle-search .vs-pro-results-toolbar {
          gap: 8px !important;
          margin-bottom: 9px !important;
        }

        vehicle-search .vs-pro-results-toolbar [data-role="count"] {
          font-size: 12px !important;
        }

        vehicle-search .vs-pro-results-toolbar [data-role="sort"] {
          width: 128px !important;
          min-width: 128px !important;
          max-width: 128px !important;
          min-height: 36px !important;
          padding: 6px 28px 6px 9px !important;
          font-size: 12px !important;
        }

        vehicle-search [data-role="grid"] {
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          gap: 8px !important;
        }

        vehicle-search [data-role="grid"] .vs__card {
          border-radius: 8px !important;
          overflow: hidden !important;
        }

        vehicle-search [data-role="grid"] .vs__card-body {
          padding: 8px !important;
        }

        vehicle-search [data-role="grid"] .vs__card-brand {
          font-size: 8px !important;
          letter-spacing: .03em !important;
        }

        vehicle-search [data-role="grid"] .vs__card-name {
          font-size: 12px !important;
          line-height: 1.18 !important;
        }

        vehicle-search [data-role="grid"] .vs__card-colour,
        vehicle-search [data-role="grid"] .vs__card-specs {
          font-size: 9px !important;
          line-height: 1.25 !important;
        }

        vehicle-search [data-role="grid"] .vs__card-price {
          font-size: 12px !important;
        }

        vehicle-search [data-role="grid"] .vs__card-stock {
          max-width: 100% !important;
          padding: 3px 6px !important;
          font-size: 8px !important;
          white-space: nowrap !important;
        }

        vehicle-search [data-role="grid"] .vs__card-footer {
          gap: 4px !important;
          align-items: flex-end !important;
        }

        /* Compact translucent chooser, similar to the native vehicle dropdown. */
        .vs-pro-brand-sheet {
          top: 12vh !important;
          bottom: auto !important;
          width: min(520px, calc(100vw - 64px)) !important;
          height: auto !important;
          max-height: 72vh !important;
          border: 1px solid rgba(206,161,69,.35) !important;
          border-radius: 28px !important;
          background: rgba(25,22,20,.82) !important;
          -webkit-backdrop-filter: blur(24px) saturate(120%) !important;
          backdrop-filter: blur(24px) saturate(120%) !important;
          box-shadow: 0 24px 70px rgba(0,0,0,.34) !important;
          transform: translate(-50%, -18px) scale(.98) !important;
        }

        .vs-pro-brand-sheet.is-open {
          transform: translate(-50%, 0) scale(1) !important;
        }

        .vs-pro-brand-sheet__handle,
        .vs-pro-brand-sheet__header {
          display: none !important;
        }

        .vs-pro-brand-sheet__scroll {
          max-height: 72vh !important;
          padding: 10px 0 !important;
          color: #fff !important;
        }

        .vs-pro-brand-sheet__list,
        .vs-pro-brand-sheet__models {
          padding: 0 10px !important;
          border: 0 !important;
        }

        .vs-pro-brand-sheet__section-title {
          padding: 8px 18px 5px !important;
          color: rgba(255,255,255,.58) !important;
          font-size: 11px !important;
        }

        .vs-pro-brand-option,
        .vs-pro-model-option {
          min-height: 58px !important;
          padding: 12px 20px !important;
          border-bottom: 1px solid rgba(255,255,255,.08) !important;
          background: transparent !important;
          color: #fff !important;
          font-size: 18px !important;
          font-weight: 500 !important;
        }

        .vs-pro-brand-option:hover,
        .vs-pro-model-option:hover,
        .vs-pro-brand-option.is-selected,
        .vs-pro-model-option.is-selected {
          background: rgba(202,155,59,.12) !important;
          color: #fff !important;
        }

        .vs-pro-brand-option__mark,
        .vs-pro-model-option__mark {
          color: #fff !important;
          font-size: 20px !important;
        }

        .vs-pro-brand-sheet__empty {
          color: rgba(255,255,255,.72) !important;
        }
      }

      /* Final Aventador grid rules */
      @media (max-width: 749px) {
        vehicle-search.vs-aventador-mode [data-role="grid"],
        vehicle-search.vs-av-mode [data-role="grid"] {
          display: grid !important;
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          gap: 3px !important;
        }

        vehicle-search .vs-aventador-card {
          min-width: 0 !important;
          width: 100% !important;
        }

        vehicle-search .vs-aventador-card__content {
          min-height: 0 !important;
          padding: 7px !important;
        }

        vehicle-search .vs-aventador-card__name {
          font-size: 10px !important;
          line-height: 1.15 !important;
        }

        vehicle-search .vs-aventador-card__finish {
          font-size: 8px !important;
        }

        vehicle-search .vs-aventador-card__finishes {
          gap: 3px !important;
          margin-top: 7px !important;
        }

        vehicle-search .vs-aventador-finish {
          width: 22px !important;
          height: 22px !important;
          flex: 0 0 22px !important;
          padding: 1px !important;
        }

        vehicle-search .vs-aventador-card__price {
          font-size: 10px !important;
        }

        vehicle-search .vs-aventador-card__open,
        vehicle-search .vs-aventador-card__status {
          font-size: 7px !important;
        }

        vehicle-search .vs-aventador-card__status {
          min-height: 22px !important;
          padding: 0 8px !important;
        }
      }


      /* =========================================================
         FINAL LAYOUT FIX — NO HERO, PERFECT TWO-COLUMN MOBILE GRID
         ========================================================= */

      vehicle-search .vs-aventador-hero,
      vehicle-search .vs-av-hero {
        display: none !important;
      }

      vehicle-search,
      vehicle-search .vs__layout,
      vehicle-search .vs__content,
      vehicle-search .vs__main,
      vehicle-search [data-role="main"],
      vehicle-search [data-role="results"] {
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0 !important;
        box-sizing: border-box !important;
      }

      @media (max-width: 749px) {
        vehicle-search {
          padding-left: 6px !important;
          padding-right: 6px !important;
          overflow-x: hidden !important;
        }

        vehicle-search .vs__layout,
        vehicle-search .vs__content,
        vehicle-search .vs__main,
        vehicle-search [data-role="main"],
        vehicle-search [data-role="results"] {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          margin: 0 !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
        }

        /* Sidebar must be a drawer on mobile, never consume grid width. */
        vehicle-search [data-role="sidebar"],
        vehicle-search .vs__sidebar {
          position: fixed !important;
          width: min(92vw, 380px) !important;
          max-width: 380px !important;
        }

        vehicle-search [data-role="grid"],
        vehicle-search.vs-aventador-mode [data-role="grid"],
        vehicle-search.vs-av-mode [data-role="grid"] {
          display: grid !important;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
          gap: 3px !important;
          padding: 0 !important;
          margin: 0 !important;
          align-items: stretch !important;
        }

        vehicle-search [data-role="grid"] > *,
        vehicle-search .vs-aventador-card,
        vehicle-search .vs-av-card,
        vehicle-search [data-role="grid"] .vs__card {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          margin: 0 !important;
          box-sizing: border-box !important;
          overflow: hidden !important;
        }

        vehicle-search .vs-aventador-card__media,
        vehicle-search .vs-av-card__media,
        vehicle-search [data-role="grid"] .vs__card-img-wrap,
        vehicle-search [data-role="grid"] .vs__card-media {
          width: 100% !important;
          aspect-ratio: 1 / 1 !important;
          overflow: hidden !important;
        }

        vehicle-search .vs-aventador-card__image,
        vehicle-search .vs-av-card__image,
        vehicle-search [data-role="grid"] .vs__card-img {
          width: 100% !important;
          height: 100% !important;
          max-width: 100% !important;
          object-fit: contain !important;
        }

        vehicle-search .vs-aventador-card__content {
          min-height: 0 !important;
          padding: 6px !important;
        }

        vehicle-search .vs-aventador-card__name {
          font-size: 9.5px !important;
          line-height: 1.15 !important;
          overflow-wrap: anywhere !important;
        }

        vehicle-search .vs-aventador-card__method,
        vehicle-search .vs-aventador-card__finish {
          font-size: 7px !important;
          line-height: 1.15 !important;
        }

        vehicle-search .vs-aventador-card__finishes {
          display: flex !important;
          gap: 2px !important;
          margin-top: 5px !important;
          overflow-x: auto !important;
        }

        vehicle-search .vs-aventador-finish {
          width: 19px !important;
          height: 19px !important;
          flex: 0 0 19px !important;
          padding: 1px !important;
        }

        vehicle-search .vs-aventador-card__bottom {
          gap: 4px !important;
          margin-top: 6px !important;
        }

        vehicle-search .vs-aventador-card__price {
          font-size: 9px !important;
          line-height: 1.1 !important;
        }

        vehicle-search .vs-aventador-card__open,
        vehicle-search .vs-aventador-card__status {
          font-size: 6px !important;
          letter-spacing: .04em !important;
        }

        vehicle-search .vs-aventador-card__status {
          min-height: 20px !important;
          padding: 0 7px !important;
        }

        vehicle-search .vs-pro-results-toolbar {
          width: 100% !important;
          padding: 0 !important;
        }
      }


      /* =========================================================
         ALL-BRAND DARK THEME + PERFORMANCE
         Applies to Aventador, Concaver, Japan Racing, Vesser, etc.
         ========================================================= */

      vehicle-search,
      vehicle-search .vs__layout,
      vehicle-search .vs__main,
      vehicle-search .vs__content,
      vehicle-search [data-role="main"],
      vehicle-search [data-role="results"],
      vehicle-search [data-role="sidebar"],
      vehicle-search .vs__sidebar,
      vehicle-search .vs__accordion-body,
      vehicle-search .vs__checkbox-list,
      vehicle-search .vs__filter-body {
        background: #080808 !important;
        color: #fff !important;
      }

      vehicle-search [data-role="sidebar"],
      vehicle-search .vs__sidebar {
        border-color: rgba(201,168,76,.18) !important;
      }

      vehicle-search .vs__filter-title,
      vehicle-search .vs__accordion-toggle,
      vehicle-search .vs__accordion-toggle *,
      vehicle-search .vs__checkbox-name,
      vehicle-search .vs__checkbox-count,
      vehicle-search .vs__range-label,
      vehicle-search .vs__range-values,
      vehicle-search label,
      vehicle-search summary {
        color: #fff !important;
      }

      vehicle-search .vs__accordion-toggle,
      vehicle-search .vs__filter-row,
      vehicle-search .vs__checkbox-row {
        background: #080808 !important;
        border-color: rgba(255,255,255,.14) !important;
      }

      vehicle-search select,
      vehicle-search input[type="text"],
      vehicle-search input[type="number"],
      vehicle-search input[type="search"] {
        background: #111 !important;
        color: #fff !important;
        border-color: rgba(201,168,76,.24) !important;
      }

      vehicle-search select option {
        background: #111 !important;
        color: #fff !important;
      }

      vehicle-search input::placeholder {
        color: rgba(255,255,255,.56) !important;
      }

      vehicle-search .vs__accordion-chevron {
        border-color: #fff !important;
      }

      vehicle-search .vs-pro-results-toolbar [data-role="sort"] {
        background: #101010 !important;
        color: #fff !important;
        border-color: rgba(201,168,76,.28) !important;
      }

      vehicle-search .vs-pro-results-toolbar [data-role="count"] {
        color: #bdbdbd !important;
      }

      /* Standard cards used by Concaver, Japan Racing and Vesser */
      vehicle-search [data-role="grid"] .vs__card,
      vehicle-search [data-role="grid"] .vs__card-body {
        background: #0d0d0d !important;
        color: #fff !important;
        border-color: rgba(201,168,76,.16) !important;
      }

      vehicle-search [data-role="grid"] .vs__card-brand {
        color: #c9a84c !important;
      }

      vehicle-search [data-role="grid"] .vs__card-name,
      vehicle-search [data-role="grid"] .vs__card-colour,
      vehicle-search [data-role="grid"] .vs__card-specs,
      vehicle-search [data-role="grid"] .vs__card-price,
      vehicle-search [data-role="grid"] .vs__card-footer,
      vehicle-search [data-role="grid"] .vs__card a {
        color: #fff !important;
      }

      vehicle-search [data-role="grid"] .vs__card-img-wrap,
      vehicle-search [data-role="grid"] .vs__card-media {
        background: #0b0b0b !important;
      }

      /* Rendering containment reduces work on long product lists. */
      vehicle-search [data-role="grid"] > * {
        content-visibility: auto !important;
        contain-intrinsic-size: 420px 560px !important;
        contain: layout paint style !important;
      }

      vehicle-search [data-role="grid"] img {
        content-visibility: auto !important;
      }

      @media (max-width: 749px) {
        vehicle-search [data-role="grid"],
        vehicle-search.vs-aventador-mode [data-role="grid"],
        vehicle-search.vs-av-mode [data-role="grid"] {
          grid-template-columns: repeat(2, minmax(0,1fr)) !important;
          gap: 3px !important;
        }
      }


      /* Final finish-button clarity */
      vehicle-search .vs-aventador-card__finishes {
        gap: 9px !important;
        margin-top: 12px !important;
        padding-bottom: 2px !important;
      }

      vehicle-search .vs-aventador-finish {
        width: 34px !important;
        height: 34px !important;
        flex: 0 0 34px !important;
        padding: 4px !important;
        border: 1px solid rgba(255,255,255,.34) !important;
        border-radius: 50% !important;
        background: #111 !important;
        box-shadow: 0 2px 8px rgba(0,0,0,.32) !important;
      }

      vehicle-search .vs-aventador-finish:hover {
        border-color: #fff !important;
        transform: scale(1.06) !important;
      }

      vehicle-search .vs-aventador-finish.is-active {
        border-color: #d8aa42 !important;
        box-shadow:
          0 0 0 2px #0b0b0b,
          0 0 0 4px #d8aa42,
          0 3px 10px rgba(0,0,0,.42) !important;
      }

      vehicle-search .vs-aventador-finish__swatch {
        width: 100% !important;
        height: 100% !important;
        display: block !important;
        border-radius: 50% !important;
        box-shadow:
          inset 0 1px 1px rgba(255,255,255,.30),
          inset 0 -1px 2px rgba(0,0,0,.45) !important;
      }

      @media (max-width: 749px) {
        vehicle-search .vs-aventador-card__finishes {
          gap: 5px !important;
          margin-top: 8px !important;
        }

        vehicle-search .vs-aventador-finish {
          width: 27px !important;
          height: 27px !important;
          flex: 0 0 27px !important;
          padding: 3px !important;
        }
      }


      /* Slightly smaller finish buttons */
      vehicle-search .vs-aventador-finish {
        width: 31px !important;
        height: 31px !important;
        flex: 0 0 31px !important;
        padding: 3px !important;
      }

      @media (max-width: 749px) {
        vehicle-search .vs-aventador-finish {
          width: 25px !important;
          height: 25px !important;
          flex: 0 0 25px !important;
          padding: 2px !important;
        }
      }


      /* Compact one-line colour selector */
      vehicle-search .vs-aventador-card__finish-label {
        margin-top: 9px !important;
        color: #c9a84c !important;
        font-size: 10.5px !important;
        font-weight: 800 !important;
        letter-spacing: .10em !important;
        line-height: 1.08 !important;
      }

      vehicle-search .vs-aventador-card__finishes {
        display: flex !important;
        flex-wrap: nowrap !important;
        align-items: center !important;
        gap: 4px !important;
        width: 100% !important;
        margin-top: 5px !important;
        overflow: hidden !important;
      }

      vehicle-search .vs-aventador-finish {
        width: 24px !important;
        height: 24px !important;
        flex: 0 0 24px !important;
        padding: 2px !important;
      }

      @media (max-width: 749px) {
        vehicle-search .vs-aventador-card__finish-label {
          margin-top: 7px !important;
          font-size: 9px !important;
        }

        vehicle-search .vs-aventador-card__finishes {
          gap: 3px !important;
          margin-top: 4px !important;
        }

        vehicle-search .vs-aventador-finish {
          width: 20px !important;
          height: 20px !important;
          flex: 0 0 20px !important;
          padding: 2px !important;
        }
      }


      /* Make the custom colour option clearly multicoloured */
      vehicle-search .vs-aventador-finish[title*="Custom" i] .vs-aventador-finish__swatch {
        background:
          conic-gradient(
            #ff3b30 0deg 45deg,
            #ff9500 45deg 90deg,
            #ffcc00 90deg 135deg,
            #34c759 135deg 180deg,
            #00c7be 180deg 225deg,
            #007aff 225deg 270deg,
            #5856d6 270deg 315deg,
            #ff2d55 315deg 360deg
          ) !important;
      }


      /* =========================================================
         FINAL UI TUNING
         - Size buttons use the same premium dark/gold style for all brands
         - "Velg farge" and price are larger on desktop and mobile
         ========================================================= */

      /* Make 19/20/21/22 size buttons match Aventador style everywhere */
      vehicle-search .vs-pro-diam-btn {
        border-color: rgba(202,155,59,.55) !important;
        background: #0d0d0d !important;
        color: #fff !important;
        box-shadow: none !important;
      }

      vehicle-search .vs-pro-diam-btn:hover {
        border-color: #d9b457 !important;
        background: #131313 !important;
        box-shadow: 0 0 0 1px rgba(217,180,87,.18) !important;
      }

      vehicle-search .vs-pro-diam-value {
        color: #fff !important;
      }

      vehicle-search .vs-pro-diam-label,
      vehicle-search .vs__diam-btn--custom .vs-pro-diam-label {
        color: #d7ac4b !important;
      }

      vehicle-search .vs-pro-diam-sublabel {
        color: rgba(255,255,255,.72) !important;
      }

      vehicle-search .vs-pro-diam-btn.vs__diam-btn--active {
        border-color: #e0bb5a !important;
        background: linear-gradient(135deg, #e2bf5e, #b98721) !important;
        box-shadow: none !important;
      }

      vehicle-search .vs-pro-diam-btn.vs__diam-btn--active .vs-pro-diam-value,
      vehicle-search .vs-pro-diam-btn.vs__diam-btn--active .vs-pro-diam-label,
      vehicle-search .vs-pro-diam-btn.vs__diam-btn--active .vs-pro-diam-sublabel {
        color: #080808 !important;
      }

      vehicle-search .vs-pro-diam-btn.vs__diam-btn--active .vs-pro-diam-check {
        color: #080808 !important;
      }

      /* Stronger card typography */
      vehicle-search .vs-aventador-card__finish {
        margin-top: 5px !important;
        font-size: 12px !important;
        font-weight: 600 !important;
        letter-spacing: .04em !important;
      }

      vehicle-search .vs-aventador-card__finish-label {
        margin-top: 9px !important;
        font-size: 10.5px !important;
        font-weight: 800 !important;
        letter-spacing: .11em !important;
        line-height: 1.12 !important;
      }

      vehicle-search .vs-aventador-card__price {
        font-size: 18px !important;
        font-weight: 800 !important;
        line-height: 1.1 !important;
      }

      vehicle-search .vs-aventador-card__open,
      vehicle-search .vs-aventador-card__status {
        font-size: 11px !important;
        letter-spacing: .06em !important;
      }

      vehicle-search .vs-aventador-card__bottom {
        margin-top: 13px !important;
        align-items: center !important;
      }

      @media (max-width: 749px) {
        vehicle-search .vs-pro-diam-btn {
          width: 58px !important;
          min-width: 58px !important;
          height: 50px !important;
          padding: 6px 6px 5px !important;
        }

        vehicle-search .vs__diam-btn--set {
          width: 112px !important;
          min-width: 112px !important;
          height: 56px !important;
        }

        vehicle-search .vs-pro-diam-value {
          font-size: 14px !important;
        }

        vehicle-search .vs__diam-btn--set .vs-pro-diam-value {
          font-size: 13px !important;
        }

        vehicle-search .vs-pro-diam-label {
          font-size: 8px !important;
        }

        vehicle-search .vs-pro-diam-sublabel {
          font-size: 7px !important;
        }

        vehicle-search .vs-aventador-card__finish {
          font-size: 10px !important;
          line-height: 1.15 !important;
        }

        vehicle-search .vs-aventador-card__finish-label {
          margin-top: 7px !important;
          font-size: 8.5px !important;
          letter-spacing: .10em !important;
        }

        vehicle-search .vs-aventador-card__price {
          font-size: 14px !important;
          line-height: 1.1 !important;
        }

        vehicle-search .vs-aventador-card__open,
        vehicle-search .vs-aventador-card__status {
          font-size: 8px !important;
          letter-spacing: .05em !important;
        }

        vehicle-search .vs-aventador-card__status {
          min-height: 23px !important;
          padding: 0 8px !important;
        }
      }


      /* Concaver/Vesser +1/+2 sizes: only shown when real products exist */
      vehicle-search .vs__diam-btn--upgrade {
        border-color: rgba(202,155,59,.55) !important;
        background: #0d0d0d !important;
      }

      vehicle-search .vs__diam-btn--upgrade .vs-pro-diam-label {
        color: #d7ac4b !important;
      }

      vehicle-search .vs__diam-btn--upgrade.vs__diam-btn--active {
        border-color: #e0bb5a !important;
        background: linear-gradient(135deg,#e2bf5e,#b98721) !important;
      }

      vehicle-search .vs__diam-btn--upgrade.vs__diam-btn--active .vs-pro-diam-value,
      vehicle-search .vs__diam-btn--upgrade.vs__diam-btn--active .vs-pro-diam-label {
        color: #080808 !important;
      }


      /* All wheel models are displayed on one page */
      vehicle-search [data-role="pagination"] {
        display: none !important;
      }


      /* Mobile header controls: brand, specifications and reset on one line */
      @media (max-width: 749px) {
        vehicle-search .vs-pro-mobile-controls {
          display: grid !important;
          grid-template-columns: minmax(0, 1.25fr) minmax(0, 1.15fr) minmax(0, .85fr) !important;
          align-items: stretch !important;
          gap: 6px !important;
          width: 100% !important;
          margin-top: 8px !important;
        }

        vehicle-search .vs-pro-mobile-brand-trigger,
        vehicle-search .vs-pro-mobile-action {
          width: 100% !important;
          min-width: 0 !important;
          min-height: 46px !important;
          height: 46px !important;
          margin: 0 !important;
          padding: 7px 8px !important;
          border: 1px solid rgba(202,155,59,.78) !important;
          border-radius: 8px !important;
          background: rgba(255,255,255,.035) !important;
          color: #fff !important;
          box-sizing: border-box !important;
          font-size: 10px !important;
          font-weight: 750 !important;
          line-height: 1.05 !important;
        }

        vehicle-search .vs-pro-mobile-brand-trigger {
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          gap: 5px !important;
        }

        vehicle-search .vs-pro-mobile-brand-trigger__left {
          min-width: 0 !important;
          gap: 5px !important;
        }

        vehicle-search .vs-pro-mobile-brand-trigger__label {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }

        vehicle-search .vs-pro-mobile-action--specs {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }

        vehicle-search .vs-pro-specs-label-wrap {
          gap: 5px !important;
          white-space: nowrap !important;
        }

        vehicle-search .vs-pro-reset-label {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 5px !important;
          white-space: nowrap !important;
        }

        /* Old wrapper is no longer used, but keep it hidden if cached markup remains. */
        vehicle-search .vs-pro-mobile-actions {
          display: contents !important;
        }
      }

      @media (max-width: 390px) {
        vehicle-search .vs-pro-mobile-controls {
          grid-template-columns: minmax(0, 1.18fr) minmax(0, 1.12fr) minmax(0, .78fr) !important;
          gap: 4px !important;
        }

        vehicle-search .vs-pro-mobile-brand-trigger,
        vehicle-search .vs-pro-mobile-action {
          padding: 6px 5px !important;
          font-size: 9px !important;
        }
      }


      /* Remove the gold frame around wheel cards.
         Keep only subtle dark separators so the grid remains structured. */
      vehicle-search [data-role="grid"],
      vehicle-search.vs-aventador-mode [data-role="grid"],
      vehicle-search.vs-av-mode [data-role="grid"] {
        border: 0 !important;
        background: #080808 !important;
      }

      vehicle-search .vs-aventador-card,
      vehicle-search .vs-av-card,
      vehicle-search [data-role="grid"] .vs__card {
        border: 0 !important;
        box-shadow: none !important;
        outline: 0 !important;
      }

      vehicle-search .vs-aventador-card__content,
      vehicle-search .vs-av-card__content,
      vehicle-search [data-role="grid"] .vs__card-body {
        border-color: rgba(255,255,255,.08) !important;
      }

      @media (max-width: 749px) {
        vehicle-search [data-role="grid"],
        vehicle-search.vs-aventador-mode [data-role="grid"],
        vehicle-search.vs-av-mode [data-role="grid"] {
          gap: 2px !important;
          background: #080808 !important;
        }

        vehicle-search .vs-aventador-card,
        vehicle-search .vs-av-card,
        vehicle-search [data-role="grid"] .vs__card {
          border: 0 !important;
          box-shadow: none !important;
        }
      }


      /* Move the active size-button checkmark a bit further right
         so it does not sit too close to the inch mark (") */
      vehicle-search .vs-pro-diam-check {
        right: 3px !important;
      }

      @media (max-width: 749px) {
        vehicle-search .vs-pro-diam-check {
          right: 2px !important;
          top: 4px !important;
        }
      }

      @media (max-width: 430px) {
        vehicle-search .vs-pro-diam-check {
          right: 1px !important;
          top: 3px !important;
        }
      }


      /* Skjul broken/manglende produktbilder pent */
      vehicle-search .vs-aventador-card__image[style*="visibility: hidden"],
      vehicle-search .vs-aventador-card__image:not([src]),
      vehicle-search .vs-aventador-card__image[src=""] {
        visibility: hidden !important;
      }

      vehicle-search .vs-aventador-card__media:has(.vs-aventador-card__image[style*="visibility: hidden"]),
      vehicle-search .vs-aventador-card__media:has(.vs-aventador-card__image[src=""]) {
        background: radial-gradient(circle at 50% 42%, #1a1a1a 0, #0d0d0d 60%, #080808 100%) !important;
      }

      /* Mobile: place diameter buttons as far left and as high as possible */
      @media (max-width: 749px) {
        vehicle-search [data-role="diameter-bar"],
        vehicle-search.vs-aventador-mode [data-role="diameter-bar"],
        vehicle-search.vs-av-mode [data-role="diameter-bar"] {
          justify-content: flex-start !important;
          align-items: stretch !important;
          margin-top: 2px !important;
          margin-bottom: 4px !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          padding-top: 0 !important;
          padding-bottom: 1px !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
        }

        /* Reduce remaining space before the result toolbar/cards */
        vehicle-search .vs-pro-results-toolbar {
          margin-top: 0 !important;
          margin-bottom: 3px !important;
          padding-top: 0 !important;
        }

        vehicle-search [data-role="grid"] {
          margin-top: 0 !important;
        }
      }

      @media (max-width: 430px) {
        vehicle-search [data-role="diameter-bar"],
        vehicle-search.vs-aventador-mode [data-role="diameter-bar"],
        vehicle-search.vs-av-mode [data-role="diameter-bar"] {
          margin-top: 1px !important;
          margin-bottom: 3px !important;
          gap: 5px !important;
        }

        vehicle-search .vs-pro-results-toolbar {
          margin-bottom: 2px !important;
        }
      }

    `;
    document.head.appendChild(style);
  }

  _initProfessionalSearchUi() {
    this._brandFacetItems = [];
    this._allBrandFacetItems = [];
    this._modelFacetItems = [];
    this._pendingBrandSheetModels = false;
    this._mobileSheetMode = 'brand';

    // A brand can be preselected technically by filters/default settings.
    // On mobile, do not advance to "Velg felgmodell" until the customer
    // has actively chosen a brand in the mobile brand selector.
    this._mobileBrandChosenByUser = false;

    // No separate Aventador title/collection block in vehicle search.
    // The customer should see sizes and wheel models immediately.
    this._aventadorHero = null;

    // Keep count and sorting together so they never leave the viewport.
    this._resultsToolbar = document.createElement('div');
    this._resultsToolbar.className = 'vs-pro-results-toolbar';
    if (this._grid?.parentNode) {
      this._grid.parentNode.insertBefore(this._resultsToolbar, this._grid);
      if (this._countEl) this._resultsToolbar.appendChild(this._countEl);
      if (this._sortSelect) this._resultsToolbar.appendChild(this._sortSelect);
    }

    // Mobile controls are appended inside the existing black vehicle header.
    this._mobileControls = document.createElement('div');
    this._mobileControls.className = 'vs-pro-mobile-controls';
    this._mobileControls.innerHTML = `
      <button type="button" class="vs-pro-mobile-brand-trigger" data-vs-pro-filter-open>
        <span class="vs-pro-mobile-brand-trigger__left">
          <span aria-hidden="true">☷</span>
          <span class="vs-pro-mobile-brand-trigger__label" data-vs-pro-filter-label>Velg merke</span>
        </span>
        <span aria-hidden="true">›</span>
      </button>

      <button type="button" class="vs-pro-mobile-action vs-pro-mobile-action--specs" data-vs-pro-action="specs" aria-expanded="false">
        <span class="vs-pro-specs-label-wrap"><span>Spesifikasjoner</span><span class="vs-pro-specs-chevron" aria-hidden="true"></span></span>
      </button>

      <button type="button" class="vs-pro-mobile-action" data-vs-pro-action="reset">
        <span class="vs-pro-reset-label"><span aria-hidden="true">⟳</span><span>Nullstill</span></span>
      </button>`;
    this._header?.appendChild(this._mobileControls);

    this._mobileFilterLabel = this._mobileControls.querySelector('[data-vs-pro-filter-label]');
    this._mobileSpecsButton = this._mobileControls.querySelector('[data-vs-pro-action="specs"]');

    this._mobileControls.querySelector('[data-vs-pro-filter-open]')
      ?.addEventListener('click', () => {
        // Mobile selector is brand-only. Opening it again always shows every
        // available brand, including "Alle merker".
        this._openMobileBrandSheet('brand');
      });

    this._mobileSpecsButton?.addEventListener('click', () => this._toggleSpecsPanel());
    this._mobileControls.querySelector('[data-vs-pro-action="reset"]')?.addEventListener('click', () => {
      const candidates = [...this.querySelectorAll('[data-role="filter-reset"]')];
      const target = candidates.find((el) => !el.closest('.vs-pro-mobile-controls'));
      target?.click();
    });

    this._setSpecsToggleState(!!this._specsPanel && !this._specsPanel.hidden);
    this._createMobileBrandSheet();

    // Brand options are not needed for first paint. Load them when the browser
    // is idle so product rendering stays responsive.
    const loadBrands = () => this._loadAllBrandOptions();
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(loadBrands, { timeout: 1800 });
    } else {
      window.setTimeout(loadBrands, 450);
    }
  }

  _toggleSpecsPanel() {
    if (!this._specsPanel) return;
    this._setSpecsToggleState(this._specsPanel.hidden);
  }

  _setSpecsToggleState(open) {
    const isOpen = !!open;

    if (this._specsPanel) {
      if (!this._specsPanel.id) this._specsPanel.id = 'vehicle-specifications-panel';
      this._specsPanel.hidden = !isOpen;
    }

    const labelHtml = `<span class="vs-pro-specs-label-wrap"><span>Spesifikasjoner</span><span class="vs-pro-specs-chevron" aria-hidden="true"></span></span>`;

    if (this._specsToggle) {
      this._specsToggle.classList.toggle('vs__header-btn--active', isOpen);
      this._specsToggle.setAttribute('aria-expanded', String(isOpen));
      this._specsToggle.setAttribute('title', isOpen ? 'Skjul spesifikasjoner' : 'Vis spesifikasjoner');
      if (this._specsPanel?.id) this._specsToggle.setAttribute('aria-controls', this._specsPanel.id);

      const desktopLabel = this._specsToggle.querySelector('.vs__header-btn-top');
      if (desktopLabel) desktopLabel.innerHTML = labelHtml;
      else this._specsToggle.innerHTML = labelHtml;
    }

    if (this._mobileSpecsButton) {
      this._mobileSpecsButton.setAttribute('aria-expanded', String(isOpen));
      this._mobileSpecsButton.setAttribute('title', isOpen ? 'Trykk igjen for å lukke' : 'Trykk for å vise spesifikasjoner');
      if (this._specsPanel?.id) this._mobileSpecsButton.setAttribute('aria-controls', this._specsPanel.id);
    }
  }

  _createMobileBrandSheet() {
    this._brandBackdrop = document.createElement('div');
    this._brandBackdrop.className = 'vs-pro-brand-backdrop';

    this._brandSheet = document.createElement('div');
    this._brandSheet.className = 'vs-pro-brand-sheet';
    this._brandSheet.setAttribute('role', 'dialog');
    this._brandSheet.setAttribute('aria-modal', 'true');
    this._brandSheet.setAttribute('aria-label', 'Velg merke');
    this._brandSheet.innerHTML = `
      <div class="vs-pro-brand-sheet__handle"></div>
      <div class="vs-pro-brand-sheet__header">
        <h2 class="vs-pro-brand-sheet__title">Velg merke</h2>
        <button type="button" class="vs-pro-brand-sheet__close" aria-label="Lukk">×</button>
      </div>
      <div class="vs-pro-brand-sheet__scroll">
        <div class="vs-pro-brand-sheet__list"></div>
        <div class="vs-pro-brand-sheet__models"></div>
        
      </div>`;

    document.body.appendChild(this._brandBackdrop);
    document.body.appendChild(this._brandSheet);

    this._brandSheetScroll = this._brandSheet.querySelector('.vs-pro-brand-sheet__scroll');
    this._brandSheetList = this._brandSheet.querySelector('.vs-pro-brand-sheet__list');
    this._brandSheetModels = this._brandSheet.querySelector('.vs-pro-brand-sheet__models');

    this._brandBackdrop.addEventListener('click', () => this._closeMobileBrandSheet());
    this._brandSheet.querySelector('.vs-pro-brand-sheet__close')?.addEventListener('click', () => this._closeMobileBrandSheet());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this._brandSheet?.classList.contains('is-open')) {
        this._closeMobileBrandSheet();
      }
    });
  }

  async _loadAllBrandOptions() {
    try {
      const res = await fetch(`${this._api}/api/v2/wheels?limit=1&page=1`);
      if (!res.ok) return;
      const json = await res.json();
      const items = json?.facets?.brand;
      if (Array.isArray(items) && items.length) {
        this._allBrandFacetItems = items;
        this._renderMobileBrandSheet();
      }
    } catch (error) {
      console.warn('Could not load full brand list', error);
    }
  }

  _openMobileBrandSheet(mode = 'brand') {
    // Mobile sheet is intentionally brand-only.
    this._mobileSheetMode = 'brand';

    const title = this._brandSheet?.querySelector('.vs-pro-brand-sheet__title');
    if (title) {
      title.textContent = this._mobileSheetMode === 'model'
        ? 'Velg felgmodell'
        : 'Velg merke';
    }

    this._brandSheet?.setAttribute(
      'aria-label',
      this._mobileSheetMode === 'model' ? 'Velg felgmodell' : 'Velg merke'
    );

    this._renderMobileBrandSheet();
    if (this._brandSheetScroll) this._brandSheetScroll.scrollTop = 0;
    this._brandBackdrop?.classList.add('is-open');
    this._brandSheet?.classList.add('is-open');
    document.body.classList.add('vs-pro-sheet-open');
  }

  _closeMobileBrandSheet() {
    this._brandBackdrop?.classList.remove('is-open');
    this._brandSheet?.classList.remove('is-open');
    document.body.classList.remove('vs-pro-sheet-open');
  }

  _getSelectedBrandName() {
    if (this._selectedDiameterIsCustom) return 'Aventador Wheels';
    const checked = [...(this._accordions?.brand?.querySelectorAll('input[type="checkbox"]:checked') || [])];
    if (checked.length === 1) return checked[0].value;
    if (checked.length > 1) return `${checked.length} merker`;
    return this._brandSelection || '';
  }

  _updateMobileBrandTrigger() {
    if (!this._mobileFilterLabel) return;
    this._mobileFilterLabel.textContent = 'Velg merke';
  }

  _renderMobileBrandSheet() {
    if (!this._brandSheetList || !this._brandSheetModels) return;

    const escape = (value) => String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    const selectedBrand = this._getSelectedBrandName();
    const selectedModel = this._modelSelection || '';

    // STEP 1: Brand only
    if (this._mobileSheetMode === 'brand') {
      const source = this._allBrandFacetItems.length
        ? this._allBrandFacetItems
        : this._brandFacetItems;

      const uniqueBrands = [];
      const seenBrands = new Set();

      for (const item of source || []) {
        const name = String(item?.value || '').trim();
        const normalized = name.toLowerCase();
        if (!name || seenBrands.has(normalized)) continue;
        seenBrands.add(normalized);
        uniqueBrands.push(name);
      }

      uniqueBrands.sort((a, b) => a.localeCompare(b, 'nb'));

      this._brandSheetList.innerHTML = [
        `<div class="vs-pro-brand-sheet__section-title">Merker</div>`,
        `<button type="button" class="vs-pro-brand-option${!selectedBrand ? ' is-selected' : ''}" data-vs-pro-brand="">
          <span>Alle merker</span>
          <span class="vs-pro-brand-option__mark">${!selectedBrand ? '✓' : '›'}</span>
        </button>`,
        ...uniqueBrands.map((name) => `
          <button type="button" class="vs-pro-brand-option${selectedBrand === name ? ' is-selected' : ''}" data-vs-pro-brand="${escape(name)}">
            <span>${escape(name)}</span>
            <span class="vs-pro-brand-option__mark">${selectedBrand === name ? '✓' : '›'}</span>
          </button>`)
      ].join('');

      this._brandSheetModels.innerHTML = '';

      this._brandSheetList.querySelectorAll('[data-vs-pro-brand]').forEach((button) => {
        button.addEventListener('click', () => {
          this._selectBrandFromMobile(button.dataset.vsProBrand || '');
        });
      });

      return;
    }

    // STEP 2: Models for selected brand only
    this._brandSheetList.innerHTML = '';

    if (!selectedBrand) {
      this._brandSheetModels.innerHTML =
        `<div class="vs-pro-brand-sheet__empty">Velg merke først.</div>`;
      return;
    }

    if (this._pendingBrandSheetModels) {
      this._brandSheetModels.innerHTML =
        `<div class="vs-pro-brand-sheet__section-title">Felgmodeller</div>
         <div class="vs-pro-brand-sheet__empty">Laster modeller…</div>`;
      return;
    }

    const uniqueModels = [];
    const seenModels = new Set();

    for (const item of this._modelFacetItems || []) {
      const name = String(item?.value || '').trim();
      const normalized = name.toLowerCase().replace(/\s+/g, ' ');
      if (!name || seenModels.has(normalized)) continue;
      seenModels.add(normalized);
      uniqueModels.push(name);
    }

    uniqueModels.sort((a, b) => a.localeCompare(b, 'nb'));

    if (!uniqueModels.length) {
      this._brandSheetModels.innerHTML =
        `<div class="vs-pro-brand-sheet__section-title">Felgmodeller</div>
         <div class="vs-pro-brand-sheet__empty">Ingen modeller tilgjengelig for ${escape(selectedBrand)}.</div>`;
      return;
    }

    this._brandSheetModels.innerHTML = [
      `<div class="vs-pro-brand-sheet__section-title">Felgmodeller</div>`,
      `<div class="vs-pro-brand-sheet__models-list">`,
      `<button type="button" class="vs-pro-model-option${!selectedModel ? ' is-selected' : ''}" data-vs-pro-wheel-model="">
        <span>Alle modeller</span>
        <span class="vs-pro-model-option__mark">${!selectedModel ? '✓' : '›'}</span>
      </button>`,
      ...uniqueModels.map((name) => `
        <button type="button" class="vs-pro-model-option${selectedModel === name ? ' is-selected' : ''}" data-vs-pro-wheel-model="${escape(name)}">
          <span>${escape(name)}</span>
          <span class="vs-pro-model-option__mark">${selectedModel === name ? '✓' : '›'}</span>
        </button>`),
      `</div>`
    ].join('');

    this._brandSheetModels.querySelectorAll('[data-vs-pro-wheel-model]').forEach((button) => {
      button.addEventListener('click', () => {
        this._selectWheelModelFromMobile(button.dataset.vsProWheelModel || '');
      });
    });
  }

  async _selectBrandFromMobile(brandName) {
    // Only a direct choice in this mobile sheet advances the mobile flow.
    // Choosing "Alle merker" resets it back to the first step.
    this._mobileBrandChosenByUser = !!brandName;

    // A non-Aventador brand must start from a clean OEM selector.
    if (brandName && brandName !== 'Aventador Wheels') {
      this._selectedDiameter = null;
      this._selectedDiameterIsCustom = false;
      this._selectedOemSetKey = '';
      if (this._filters?.size) this._filters.size.value = '';

      this._resetOemFallbackState();
      this._diameterAvailabilityResolvedKey = '';
      this._diameterAvailabilityRequestId += 1;

      if (this._vehicle?.specs) {
        this._buildDiameterButtons(this._vehicle.specs, this._vehicleSpecs);
      }
    }

    this._brandSelection = brandName;
    this._showAllBrands = !brandName;
    this._modelSelection = '';
    this._pendingBrandSheetModels = false;

    const brandAccordion = this._accordions?.brand;
    brandAccordion?.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      checkbox.checked = !!brandName && checkbox.value === brandName;
    });
    this._accordions?.wheel_model?.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      checkbox.checked = false;
    });

    this._updateMobileBrandTrigger();
    this._page = 1;
    this._closeMobileBrandSheet();

    // Show only the selected brand. The sheet stays closed until the customer
    // presses "Velg merke" again.
    this._search().finally(() => {
      this._pendingBrandSheetModels = false;
      this._updateMobileBrandTrigger();
    });
  }

  _selectWheelModelFromMobile(modelName) {
    this._modelSelection = modelName;
    this._pendingBrandSheetModels = false;
    this._accordions?.wheel_model?.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      checkbox.checked = !!modelName && checkbox.value === modelName;
    });
    this._updateMobileBrandTrigger();
    this._renderMobileBrandSheet();
    this._closeMobileBrandSheet();
    this._page = 1;
    this._search();
  }

  // ── Initialization ───────────────────────────────────────────────────

  _init() {
    const params = new URLSearchParams(window.location.search);
    const make = params.get('make');
    const model = params.get('model');
    const trim = params.get('trim');
    const regnr = params.get('regnr');
    const q = params.get('q') || params.get('type'); // Shopify search uses ?q=

    // Restore session state
    this._restoredAccordions = {};
    if (!regnr) {
      try {
        const stateStr = sessionStorage.getItem('fh_vs_state');
        if (stateStr) {
          const state = JSON.parse(stateStr);
          if (state.sort && this._sortSelect) {
            this._sort = state.sort;
            this._sortSelect.value = state.sort;
          }
          if (state.selectedDiameter) this._selectedDiameter = state.selectedDiameter;
          this._selectedDiameterIsCustom = !!state.selectedDiameterIsCustom;
          this._selectedOemSetKey = state.selectedOemSetKey || '';
          if (state.size && this._filters.size) this._filters.size.value = state.size;
          if (state.width && this._filters.width) this._filters.width.value = state.width;
          if (state.pcd && this._filters.pcd) this._filters.pcd.value = state.pcd;
          if (state.axle && this._axleSelectSidebar) {
            this._axle = state.axle;
            this._axleSelectSidebar.value = state.axle;
          }

          this._restoredAccordions = {
            color: state.color || [],
            brand: state.brand || [],
            wheel_model: state.wheel_model || []
          };

          if (state.ranges) {
            for (const [key, val] of Object.entries(state.ranges)) {
              if (this._rangeInputs[key]) this._rangeInputs[key].value = val;
            }
          }
        }
      } catch (e) { console.warn('Failed to restore filter state', e); }
    }

    // Text search mode — show search bar only when ?q= is present
    if (q) {
      this._queryText = q;
      if (this._searchInput) this._searchInput.value = q;
      if (this._searchBar) this._searchBar.hidden = false;
    }

    // Regnr mode — look up plate, set vehicle, search
    if (regnr) {
      this._initFromRegnr(regnr);
      return;
    }

    // Vehicle mode
    if (make && model && trim) {
      const saved = getVehicle();
      if (saved && saved.makeSlug === make && saved.modelSlug === model && saved.trimId === trim) {
        this._vehicle = saved;
      } else {
        this._vehicle = { makeSlug: make, modelSlug: model, trimId: trim };
      }
      this._showVehicleHeader();
      this._fetchVehicleSpecsThenSearch();
    } else if (!q) {
      // No text query either — check localStorage
      const saved = getVehicle();
      if (saved?.makeSlug) {
        this._vehicle = saved;
        this._showVehicleHeader();
        this._fetchVehicleSpecsThenSearch();
      } else {
        this._showVehicleSelector();
        this._search();
      }
    } else {
      // Text-only search — show selector for vehicle picking
      this._showVehicleSelector();
      this._search();
    }
    
  }

  async _initFromRegnr(plate) {
    // Always refresh registration-number data from the API.
    // This prevents stale vehicle specs/model slugs in localStorage from
    // overriding newer backend data after mapping or fitment corrections.
    try {
      const res = await fetch(`${this._api}/api/vehicles/regnr/${encodeURIComponent(plate)}`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        this._showVehicleSelector();
        this._search();
        return;
      }

      const meta = json.meta;
      this._vehicle = {
        makeSlug: meta.make_slug,
        makeName: meta.make,
        modelSlug: meta.model_slug,
        modelName: meta.model,
        year: meta.year ? String(meta.year) : '',
        trimId: meta.trim_id ? String(meta.trim_id) : `regnr:${plate}`,
        trimName: meta.trim_name || meta.model,
        regnr: plate,
        regnrSource: 'vegvesen',
      };

      // API returns CSV data directly when a CSV match exists,
      // so json.data already has all alt specs — no second fetch needed.
      this._vehicleSpecs = json.data;
      const specs = this._parseSpecs(json.data);
      this._vehicle.specs = specs;
      this._vehicle.specsRaw = json.data;
      saveVehicle(this._vehicle);

      this._showVehicleHeader();
      if (this._axleFilter) this._axleFilter.hidden = !specs.is_staggered;
      this._buildDiameterButtons(specs, json.data);
      this._buildSpecsTable(json.data);
      this._search();
    } catch (e) {
      console.error('Regnr init failed', e);
      this._showVehicleSelector();
      this._search();
    }
  }

  async _onRegnrSearch() {
    const plate = (this._hRegnr?.value || '').replace(/[\s-]/g, '').toUpperCase();
    if (plate.length < 4) return;

    if (this._hRegnrSearch) this._hRegnrSearch.disabled = true;

    try {
      const res = await fetch(`${this._api}/api/vehicles/regnr/${encodeURIComponent(plate)}`);
      const json = await res.json();

      if (!res.ok || !json.success) {
        if (this._hRegnrSearch) this._hRegnrSearch.disabled = false;
        return;
      }

      const meta = json.meta;
      this._vehicle = {
        makeSlug: meta.make_slug,
        makeName: meta.make,
        modelSlug: meta.model_slug,
        modelName: meta.model,
        year: meta.year ? String(meta.year) : '',
        trimId: meta.trim_id ? String(meta.trim_id) : `regnr:${plate}`,
        trimName: meta.trim_name || meta.model,
        regnr: plate,
        regnrSource: 'vegvesen',
      };

      // API returns CSV data directly when a CSV match exists,
      // so json.data already has all alt specs — no second fetch needed.
      this._vehicleSpecs = json.data;
      const specs = this._parseSpecs(json.data);
      this._vehicle.specs = specs;
      this._vehicle.specsRaw = json.data;
      saveVehicle(this._vehicle);

      // Update URL
      const urlParams = new URLSearchParams({ regnr: plate });
      if (this._queryText) urlParams.set('q', this._queryText);
      window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);

      this._showVehicleHeader();
      this._selectedDiameter = null;
      this._selectedDiameterIsCustom = false;
      this._selectedOemSetKey = '';
      this._resetOemFallbackState();
      this._slidersInitialized = false;
      this._page = 1;
      sessionStorage.removeItem('fh_vs_state');
      Object.values(this._filters).forEach((sel) => { if (sel) sel.value = ''; });
      this._resetAccordions();
      this._brandSelection = this._defaultBrand;
      this._modelSelection = '';
      Object.values(this._rangeInputs).forEach((input) => { if (input) input.value = ''; });
      this._resetSlider('et');
      this._resetSlider('bore');
      this._resetSlider('price');

      if (this._axleFilter) this._axleFilter.hidden = !specs.is_staggered;
      this._buildDiameterButtons(specs, json.data);
      this._buildSpecsTable(json.data);
      this._search();
    } catch (e) {
      console.error('Regnr search failed', e);
      if (this._hRegnrSearch) this._hRegnrSearch.disabled = false;
    }
  }

  _onTextSearch() {
    const val = this._searchInput?.value.trim() || '';
    this._queryText = val;
    this._page = 1;

    // Update URL
    const urlParams = new URLSearchParams(window.location.search);
    if (val) {
      urlParams.set('q', val);
    } else {
      urlParams.delete('q');
    }
    const qs = urlParams.toString();
    window.history.replaceState({}, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);

    this._search();
  }

  async _fetchVehicleSpecsThenSearch() {
    if (!this._vehicle) return this._search();

    // Reuse cached specifications only when the cached response already contains
    // a calculated load requirement. Older cached Wheel-Size responses are
    // fetched again so RequiredLoad is not lost.
    const cachedData = this._vehicle.specsRaw;
    const cachedChassis = cachedData?.DRDChassisReturn;
    const cachedRequiredLoad = parseFloat(
      cachedChassis?.RequiredLoad ||
      cachedChassis?.RequiredLoadFront ||
      cachedChassis?.RequiredLoadRear ||
      0
    );

    if (cachedData && (!cachedChassis || cachedRequiredLoad > 0)) {
      this._vehicleSpecs = cachedData;
      const specs = this._parseSpecs(cachedData);
      this._vehicle.specs = specs;
      saveVehicle(this._vehicle);
      if (this._axleFilter) this._axleFilter.hidden = !specs.is_staggered;
      this._buildDiameterButtons(specs, cachedData);
      this._buildSpecsTable(cachedData);
      this._search();
      return;
    }

    const { makeSlug, modelSlug, trimId } = this._vehicle;
    if (!makeSlug || !modelSlug || !trimId) return this._search();

    // Fetch trim specs from CSV database (used for manual vehicle selection)
    try {
      const res = await fetch(
        `${this._api}/api/vehicles/makes/${makeSlug}/models/${modelSlug}/${trimId}`
      );
      if (res.ok) {
        const json = await res.json();
        const data = json.data ?? json;
        this._vehicleSpecs = data;
        const specs = this._parseSpecs(data);
        this._vehicle.specs = specs;
        this._vehicle.specsRaw = data;
        saveVehicle(this._vehicle);
        if (this._axleFilter) this._axleFilter.hidden = !specs.is_staggered;
        this._buildDiameterButtons(specs, data);
        this._buildSpecsTable(data);
      }
    } catch (e) {
      console.error('Failed to fetch vehicle specs', e);
    }
    this._search();
  }

  _normalizeVehiclePcd(value) {
    const raw = String(value ?? '').trim();
    const compact = raw.toLowerCase().replace(/\s+/g, '');
    if (!raw || ['n/a', 'na', 'none', 'null', '-', '0', '0x0'].includes(compact)) return '';
    return raw;
  }

  _isCenterlockSpec(config = {}, rawPcd = '') {
    const explicitValues = [
      config.CenterLock, config.CentreLock, config.centerlock, config.center_lock,
      config.FixingType, config.MountingType, config.BoltType, config.FasteningType,
    ];
    if (explicitValues.some(value => value === true || value === 1 || String(value).toLowerCase() === 'true')) {
      return true;
    }
    const text = explicitValues.map(value => String(value ?? '')).join(' ').toLowerCase();
    if (/(?:center|centre)[\s_-]*lock|central[\s_-]*lock|single[\s_-]*nut|monolock/.test(text)) {
      return true;
    }

    if (this._normalizeVehiclePcd(rawPcd)) return false;
    const make = String(this._vehicle?.makeName || this._vehicle?.makeSlug || '').toLowerCase();
    const model = String(this._vehicle?.modelName || this._vehicle?.modelSlug || '').toLowerCase();
    return make.includes('porsche') && /(?:^|\b)(?:911|991|992|997)(?:\b|$)/.test(model);
  }

  _formatVehiclePcd(value, config = {}) {
    const normalized = this._normalizeVehiclePcd(value);
    if (normalized) return normalized;
    return this._isCenterlockSpec(config, value) ? 'Centerlock' : '–';
  }

  _vehicleRimInfo(value) {
    const parts = String(value || '').trim().toLowerCase().replace('j', '').split('x').map(part => part.trim());
    if (parts.length !== 2) return null;
    const width = parseFloat(parts[0]);
    const diameter = Math.round(parseFloat(parts[1]));
    if (!Number.isFinite(width) || !Number.isFinite(diameter)) return null;
    if (width < 4 || width > 16 || diameter < 12 || diameter > 26) return null;
    return { width, diameter };
  }

  _vehicleTyreDiameter(value) {
    const text = String(value || '').toUpperCase();
    const match = text.match(/R\s*(\d{2})(?:\D|$)/);
    return match ? parseInt(match[1], 10) : null;
  }

  _vehicleFrontOffset(config = {}) {
    const values = [
      config.RimOffset,
      config.Offset,
      config.ET,
      config.et,
      config.offset,
    ];
    for (const value of values) {
      if (value === null || value === undefined || String(value).trim() === '') continue;
      const parsed = parseFloat(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }

  _vehicleRearOffset(config = {}) {
    // Different backend/CSV revisions have used both OffsetR and OffSetR.
    // Normalize every known spelling here so rear ET never silently falls back
    // to an unrelated front/default value.
    const values = [
      config.OffsetR,
      config.OffSetR,
      config.RimOffsetR,
      config.RearOffset,
      config.ETRear,
      config.et_rear,
      config.offset_rear,
    ];
    for (const value of values) {
      if (value === null || value === undefined || String(value).trim() === '') continue;
      const parsed = parseFloat(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }

  _copyVehicleConfig(config = {}) {
    return { ...config };
  }

  _repairVehicleConfigPairing(configs = []) {
    // Read from an immutable snapshot while writing to a separate clone.
    // Otherwise the first repaired row could become the source for the next
    // row and rotate the data a second time.
    const sourceSnapshot = configs.map(config => this._copyVehicleConfig(config));
    const cloned = configs.map(config => this._copyVehicleConfig(config));
    const valid = sourceSnapshot.map((config, index) => ({
      index,
      config,
      front: this._vehicleRimInfo(config.RimSize),
      rear: this._vehicleRimInfo(config.RimSizeR),
    })).filter(item => item.front && item.rear);

    if (valid.length < 2) return cloned;

    const countDiameters = (items, axle) => {
      const counts = new Map();
      for (const item of items) {
        const diameter = item[axle].diameter;
        counts.set(diameter, (counts.get(diameter) || 0) + 1);
      }
      return counts;
    };

    const frontCounts = countDiameters(valid, 'front');
    const rearCounts = countDiameters(valid, 'rear');
    const sameDiameterMultiset =
      frontCounts.size === rearCounts.size &&
      [...frontCounts.entries()].every(([diameter, count]) => rearCounts.get(diameter) === count);

    const mismatched = valid.filter(item => item.front.diameter !== item.rear.diameter).length;

    /*
     * Guard against a known flattened-CSV/API failure mode where the rear rows
     * are rotated one position relative to the front rows. Example:
     *   19/21, 20/19, 21/20
     * Although every individual row looks staggered, the front and rear
     * diameter multisets are identical. A real staggered family such as
     * 19/20 + 20/21 does NOT have identical multisets and is therefore kept.
     *
     * Only repair when at least two rows disagree and the majority of complete
     * rows are mismatched. This deliberately avoids forcing equal diameters on
     * genuine 20/21, 21/22, etc. OEM setups.
     */
    const looksLikeRearPermutation =
      sameDiameterMultiset &&
      mismatched >= 2 &&
      mismatched >= Math.ceil(valid.length * 0.6);

    if (!looksLikeRearPermutation) return cloned;

    const rearByDiameter = new Map();
    for (const item of valid) {
      const diameter = item.rear.diameter;
      if (!rearByDiameter.has(diameter)) rearByDiameter.set(diameter, []);
      rearByDiameter.get(diameter).push(item);
    }

    const usedRearIndices = new Set();
    for (const item of valid) {
      const targetDiameter = item.front.diameter;
      const pool = (rearByDiameter.get(targetDiameter) || [])
        .filter(candidate => !usedRearIndices.has(candidate.index));
      if (!pool.length) continue;

      // Prefer the rear row whose width is the nearest sensible match for the
      // current front row. This matters when a diameter has several OEM widths.
      pool.sort((a, b) => {
        const score = candidate => {
          const rearWidth = candidate.rear.width;
          const frontWidth = item.front.width;
          const narrowerPenalty = rearWidth + 0.001 < frontWidth ? 100 : 0;
          return narrowerPenalty + Math.abs(rearWidth - frontWidth);
        };
        return score(a) - score(b);
      });

      const source = pool[0];
      usedRearIndices.add(source.index);

      const target = cloned[item.index];
      const sourceConfig = source.config;
      target.RimSizeR = sourceConfig.RimSizeR;

      const rearOffset = this._vehicleRearOffset(sourceConfig);
      if (rearOffset !== null) target.OffsetR = rearOffset;
      else delete target.OffsetR;

      const rearTyre = sourceConfig.TyreSizeR || sourceConfig.TyreSizeRear || sourceConfig.RearTires;
      if (rearTyre) target.TyreSizeR = rearTyre;

      if (sourceConfig.CentreBoreR !== undefined) target.CentreBoreR = sourceConfig.CentreBoreR;
      if (sourceConfig.CenterBoreR !== undefined) target.CenterBoreR = sourceConfig.CenterBoreR;
      if (sourceConfig.MinETRear !== undefined) target.MinETRear = sourceConfig.MinETRear;
      if (sourceConfig.MaxETRear !== undefined) target.MaxETRear = sourceConfig.MaxETRear;
    }

    console.warn('[fitment] repaired rotated rear OEM configuration rows', {
      before: valid.map(item => `${item.front.diameter}/${item.rear.diameter}`),
      after: cloned.map(config => {
        const front = this._vehicleRimInfo(config.RimSize);
        const rear = this._vehicleRimInfo(config.RimSizeR);
        return front && rear ? `${front.diameter}/${rear.diameter}` : null;
      }).filter(Boolean),
    });

    return cloned;
  }

  _getVehicleConfigSource(data, { modelOnly = false } = {}) {
    const modelReturn = data?.DRDModelReturn;
    const modelConfigs = [
      modelReturn?.PrimaryOption,
      ...(Array.isArray(modelReturn?.Options) ? modelReturn.Options : []),
    ].filter(Boolean);

    // Trim-specific model rows are authoritative when present. Chassis is a
    // fallback and can describe another/base configuration of the same model.
    const source = modelConfigs.length
      ? modelConfigs
      : (modelOnly ? [] : [data?.DRDChassisReturn].filter(Boolean));

    return this._repairVehicleConfigPairing(source);
  }

  _vehicleConfigKey(config = {}) {
    const clean = value => String(value ?? '').trim().toLowerCase().replace(/\s+/g, '');
    return [
      config.RimSize,
      this._vehicleFrontOffset(config),
      config.TyreSize || config.TyreSizeFront || config.FrontTires,
      config.RimSizeR,
      this._vehicleRearOffset(config),
      config.TyreSizeR || config.TyreSizeRear || config.RearTires,
      config.PCD,
      config.CentreBore,
    ].map(clean).join('|');
  }

  _getUniqueVehicleConfigs(data) {
    const source = this._getVehicleConfigSource(data);
    const seen = new Set();
    return source.filter(config => {
      const key = this._vehicleConfigKey(config);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  _specRowKey(row = {}) {
    const clean = value => String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/\.0(?=\D|$)/g, '');
    return [row.size, row.pcd, row.bore, row.et, row.tyre, row.pos].map(clean).join('|');
  }

  _specRowBaseKey(row = {}) {
    const clean = value => String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/\.0(?=\D|$)/g, '');
    return [row.size, row.pcd, row.bore, row.et, row.pos].map(clean).join('|');
  }

  _dedupeSpecRows(rows = []) {
    const exactSeen = new Set();
    const exact = rows.filter(row => {
      const key = this._specRowKey(row);
      if (!key || exactSeen.has(key)) return false;
      exactSeen.add(key);
      return true;
    });
    const completeBases = new Set(
      exact.filter(row => String(row.tyre || '').trim())
        .map(row => this._specRowBaseKey(row))
    );
    return exact.filter(row =>
      String(row.tyre || '').trim() || !completeBases.has(this._specRowBaseKey(row))
    );
  }

  _parseSpecs(data) {
    console.debug('[vehicle-search] raw specs data:', data);

    const chassis = data?.DRDChassisReturn;
    if (chassis) {
      const configs = this._getUniqueVehicleConfigs(data);
      const primary = configs[0] || chassis;
      const primaryFront = this._vehicleRimInfo(primary.RimSize)
        || this._vehicleRimInfo(chassis.RimSize);

      if (!primaryFront) {
        return { pcd: '', center_bore: 0, diameter_front: 0, diameter_rear: 0, options: [] };
      }

      const primaryRear = this._vehicleRimInfo(primary.RimSizeR) || primaryFront;
      const frontEt = this._vehicleFrontOffset(primary)
        ?? this._vehicleFrontOffset(chassis)
        ?? 0;
      const rearEt = this._vehicleRearOffset(primary)
        ?? (primaryRear ? frontEt : null)
        ?? this._vehicleRearOffset(chassis)
        ?? frontEt;

      let minDiam = Math.min(primaryFront.diameter, primaryRear.diameter);
      let maxDiam = Math.max(primaryFront.diameter, primaryRear.diameter);
      let maxWidthFront = parseFloat(chassis.MaxRimWidthFront || 0);
      let maxWidthRear = parseFloat(chassis.MaxRimWidthRear || 0);

      const hasSplit = config => {
        if (!config) return false;
        const front = this._vehicleRimInfo(config.RimSize);
        const rear = this._vehicleRimInfo(config.RimSizeR) || front;
        if (!front || !rear) return false;

        const fEt = this._vehicleFrontOffset(config);
        const rEt = this._vehicleRearOffset(config) ?? fEt;
        const frontTyre = String(config.TyreSize || config.TyreSizeFront || config.FrontTires || '').trim();
        const rearTyre = String(config.TyreSizeR || config.TyreSizeRear || config.RearTires || '').trim();

        return (
          front.diameter !== rear.diameter ||
          Math.abs(front.width - rear.width) > 0.001 ||
          (Number.isFinite(fEt) && Number.isFinite(rEt) && Math.abs(fEt - rEt) > 0.001) ||
          (!!rearTyre && rearTyre !== frontTyre)
        );
      };

      const options = [];
      const optionSeen = new Set();

      for (const config of configs) {
        const front = this._vehicleRimInfo(config.RimSize);
        if (!front) continue;
        const rear = this._vehicleRimInfo(config.RimSizeR) || front;
        const fEt = this._vehicleFrontOffset(config);
        const rEt = this._vehicleRearOffset(config) ?? fEt;

        minDiam = Math.min(minDiam, front.diameter, rear.diameter);
        maxDiam = Math.max(maxDiam, front.diameter, rear.diameter);
        maxWidthFront = Math.max(maxWidthFront || 0, front.width);
        maxWidthRear = Math.max(maxWidthRear || 0, rear.width);

        const option = {
          diameter_front: front.diameter,
          width_front: front.width,
          et_front: Number.isFinite(fEt) ? fEt : 0,
          diameter_rear: rear.diameter,
          width_rear: rear.width,
          et_rear: Number.isFinite(rEt) ? rEt : (Number.isFinite(fEt) ? fEt : 0),
        };

        const key = [
          option.diameter_front, option.width_front, option.et_front,
          option.diameter_rear, option.width_rear, option.et_rear,
        ].map(value => Number(value).toFixed(3)).join('|');
        if (!optionSeen.has(key)) {
          optionSeen.add(key);
          options.push(option);
        }
      }

      maxWidthFront = Math.max(maxWidthFront || 0, primaryFront.width);
      maxWidthRear = Math.max(maxWidthRear || 0, primaryRear.width);

      const rawPcd = primary.PCD || chassis.PCD || '';
      const isCenterlock = this._isCenterlockSpec(primary, rawPcd)
        || this._isCenterlockSpec(chassis, rawPcd);

      return {
        pcd: this._normalizeVehiclePcd(rawPcd),
        is_centerlock: isCenterlock,
        center_bore: parseFloat(primary.CentreBore || chassis.CentreBore || 0),
        center_bore_rear: parseFloat(primary.CentreBoreR || chassis.CentreBoreR || primary.CentreBore || chassis.CentreBore || 0),
        et_front: Number.isFinite(frontEt) ? frontEt : 0,
        et_rear: Number.isFinite(rearEt) ? rearEt : (Number.isFinite(frontEt) ? frontEt : 0),
        et_min_front: parseFloat(primary.MinETFront ?? chassis.MinETFront ?? 0),
        et_max_front: parseFloat(primary.MaxETFront ?? chassis.MaxETFront ?? 0),
        et_min_rear: parseFloat(primary.MinETRear ?? chassis.MinETRear ?? primary.MinETFront ?? chassis.MinETFront ?? 0),
        et_max_rear: parseFloat(primary.MaxETRear ?? chassis.MaxETRear ?? primary.MaxETFront ?? chassis.MaxETFront ?? 0),
        diameter_front: primaryFront.diameter,
        diameter_rear: primaryRear.diameter,
        diameter_min: minDiam,
        diameter_max: maxDiam,
        width_front: primaryFront.width,
        width_rear: primaryRear.width,
        width_max_front: maxWidthFront,
        width_max_rear: maxWidthRear,
        max_load_front: this._vehicle?.regnrSource === 'vegvesen'
          ? Math.ceil(parseFloat(chassis.MaxLoadFront || 0))
          : parseFloat(chassis.RequiredLoadFront || 0),
        max_load_rear: this._vehicle?.regnrSource === 'vegvesen'
          ? Math.ceil(parseFloat(chassis.MaxLoadRear || chassis.MaxLoadFront || 0))
          : parseFloat(chassis.RequiredLoadRear || chassis.RequiredLoadFront || 0),
        required_load: this._vehicle?.regnrSource === 'vegvesen'
          ? Math.max(
              Math.ceil(parseFloat(chassis.MaxLoadFront || 0)),
              Math.ceil(parseFloat(chassis.MaxLoadRear || chassis.MaxLoadFront || 0))
            )
          : parseFloat(
              chassis.RequiredLoad ||
              Math.max(
                parseFloat(chassis.RequiredLoadFront || 0),
                parseFloat(chassis.RequiredLoadRear || chassis.RequiredLoadFront || 0)
              ) ||
              0
            ),
        is_staggered: configs.some(hasSplit),
        options,
      };
    }

    // Fallback: older response format with wheels.front/rear or array.
    let front, rear;
    if (Array.isArray(data)) {
      front = data[0] || {};
      rear = data.length > 1 ? data[1] : front;
    } else {
      front = data.wheels?.front || data.front || data;
      rear = data.wheels?.rear || data.rear || front;
      if (Array.isArray(front)) {
        rear = front.length > 1 ? front[1] : front[0];
        front = front[0] || {};
      }
    }

    const frontDiameter = parseFloat(front.rim_diameter || front.Diameter || front.diameter || 0);
    const rearDiameter = parseFloat(rear.rim_diameter || rear.Diameter || rear.diameter || frontDiameter || 0);
    const frontWidth = parseFloat(front.rim_width || front.Width || front.width || 0);
    const rearWidth = parseFloat(rear.rim_width || rear.Width || rear.width || frontWidth || 0);
    const frontOffset = parseFloat(front.offset || front.et || 0);
    const rearOffset = parseFloat(rear.offset || rear.et || front.offset || front.et || 0);

    return {
      pcd: this._normalizeVehiclePcd(front.bolt_pattern || front.pcd),
      is_centerlock: this._isCenterlockSpec(front, front.bolt_pattern || front.pcd),
      center_bore: parseFloat(front.center_bore || front.hub_bore || front.bore || 0),
      et_front: frontOffset,
      et_rear: rearOffset,
      diameter_front: frontDiameter,
      diameter_rear: rearDiameter,
      diameter_min: 0,
      diameter_max: 0,
      width_front: frontWidth,
      width_rear: rearWidth,
      max_load_front: this._vehicle?.regnrSource === 'vegvesen'
        ? Math.ceil(parseFloat(front.MaxLoadFront || front.maxLoadFront || front.max_load_front || front.MaxLoad || front.max_load || 0))
        : parseFloat(front.RequiredLoadFront || front.requiredLoadFront || front.required_load_front || front.RequiredLoad || front.requiredLoad || front.required_load || 0),
      max_load_rear: this._vehicle?.regnrSource === 'vegvesen'
        ? Math.ceil(parseFloat(rear.MaxLoadRear || rear.maxLoadRear || rear.max_load_rear || rear.MaxLoad || rear.max_load || front.MaxLoadRear || front.maxLoadRear || front.max_load_rear || front.MaxLoadFront || front.maxLoadFront || front.max_load_front || 0))
        : parseFloat(rear.RequiredLoadRear || rear.requiredLoadRear || rear.required_load_rear || rear.RequiredLoad || rear.requiredLoad || rear.required_load || front.RequiredLoadRear || front.requiredLoadRear || front.required_load_rear || front.RequiredLoad || front.requiredLoad || front.required_load || 0),
      required_load: this._vehicle?.regnrSource === 'vegvesen'
        ? Math.max(
            Math.ceil(parseFloat(front.MaxLoadFront || front.maxLoadFront || front.max_load_front || front.MaxLoad || front.max_load || 0)),
            Math.ceil(parseFloat(rear.MaxLoadRear || rear.maxLoadRear || rear.max_load_rear || rear.MaxLoad || rear.max_load || front.MaxLoadRear || front.maxLoadRear || front.max_load_rear || front.MaxLoadFront || front.maxLoadFront || front.max_load_front || 0))
          )
        : Math.max(
            parseFloat(front.RequiredLoadFront || front.requiredLoadFront || front.required_load_front || front.RequiredLoad || front.requiredLoad || front.required_load || 0),
            parseFloat(rear.RequiredLoadRear || rear.requiredLoadRear || rear.required_load_rear || rear.RequiredLoad || rear.requiredLoad || rear.required_load || front.RequiredLoadRear || front.requiredLoadRear || front.required_load_rear || front.RequiredLoad || front.requiredLoad || front.required_load || 0)
          ),
      is_staggered: (
        frontDiameter !== rearDiameter ||
        frontWidth !== rearWidth ||
        frontOffset !== rearOffset
      ),
    };
  }

  // ── Vehicle header  // ── Vehicle header ───────────────────────────────────────────────────

  _showVehicleHeader() {
    if (!this._vehicle) {
      this._showVehicleSelector();
      return;
    }
    const parts = [
      this._vehicle.makeName || this._vehicle.makeSlug,
      this._vehicle.modelName || this._vehicle.modelSlug,
      this._vehicle.year || '',
      this._vehicle.trimName || '',
    ].filter(Boolean);
    this._vehicleLabel.textContent = parts.join(' ').toUpperCase();
    this._headerVehicle.hidden = false;
    this._headerSelector.hidden = true;
    this.classList.add('vs-pro-has-vehicle');
    this._updateMobileBrandTrigger();
  }

  async _changeVehicle() {
    const v = this._vehicle;
    // Skip transitions when restoring saved vehicle state
    this.dataset.noTransition = '';
    this._showVehicleSelector();
    if (!v?.makeSlug) { delete this.dataset.noTransition; return; }

    // Ensure makes are loaded, then pre-select
    if (this._makes.length === 0) await this._loadMakes();
    if (this._hMake) {
      this._hMake.value = v.makeSlug;
      if (!this._hMake.value) this._hMake.selectedIndex = 0;
    }

    // Load and pre-select model
    if (v.modelSlug) {
      await this._onMakeChange();
      if (this._hModel) this._hModel.value = v.modelSlug;
    }

    // Load and pre-select year
    if (v.modelSlug && v.year) {
      await this._onModelChange();
      if (this._hYear) this._hYear.value = v.year;
    }

    // Load and pre-select trim
    if (v.modelSlug && v.year && v.trimId) {
      await this._onYearChange();
      if (this._hTrim) this._hTrim.value = v.trimId;
      if (this._hSearch) this._hSearch.disabled = false;
    }

    this._updateMobileSteps();
    // Re-enable transitions after a frame
    requestAnimationFrame(() => { delete this.dataset.noTransition; });
  }

  _showVehicleSelector() {
    this._headerVehicle.hidden = true;
    this._headerSelector.hidden = false;
    this.classList.remove('vs-pro-has-vehicle');
    if (this._specsTable) this._specsTable.querySelector('tbody').innerHTML = '';
    // Close specs panel when switching to selector
    if (this._specsPanel) this._specsPanel.hidden = true;
    this._setSpecsToggleState(false);
    // Load makes if not already loaded
    if (this._makes.length === 0) {
      this._loadMakes();
    }
    requestAnimationFrame(() => this._updateMobileSteps());
  }

  _clearVehicle() {
    clearVehicle();
    sessionStorage.removeItem('fh_vs_state');
    this._vehicle = null;
    this._vehicleSpecs = null;
    this._diameterBar.innerHTML = '';
    this._selectedDiameter = null;
    this._selectedDiameterIsCustom = false;
    this._selectedOemSetKey = '';
    this._resetOemFallbackState();
    if (this._specsPanel) this._specsPanel.hidden = true;
    if (this._specsTable) this._specsTable.querySelector('tbody').innerHTML = '';
    this._setSpecsToggleState(false);
    this._axle = 'all';
    this._slidersInitialized = false;
    if (this._axleFilter) this._axleFilter.hidden = true;
    if (this._axleSelectSidebar) this._axleSelectSidebar.value = 'all';
    // Reset all filters
    Object.values(this._filters).forEach((sel) => { if (sel) sel.value = ''; });
    this._resetAccordions();
    this._brandSelection = this._defaultBrand;
    this._modelSelection = '';
    this._mobileBrandChosenByUser = false;
    this._updateMobileBrandTrigger();
    Object.values(this._rangeInputs).forEach((input) => { if (input) input.value = ''; });
    this._resetSlider('et');
    this._resetSlider('bore');
    // Hide recommendation labels
    const etSlider = this._sliders.et;
    if (etSlider?.rec) etSlider.rec.hidden = true;
    const boreSlider = this._sliders.bore;
    if (boreSlider?.rec) boreSlider.rec.hidden = true;
    this._resetSlider('price');
    // Show inline selector
    this._showVehicleSelector();
    // Reset inline selector state
    this._resetSelect(this._hModel, 'Modell');
    this._resetSelect(this._hYear, '\u00c5r');
    this._resetSelect(this._hTrim, 'Type');
    if (this._hMake) this._hMake.value = '';
    if (this._hSearch) this._hSearch.disabled = true;
    this._updateMobileSteps();
    // Reset regnr
    if (this._hRegnr) this._hRegnr.value = '';
    if (this._hRegnrSearch) {
      this._hRegnrSearch.disabled = true;
      delete this._hRegnrSearch.dataset.revealed;
    }
    // Keep text query if present
    const urlParams = new URLSearchParams();
    if (this._queryText) urlParams.set('q', this._queryText);
    const qs = urlParams.toString();
    window.history.replaceState({}, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
    this._page = 1;
    this._search();
  }

  _resetOemFallbackState() {
    this._oemFallbackContextKey = '';
    this._oemFallbackTriedDiameters = new Set();
    this._zeroResultOemSetKeys = new Set();
  }

  _markSelectedOemSetAsZeroResult() {
    if (this._selectedDiameterIsCustom || !this._selectedOemSetKey) return false;

    const button = [...(this._diameterBar?.querySelectorAll('.vs__diam-btn[data-custom="0"]') || [])]
      .find((candidate) => candidate.dataset.setKey === this._selectedOemSetKey);
    if (!button) return false;

    // A diameter with zero real products must disappear. Never convert an
    // empty OEM size into an Aventador/Custom choice: that previously caused
    // unsupported sizes such as 17" and 18" to remain visible.
    this._zeroResultOemSetKeys.add(this._selectedOemSetKey);
    button.classList.add('is-unavailable');
    button.hidden = true;
    button.disabled = true;
    button.setAttribute('aria-hidden', 'true');
    return false;
  }

  _selectDiameterButton(button) {
    if (!button) return;
    const diameter = parseInt(button.dataset.diam, 10);
    if (!Number.isFinite(diameter)) return;

    const isCustom = button.dataset.custom === '1';
    this._selectedDiameter = diameter;
    this._selectedDiameterIsCustom = isCustom;
    this._selectedOemSetKey = isCustom ? '' : (button.dataset.setKey || '');

    if (this._filters?.size) this._filters.size.value = String(diameter);
    this._syncDiameterButtonActiveState();
  }

  _isDiameterButtonSelected(button) {
    if (!button) return false;
    const diameter = parseInt(button.dataset.diam, 10);
    const isCustom = button.dataset.custom === '1';

    if (isCustom) {
      return this._selectedDiameterIsCustom && diameter === Number(this._selectedDiameter);
    }

    if (this._selectedOemSetKey) {
      return !this._selectedDiameterIsCustom && button.dataset.setKey === this._selectedOemSetKey;
    }

    return !this._selectedDiameterIsCustom && diameter === Number(this._selectedDiameter);
  }

  _syncDiameterButtonActiveState() {
    this._diameterBar?.querySelectorAll('.vs__diam-btn').forEach((button) => {
      button.classList.toggle('vs__diam-btn--active', this._isDiameterButtonSelected(button));
    });
  }

  _getAvailableOemButtons() {
    return [...(this._diameterBar?.querySelectorAll('.vs__diam-btn[data-custom="0"]') || [])]
      .filter((button) => !button.hidden && !button.disabled && !button.classList.contains('is-unavailable'));
  }

  _tryNextOemDiameterAfterEmpty() {
    if (this._page !== 1 || this._selectedDiameterIsCustom || !this._vehicle) return false;

    const currentDiameter = Number(this._selectedDiameter);
    if (Number.isFinite(currentDiameter) && currentDiameter > 0) {
      this._oemFallbackTriedDiameters.add(currentDiameter);
    }

    // Buttons are rendered in the preferred OEM order. Skip another setup
    // with the same front diameter because it would perform the identical
    // product query.
    const nextButton = this._getAvailableOemButtons().find((button) => {
      const diameter = parseInt(button.dataset.diam, 10);
      return Number.isFinite(diameter) && !this._oemFallbackTriedDiameters.has(diameter);
    });

    if (!nextButton) return false;
    this._selectDiameterButton(nextButton);
    return true;
  }

  // ── Diameter quick buttons ───────────────────────────────────────────


  _buildDiameterButtons(specs, rawData) {
    if (!specs.diameter_front) {
      console.debug('[vehicle-search] no diameter_front in specs, skipping diameter bar');
      return;
    }

    const axle = this._axle || 'all';
    let candidates = [];
    const candidateKeys = new Set();

    const addCandidate = (frontDiameter, rearDiameter = frontDiameter) => {
      const front = Math.round(Number(frontDiameter) || 0);
      const rear = Math.round(Number(rearDiameter) || front || 0);
      if (!front && !rear) return;

      let effectiveFront = front || rear;
      let effectiveRear = rear || front;
      if (axle === 'front') effectiveRear = effectiveFront;
      if (axle === 'rear') effectiveFront = effectiveRear;

      const key = `${effectiveFront}-${effectiveRear}`;
      if (candidateKeys.has(key)) return;
      candidateKeys.add(key);
      candidates.push({ front: effectiveFront, rear: effectiveRear, key });
    };

    if (rawData) {
      // Use the same repaired, trim-specific configuration source everywhere.
      // Never pair front and rear arrays by index, and never use a base chassis
      // row when trim-specific PrimaryOption/Options exist.
      const configs = this._getUniqueVehicleConfigs(rawData);
      const completeFrontDiameters = new Set();
      const incomplete = [];

      for (const config of configs) {
        const front = this._vehicleRimInfo(config.RimSize);
        const rear = this._vehicleRimInfo(config.RimSizeR);
        if (!front) continue;

        if (rear) {
          completeFrontDiameters.add(front.diameter);
          if (axle === 'front') addCandidate(front.diameter, front.diameter);
          else if (axle === 'rear') addCandidate(rear.diameter, rear.diameter);
          else addCandidate(front.diameter, rear.diameter);
        } else {
          incomplete.push(front);
        }
      }

      // A missing rear row may represent a square setup, but only use it when
      // no complete front/rear configuration exists for that front diameter.
      for (const front of incomplete) {
        if (completeFrontDiameters.has(front.diameter)) continue;
        addCandidate(front.diameter, front.diameter);
      }
    }

    if (!candidates.length) {
      const front = Math.round(specs.diameter_front || 0);
      const rear = Math.round(specs.diameter_rear || front || 0);
      addCandidate(front, rear);
    }

    // Keep every genuine OEM setup. Do NOT remove square setups merely because
    // another OEM option is staggered; some cars legitimately offer both.
    candidates.sort((a, b) => {
      if (a.front !== b.front) return a.front - b.front;
      if (a.rear !== b.rear) return a.rear - b.rear;
      return a.key.localeCompare(b.key);
    });

    const largestOemDiameter = candidates.reduce(
      (max, candidate) => Math.max(max, candidate.front, candidate.rear),
      0
    );
    const customSizes = largestOemDiameter
      ? [largestOemDiameter + 1, largestOemDiameter + 2].filter(diameter => diameter <= 24)
      : [];

    const buttons = candidates.map(candidate => {
      const mixedDiameter = candidate.front !== candidate.rear;
      return {
        diameter: candidate.front,
        rearDiameter: candidate.rear,
        setKey: `oem:${candidate.key}`,
        type: mixedDiameter ? 'oem-set' : 'oem',
        valueText: mixedDiameter
          ? `${candidate.front}" / ${candidate.rear}"`
          : `${candidate.front}"`,
        label: mixedDiameter ? 'OEM Sett' : 'OEM',
        sublabel: mixedDiameter ? 'Foran / Bak' : '',
      };
    });

    buttons.push(...customSizes.map(diameter => ({
      diameter,
      rearDiameter: diameter,
      setKey: `custom:${diameter}`,
      type: 'custom',
      valueText: `${diameter}"`,
      label: 'Custom',
      sublabel: '',
    })));

    const currentKey = this._selectedOemSetKey;
    const currentStillExists = buttons.some(button => button.type !== 'custom' && button.setKey === currentKey);
    const preferredOem = currentStillExists
      ? buttons.find(button => button.setKey === currentKey)
      : buttons
          .filter(button => button.type !== 'custom')
          .sort((a, b) => b.diameter - a.diameter || b.rearDiameter - a.rearDiameter)[0];

    this._selectedDiameter = preferredOem?.diameter || null;
    this._selectedDiameterIsCustom = false;
    this._selectedOemSetKey = preferredOem?.setKey || '';
    this._resetOemFallbackState();
    this._diameterAvailabilityResolvedKey = '';

    if (this._filters?.size) {
      this._filters.size.value = this._selectedDiameter ? String(this._selectedDiameter) : '';
    }

    this._diameterBar.style.visibility = 'hidden';
    this._diameterBar.setAttribute('aria-busy', 'true');

    this._diameterBar.innerHTML = buttons.map(button => {
      const { diameter, rearDiameter, setKey, type, valueText, label, sublabel } = button;
      const active = setKey === this._selectedOemSetKey ? ' vs__diam-btn--active' : '';
      const typeClass = type === 'custom'
        ? ' vs__diam-btn--custom'
        : (type === 'oem-set' ? ' vs__diam-btn--oem vs__diam-btn--set' : ' vs__diam-btn--oem');
      const customAttr = type === 'custom' ? ' data-custom="1"' : ' data-custom="0"';
      const sublabelHtml = sublabel ? `<span class="vs-pro-diam-sublabel">${sublabel}</span>` : '';
      return `<button type="button" class="vs__diam-btn vs-pro-diam-btn${typeClass}${active}" data-diam="${diameter}" data-rear-diam="${rearDiameter}" data-set-key="${setKey}"${customAttr}><span class="vs-pro-diam-check" aria-hidden="true">✓</span><span class="vs-pro-diam-value">${valueText}</span><span class="vs-pro-diam-label">${label}</span>${sublabelHtml}</button>`;
    }).join('');

    localStorage.removeItem('sbfWheelSize');
  }


  // ── Vehicle specs table  // ── Vehicle specs table ─────────────────────────────────────────────

  _buildSpecsTable(data) {
    if (!this._specsTable) return;
    const tbody = this._specsTable.querySelector('tbody');
    if (!tbody) return;

    const chassis = data.DRDChassisReturn;
    if (!chassis) { tbody.innerHTML = ''; return; }

    const fallbackPcd = chassis.PCD || '';
    const fallbackBore = chassis.CentreBore || '';
    const rows = [];

    for (const config of this._getUniqueVehicleConfigs(data)) {
      this._addSpecRows(rows, config, fallbackPcd, fallbackBore);
    }

    const unique = this._dedupeSpecRows(rows);

    unique.sort((a, b) => {
      if (a.diam !== b.diam) return a.diam - b.diam;
      if (a.width !== b.width) return a.width - b.width;
      const posOrder = { Foran: 0, Bak: 1, Begge: 2 };
      return (posOrder[a.pos] ?? 3) - (posOrder[b.pos] ?? 3);
    });

    tbody.innerHTML = unique.map(r => `<tr>
      <td>${this._esc(r.size)}</td>
      <td>${this._esc(r.pcd)}</td>
      <td>${this._esc(r.bore)}</td>
      <td>${this._esc(r.et)}</td>
      <td>${this._esc(r.tyre)}</td>
      <td>${this._esc(r.pos)}</td>
    </tr>`).join('');
  }

  _addSpecRows(rows, config, fallbackPcd, fallbackBore) {
    const frontRim = (config.RimSize || '').trim();
    const rearRim = (config.RimSizeR || '').trim();
    if (!frontRim) return;

    const rawPcd = config.PCD || fallbackPcd;
    const pcd = this._formatVehiclePcd(rawPcd, config);
    const bore = config.CentreBore ?? fallbackBore ?? '–';
    const frontETValue = this._vehicleFrontOffset(config);
    const rearETValue = this._vehicleRearOffset(config);
    const frontET = frontETValue ?? '';
    const rearET = rearETValue ?? '';
    const frontTyre = config.TyreSize || config.TyreSizeFront || '';
    const rearTyre = config.TyreSizeR || config.TyreSizeRear || '';

    const fSize = this._formatRimSize(frontRim);
    const rSize = this._formatRimSize(rearRim || frontRim);
    const fDiam = this._parseDiameter(frontRim);
    const rDiam = this._parseDiameter(rearRim || frontRim);
    const fWidth = this._parseWidth(frontRim);
    const rWidth = this._parseWidth(rearRim || frontRim);

    // Check if front and rear are identical (only when rear data exists)
    const hasRear = !!(rearRim && rearRim !== frontRim) || !!(rearET && rearET !== frontET) || !!(rearTyre && rearTyre !== frontTyre);

    if (!hasRear) {
      rows.push({
        size: fSize, pcd, bore: String(bore), et: String(frontET),
        tyre: frontTyre, pos: 'Begge', diam: fDiam, width: fWidth,
      });
    } else {
      rows.push({
        size: fSize, pcd, bore: String(bore), et: String(frontET),
        tyre: frontTyre, pos: 'Foran', diam: fDiam, width: fWidth,
      });
      rows.push({
        size: rSize, pcd, bore: String(bore), et: String(rearET),
        tyre: rearTyre, pos: 'Bak', diam: rDiam, width: rWidth,
      });
    }
  }

  /** "7.5 x 17" → "17x7.5 (OEM)" */
  _formatRimSize(rimStr) {
    const parts = rimStr.split('x').map((s) => s.trim());
    if (parts.length === 2) return `${parts[1]}x${parts[0]} (OEM)`;
    return `${rimStr} (OEM)`;
  }

  _parseDiameter(rimStr) {
    const parts = rimStr.split('x').map((s) => s.trim());
    return parts.length === 2 ? parseFloat(parts[1]) || 0 : 0;
  }

  _parseWidth(rimStr) {
    const parts = rimStr.split('x').map((s) => s.trim());
    return parts.length >= 1 ? parseFloat(parts[0]) || 0 : 0;
  }

  // ── Range sliders ──────────────────────────────────────────────────

  _initSlider(sliderKey, minFilterKey, maxFilterKey) {
    const s = this._sliders[sliderKey];
    if (!s) return;
    const { thumbMin, thumbMax } = s;

    // Initial fill
    this._updateSliderFill(sliderKey);

    // No active filter at start (thumbs at extremes = no constraint)
    const wireThumb = (e) => {
      // Clamp: min cannot exceed max, max cannot go below min
      const minV = parseFloat(thumbMin.value);
      const maxV = parseFloat(thumbMax.value);
      if (minV > maxV) {
        if (e.target === thumbMin) thumbMin.value = thumbMax.value;
        else thumbMax.value = thumbMin.value;
      }
      this._updateSliderFill(sliderKey);
      // Sync to hidden inputs
      const isAtMin = thumbMin.value === thumbMin.min;
      const isAtMax = thumbMax.value === thumbMax.max;
      this._rangeInputs[minFilterKey].value = isAtMin ? '' : thumbMin.value;
      this._rangeInputs[maxFilterKey].value = isAtMax ? '' : thumbMax.value;
      // Debounced search
      clearTimeout(this._rangeDebounce);
      this._rangeDebounce = setTimeout(() => {
        this._page = 1;
        this._search();
      }, 400);
    };

    thumbMin.addEventListener('input', wireThumb);
    thumbMax.addEventListener('input', wireThumb);
  }

  _updateSliderFill(sliderKey) {
    const s = this._sliders[sliderKey];
    if (!s) return;
    const { thumbMin, thumbMax, fill, valMin, valMax } = s;
    const lo = parseFloat(thumbMin.min);
    const hi = parseFloat(thumbMin.max);
    const range = hi - lo;
    if (!range) return;
    const left = ((thumbMin.value - lo) / range) * 100;
    const right = ((thumbMax.value - lo) / range) * 100;
    fill.style.left = left + '%';
    fill.style.width = (right - left) + '%';
    // Update displayed values
    if (sliderKey === 'price') {
      valMin.textContent = this._formatPrice(thumbMin.value);
      valMax.textContent = this._formatPrice(thumbMax.value);
    } else {
      const step = parseFloat(thumbMin.step) || 1;
      const fmt = step < 1 ? 1 : 0;
      valMin.textContent = parseFloat(thumbMin.value).toFixed(fmt);
      valMax.textContent = parseFloat(thumbMax.value).toFixed(fmt);
    }
  }

  _formatPrice(val) {
    const n = Math.round(parseFloat(val));
    return n.toLocaleString('nb-NO') + ' kr';
  }

  _setSlider(sliderKey, minVal, maxVal) {
    const s = this._sliders[sliderKey];
    if (!s) return;
    if (minVal != null) s.thumbMin.value = minVal;
    if (maxVal != null) s.thumbMax.value = maxVal;
    this._updateSliderFill(sliderKey);
  }

  _resetSlider(sliderKey) {
    const s = this._sliders[sliderKey];
    if (!s) return;
    s.thumbMin.value = s.thumbMin.min;
    s.thumbMax.value = s.thumbMax.max;
    this._updateSliderFill(sliderKey);
  }

  // ── API search ───────────────────────────────────────────────────────

  _getCheckedFilterValues(key) {
    return [...(this._accordions?.[key]?.querySelectorAll('input[type="checkbox"]:checked') || [])]
      .map((checkbox) => checkbox.value)
      .filter(Boolean);
  }

  _getRequiredVehicleLoad() {
    return parseFloat(
      this._vehicle?.specs?.required_load ||
      this._vehicle?.specs?.RequiredLoad ||
      this._vehicle?.RequiredLoad ||
      Math.max(
        parseFloat(
          this._vehicle?.specs?.RequiredLoadFront ||
          this._vehicle?.specs?.max_load_front ||
          0
        ),
        parseFloat(
          this._vehicle?.specs?.RequiredLoadRear ||
          this._vehicle?.specs?.max_load_rear ||
          0
        )
      ) ||
      0
    );
  }

  _isAventadorOnlySearch() {
    const checkedBrands = this._getCheckedFilterValues('brand');
    const brands = checkedBrands.length
      ? checkedBrands
      : (this._brandSelection ? [this._brandSelection] : []);
    return brands.length === 1
      && String(brands[0]).trim().toLowerCase() === 'aventador wheels';
  }

  _isAventadorAllowedByBrandFilter() {
    const checkedBrands = this._getCheckedFilterValues('brand');
    const brands = checkedBrands.length
      ? checkedBrands
      : (this._brandSelection ? [this._brandSelection] : []);

    // No explicit brand filter means Aventador Wheels is allowed together
    // with the other brands. Otherwise it must be included explicitly.
    if (!brands.length) return true;
    return brands.some((brand) =>
      String(brand).trim().toLowerCase() === 'aventador wheels'
    );
  }

  _getSingleSelectedBrand() {
    const checkedBrands = this._getCheckedFilterValues('brand');
    if (checkedBrands.length === 1) return String(checkedBrands[0]).trim();
    if (checkedBrands.length > 1) return '';
    return String(this._brandSelection || '').trim();
  }

  _isStockBrandSearch() {
    const brand = this._getSingleSelectedBrand().toLowerCase();
    return !!brand && brand !== 'aventador wheels';
  }

  _isExtendedStockBrand() {
    const brand = this._getSingleSelectedBrand().toLowerCase();
    return brand === 'concaver' || brand === 'vesser';
  }

  _buildStockBrandFallbackParams(baseParams) {
    const params = new URLSearchParams(baseParams.toString());

    // Some products have correct PCD/load/bore/size data but are missing an
    // exact make/model/trim relation. Keep the technical safety filters and
    // remove only the brittle vehicle-row identifiers.
    ['make', 'model', 'trim', 'axle'].forEach((key) => params.delete(key));

    // The normal result page only requests 24 products. That caused the broad
    // technical fallback to include only its first page, even when the brand
    // facet showed 50+ compatible products. Load the full compatible stock set
    // before grouping by wheel model and finish.
    params.set('limit', '200');
    params.set('page', '1');

    return params;
  }

  async _fetchStockBrandFallback(baseParams) {
    if (!this._vehicle || !this._isStockBrandSearch() || this._selectedDiameterIsCustom) {
      return null;
    }

    const params = this._buildStockBrandFallbackParams(baseParams);
    const response = await fetch(`${this._api}/api/v2/wheels?${params}`);
    if (!response.ok) throw new Error(`Stock brand fallback API ${response.status}`);
    const json = await response.json();
    return (json?.data || []).length ? json : null;
  }

  _buildAventadorOemFallbackParams(baseParams) {
    const params = new URLSearchParams(baseParams.toString());

    // Aventador Wheels are produced to the selected car. For an OEM set we
    // therefore keep the selected front diameter, but remove filters that only
    // make sense for stock wheels with fixed ET, bore, width and vehicle rows.
    [
      'make', 'model', 'trim', 'axle',
      'required_load', 'min_load',
      'pcd', 'min_bore', 'max_bore',
      'min_et', 'max_et',
      'min_width', 'max_width',
    ].forEach((key) => params.delete(key));

    params.set('brand', 'Aventador Wheels');
    return params;
  }

  async _fetchAventadorOemFallback(baseParams) {
    if (
      !this._vehicle ||
      this._selectedDiameterIsCustom ||
      !this._selectedDiameter ||
      !this._isAventadorAllowedByBrandFilter()
    ) {
      return null;
    }

    const params = this._buildAventadorOemFallbackParams(baseParams);
    const response = await fetch(`${this._api}/api/v2/wheels?${params}`);
    if (!response.ok) throw new Error(`Aventador OEM fallback API ${response.status}`);
    const json = await response.json();
    return (json?.data || []).length ? json : null;
  }

  _shouldUseVehicleFitmentFilters() {
    return !!this._vehicle
      && !this._selectedDiameterIsCustom
      && !this._vehicle?.specs?.is_centerlock
      && !this._isAventadorOnlySearch();
  }

  _hasUsableVehiclePcd() {
    const specs = this._vehicle?.specs;
    return !!specs && !specs.is_centerlock && !!this._normalizeVehiclePcd(specs.pcd);
  }

  _buildDiameterAvailabilityParams({ custom = false } = {}) {
    const params = new URLSearchParams();
    params.set('limit', '1');
    params.set('page', '1');

    if (this._queryText) params.set('q', this._queryText);

    const centerlockVehicle = !!this._vehicle?.specs?.is_centerlock;
    const madeToOrderAventador = custom || centerlockVehicle || this._isAventadorOnlySearch();
    const useVehicleFitment = !custom && this._shouldUseVehicleFitmentFilters();

    if (useVehicleFitment) {
      if (this._vehicle.makeSlug) params.set('make', this._vehicle.makeSlug);
      if (this._vehicle.modelSlug) params.set('model', this._vehicle.modelSlug);
      if (this._vehicle.trimId) params.set('trim', this._vehicle.trimId);
      if (this._axle && this._axle !== 'all') params.set('axle', this._axle);
    }

    const requiredLoad = this._getRequiredVehicleLoad();
    if (!madeToOrderAventador && requiredLoad > 0) {
      const roundedRequiredLoad = String(Math.ceil(requiredLoad));
      params.set('required_load', roundedRequiredLoad);
      params.set('min_load', roundedRequiredLoad);
    }

    const widthVal = this._filters?.width?.value;
    if (widthVal) {
      params.set('min_width', widthVal);
      params.set('max_width', widthVal);
    }

    const colorVals = this._getCheckedFilterValues('color');
    if (colorVals.length) params.set('color', colorVals.join(','));

    const selectedBrands = this._getCheckedFilterValues('brand');
    if (custom || centerlockVehicle) {
      params.set('brand', 'Aventador Wheels');
    } else if (selectedBrands.length) {
      params.set('brand', selectedBrands.join(','));
    } else if (this._brandSelection) {
      params.set('brand', this._brandSelection);
    }

    const modelVals = this._getCheckedFilterValues('wheel_model');
    if (modelVals.length) params.set('wheel_model', modelVals.join(','));

    if (useVehicleFitment) {
      const pcdVal = this._filters?.pcd?.value;
      if (pcdVal) {
        params.set('pcd', pcdVal);
      } else if (this._hasUsableVehiclePcd()) {
        params.set('pcd', this._normalizeVehiclePcd(this._vehicle.specs.pcd));
      }

      if (
        !this._vehicle?.specs?.is_centerlock &&
        this._vehicle?.specs?.center_bore &&
        !this._rangeInputs?.min_bore?.value
      ) {
        params.set('min_bore', String(this._vehicle.specs.center_bore));
      }
    }

    for (const [key, input] of Object.entries(this._rangeInputs || {})) {
      const value = input?.value;
      if (!value) continue;
      if (madeToOrderAventador && ['min_et', 'max_et', 'min_bore', 'max_bore'].includes(key)) continue;
      params.set(key, value);
    }

    // Deliberately do not include min_size/max_size here. The size facet from
    // this request is used to decide which diameter buttons have real matches.
    return params;
  }

  _collectAvailableDiameters(json) {
    const sizes = new Set();

    for (const item of json?.facets?.size || []) {
      const diameter = Math.round(parseFloat(item?.value));
      const count = parseInt(item?.count, 10);
      if (diameter > 0 && (!Number.isFinite(count) || count > 0)) sizes.add(diameter);
    }

    for (const product of json?.data || []) {
      for (const value of product?.available_sizes || []) {
        const diameter = Math.round(parseFloat(value));
        if (diameter > 0) sizes.add(diameter);
      }
    }

    return sizes;
  }

  async _fetchAvailableDiameters(params) {
    const cacheKey = params.toString();
    const cached = this._diameterAvailabilityCache.get(cacheKey);
    if (cached) return new Set(cached);

    const response = await fetch(`${this._api}/api/v2/wheels?${params}`);
    if (!response.ok) throw new Error(`Diameter availability API ${response.status}`);
    const json = await response.json();
    const sizes = this._collectAvailableDiameters(json);

    this._diameterAvailabilityCache.set(cacheKey, [...sizes]);
    if (this._diameterAvailabilityCache.size > 30) {
      const oldestKey = this._diameterAvailabilityCache.keys().next().value;
      this._diameterAvailabilityCache.delete(oldestKey);
    }

    return sizes;
  }

  async _refreshDiameterAvailability() {
    const buttons = [...(this._diameterBar?.querySelectorAll('.vs-pro-diam-btn') || [])];
    if (!buttons.length || !this._vehicle?.specs) {
      if (this._diameterBar) {
        this._diameterBar.style.visibility = '';
        this._diameterBar.removeAttribute('aria-busy');
      }
      return false;
    }

    const requestId = ++this._diameterAvailabilityRequestId;

    try {
      // One broad request is both faster and more reliable than one request per
      // diameter. The API size facet tells us which OEM diameters really have
      // products for the selected vehicle and brand.
      const oemParams = this._buildDiameterAvailabilityParams({ custom: false });
      let oemSizes = await this._fetchAvailableDiameters(oemParams);

      // Retry the size facet without exact make/model/trim mapping when the
      // selected stock brand has technically compatible products but no
      // explicit vehicle relation in the database.
      if (!oemSizes.size && this._isStockBrandSearch()) {
        const fallbackParams = this._buildStockBrandFallbackParams(oemParams);
        oemSizes = await this._fetchAvailableDiameters(fallbackParams);
      }

      const aventadorAllowed = this._isAventadorAllowedByBrandFilter();
      const customSizes = aventadorAllowed
        ? await this._fetchAvailableDiameters(
            this._buildDiameterAvailabilityParams({ custom: true })
          )
        : new Set();

      if (requestId !== this._diameterAvailabilityRequestId) return false;

      let selectionChanged = false;
      let selectedButtonIsAvailable = false;
      const availableOemButtons = [];

      const selectedBrand = this._getSingleSelectedBrand().toLowerCase();
      const extendedStockBrand = this._isExtendedStockBrand();

      for (const button of buttons) {
        const diameter = parseInt(button.dataset.diam, 10);
        const originallyCustom = button.classList.contains('vs__diam-btn--custom');

        // All sizes outside the vehicle's OEM dimensions are labelled Custom.
        // Stock brands still use their existing technical availability logic;
        // only the customer-facing label is unified across all brands.
        if (originallyCustom) {
          const label = button.querySelector('.vs-pro-diam-label');

          if (selectedBrand === 'aventador wheels') {
            button.dataset.custom = '1';
            button.dataset.upgrade = '0';
            button.dataset.setKey = `custom:${diameter}`;
            button.classList.remove('vs__diam-btn--upgrade');
            if (label) label.textContent = 'Custom';
          } else if (extendedStockBrand) {
            button.dataset.custom = '0';
            button.dataset.upgrade = '1';
            button.dataset.setKey = `upgrade:${diameter}`;
            button.classList.add('vs__diam-btn--upgrade');
            if (label) label.textContent = 'Custom';
          } else {
            button.dataset.custom = '0';
            button.dataset.upgrade = '0';
            button.classList.remove('vs__diam-btn--upgrade');
          }
        }

        const isCustom = button.dataset.custom === '1';
        const isUpgrade = button.dataset.upgrade === '1';
        const blockedByZeroResult = !isCustom && !isUpgrade
          && this._zeroResultOemSetKeys.has(button.dataset.setKey || '');

        const available = isCustom
          ? aventadorAllowed
            && Number.isFinite(diameter)
            && diameter > 0
            && diameter <= 24
            && (customSizes.size === 0 || customSizes.has(diameter))
          : isUpgrade
            ? Number.isFinite(diameter) && oemSizes.has(diameter)
            : Number.isFinite(diameter)
              && oemSizes.has(diameter)
              && !blockedByZeroResult;

        button.classList.toggle('is-unavailable', !available);
        button.hidden = !available;
        button.setAttribute('aria-hidden', available ? 'false' : 'true');
        button.disabled = !available;

        if (available && !isCustom) availableOemButtons.push(button);
        if (available && this._isDiameterButtonSelected(button)) {
          selectedButtonIsAvailable = true;
        }
      }

      if (this._selectedDiameter && !selectedButtonIsAvailable) {
        this._selectedDiameter = null;
        this._selectedDiameterIsCustom = false;
        this._selectedOemSetKey = '';
        if (this._filters?.size) this._filters.size.value = '';
        selectionChanged = true;
      }

      // Prefer the largest valid OEM diameter for the newly selected brand.
      if (!this._selectedDiameter && availableOemButtons.length) {
        const largestAvailableOem = availableOemButtons
          .slice()
          .sort((a, b) => parseInt(b.dataset.diam, 10) - parseInt(a.dataset.diam, 10))[0];
        this._selectDiameterButton(largestAvailableOem);
        selectionChanged = true;
      }

      this._syncDiameterButtonActiveState();
      return selectionChanged;
    } catch (error) {
      console.warn('Could not refresh diameter availability', error);
      return false;
    } finally {
      if (requestId === this._diameterAvailabilityRequestId && this._diameterBar) {
        this._diameterBar.style.visibility = '';
        this._diameterBar.removeAttribute('aria-busy');
      }
    }
  }

  async _search() {
    const searchRequestId = ++this._searchRequestId;

    // A selected vehicle opens in the Aventador collection unless the customer
    // has explicitly asked to see all brands or selected another brand.
    const explicitBrandValues = this._getCheckedFilterValues('brand');
    if (
      this._vehicle?.specs &&
      !this._showAllBrands &&
      explicitBrandValues.length === 0 &&
      !this._brandSelection
    ) {
      this._brandSelection = 'Aventador Wheels';
    }

    this._updateMobileBrandTrigger();
    this._loading.hidden = false;
    this._empty.hidden = true;
    this._grid.innerHTML = '';
    this._pagination.innerHTML = '';

    const params = new URLSearchParams();

    // Every brand must show all wheel models on one page. The API returns one
    // row per finish, so use a high limit before grouping rows into model cards.
    // Images remain lazy-loaded, so models below the viewport do not download
    // their main image until the customer scrolls toward them.
    const aventadorCollectionRequest = this._isAventadorOnlySearch();
    params.set('limit', '500');
    params.set('page', '1');
    this._page = 1;

    const centerlockVehicle = !!this._vehicle?.specs?.is_centerlock;
    const madeToOrderAventador = this._selectedDiameterIsCustom
      || centerlockVehicle
      || this._isAventadorOnlySearch();
    const useVehicleFitment = this._shouldUseVehicleFitmentFilters();

    // Text search
    if (this._queryText) {
      params.set('q', this._queryText);
    }

    // Vehicle filters.
    // Custom sizes are made-to-order Aventador Wheels, so they must not be
    // removed by the normal OEM fitment filters.
    if (useVehicleFitment) {
      if (this._vehicle.makeSlug) params.set('make', this._vehicle.makeSlug);
      if (this._vehicle.modelSlug) params.set('model', this._vehicle.modelSlug);
      if (this._vehicle.trimId) params.set('trim', this._vehicle.trimId);
      if (this._axle && this._axle !== 'all') params.set('axle', this._axle);
    }

    const requiredLoad = this._getRequiredVehicleLoad();

    if (requiredLoad > 0 && !madeToOrderAventador) {
      const roundedRequiredLoad = String(Math.ceil(requiredLoad));
      params.set('required_load', roundedRequiredLoad);
      params.set('min_load', roundedRequiredLoad);
    }

    if (this._sort) params.set('sort', this._sort);
    if (this._selectedDiameter && !this._selectedDiameterIsCustom) {
      params.set('min_size', String(this._selectedDiameter));
      params.set('max_size', String(this._selectedDiameter));
    }

    // Sidebar select filters
    const sizeVal = this._filters.size?.value;
    if (sizeVal && !this._selectedDiameter) {
      params.set('min_size', sizeVal);
      params.set('max_size', sizeVal);
    }
    const widthVal = this._filters.width?.value;
    if (widthVal) {
      params.set('min_width', widthVal);
      params.set('max_width', widthVal);
    }
    const getChecked = (key) => [...(this._accordions[key]?.querySelectorAll('input:checked') || [])].map(cb => cb.value);
    const colorVals = getChecked('color');
    if (colorVals.length) params.set('color', colorVals.join(','));
    const brandVals = getChecked('brand');
    if (this._selectedDiameterIsCustom || centerlockVehicle) {
      params.set('brand', 'Aventador Wheels');
    } else if (brandVals.length) {
      params.set('brand', brandVals.join(','));
    } else if (this._brandSelection) {
      params.set('brand', this._brandSelection);
    }
    const modelVals = getChecked('wheel_model');
    if (modelVals.length) params.set('wheel_model', modelVals.join(','));
    const pcdVal = this._filters.pcd?.value;
    if (useVehicleFitment) {
      if (pcdVal) params.set('pcd', pcdVal);
      // Never send N/A/blank PCD values to the API. Centerlock is handled
      // separately and must not be treated as a normal bolt pattern.
      if (!pcdVal && this._hasUsableVehiclePcd()) {
        params.set('pcd', this._normalizeVehiclePcd(this._vehicle.specs.pcd));
      }
      // Centerlock and made-to-order Aventador Wheels must not be rejected by
      // a normal hub-bore filter.
      if (
        !this._vehicle?.specs?.is_centerlock &&
        this._vehicle?.specs?.center_bore &&
        !this._rangeInputs.min_bore?.value
      ) {
        params.set('min_bore', String(this._vehicle.specs.center_bore));
      }

      // Send vehicle ET limits automatically if not manually overridden
      const _axle = this._axle || 'all';
      const _isRear = _axle === 'rear';
      const _etMin = _isRear
        ? (this._vehicle.specs.et_min_rear || this._vehicle.specs.et_min_front)
        : this._vehicle.specs.et_min_front;
      const _etMax = _isRear
        ? (this._vehicle.specs.et_max_rear || this._vehicle.specs.et_max_front)
        : this._vehicle.specs.et_max_front;
      if (_etMin > 0 && !this._rangeInputs.min_et?.value) {
        params.set('min_et', String(_etMin));
      }
      if (_etMax > 0 && !this._rangeInputs.max_et?.value) {
        params.set('max_et', String(_etMax));
      }
    }

    // Range filter inputs. Custom Aventador searches ignore ET and bore,
    // because each wheel is produced to the selected vehicle.
    for (const [key, input] of Object.entries(this._rangeInputs)) {
      const val = input?.value;
      if (!val) continue;
      if (madeToOrderAventador && ['min_et', 'max_et', 'min_bore', 'max_bore'].includes(key)) {
        continue;
      }
      params.set(key, val);
    }

    // Save state to sessionStorage
    const stateToSave = {
      sort: this._sort,
      selectedDiameter: this._selectedDiameter,
      selectedDiameterIsCustom: this._selectedDiameterIsCustom,
      selectedOemSetKey: this._selectedOemSetKey,
      size: sizeVal,
      width: widthVal,
      pcd: pcdVal,
      axle: this._axle,
      color: colorVals,
      brand: brandVals,
      wheel_model: modelVals,
      ranges: {}
    };
    for (const [key, input] of Object.entries(this._rangeInputs)) {
      if (input && input.value) stateToSave.ranges[key] = input.value;
    }
    sessionStorage.setItem('fh_vs_state', JSON.stringify(stateToSave));

    // Keep automatic OEM fallback attempts scoped to the current vehicle and
    // non-diameter filters. Changing brand, model, load, ET, etc. starts over.
    const fallbackContextParams = new URLSearchParams(params);
    fallbackContextParams.delete('min_size');
    fallbackContextParams.delete('max_size');
    fallbackContextParams.delete('page');
    const fallbackContextKey = fallbackContextParams.toString();
    if (this._oemFallbackContextKey !== fallbackContextKey) {
      this._oemFallbackContextKey = fallbackContextKey;
      this._oemFallbackTriedDiameters = new Set();
      this._zeroResultOemSetKeys = new Set();
    }

    // Resolve diameter availability in parallel with the product request.
    // The result is cached per vehicle/filter context, so normal sorting and
    // pagination do not repeat the checks.
    const availabilityContextParams = new URLSearchParams(params);
    availabilityContextParams.delete('min_size');
    availabilityContextParams.delete('max_size');
    availabilityContextParams.delete('page');
    availabilityContextParams.delete('sort');
    const availabilityContextKey = availabilityContextParams.toString();
    const needsAvailabilityRefresh = !!this._vehicle?.specs
      && this._diameterAvailabilityResolvedKey !== availabilityContextKey;
    const availabilityPromise = needsAvailabilityRefresh
      ? this._refreshDiameterAvailability()
      : Promise.resolve(false);

    try {
      console.debug('[vehicle-search] load filter active:', {
        requiredLoad,
        request: `${this._api}/api/v2/wheels?${params}`
      });
      const [res, diameterSelectionChanged] = await Promise.all([
        fetch(`${this._api}/api/v2/wheels?${params}`),
        availabilityPromise,
      ]);

      if (searchRequestId !== this._searchRequestId) return;
      if (!res.ok) throw new Error(`API ${res.status}`);

      if (needsAvailabilityRefresh) {
        this._diameterAvailabilityResolvedKey = availabilityContextKey;
      }

      if (diameterSelectionChanged) {
        this._page = 1;
        this._search();
        return;
      }

      let json = await res.json();
      let products = json.data || [];

      // Stock brands often have incomplete make/model/trim mappings even when
      // the wheel is technically compatible. Always load the broader technical
      // fallback for Japan Racing, Concaver and other stock brands, then merge
      // it with the exact vehicle result. This prevents valid wheel models from
      // disappearing just because only a few products have an exact vehicle row.
      if (!this._selectedDiameterIsCustom && this._isStockBrandSearch()) {
        const stockFallback = await this._fetchStockBrandFallback(params);

        if (stockFallback) {
          const fallbackProducts = stockFallback.data || [];
          const mergedProducts = [];
          const seenProducts = new Set();

          const productKey = (product) =>
            String(
              product?.id ||
              product?.shopify_handle ||
              product?.group_handle ||
              [
                product?.brand,
                product?.wheel_model || product?.model,
                product?.colour || product?.color,
                product?.size,
                product?.width,
                product?.et_min,
                product?.et_max,
              ].join('|')
            );

          for (const product of [...products, ...fallbackProducts]) {
            const key = productKey(product);
            if (seenProducts.has(key)) continue;
            seenProducts.add(key);
            mergedProducts.push(product);
          }

          products = mergedProducts;

          // Use the broader technical facets/counts when they contain more
          // compatible products than the brittle exact-vehicle mapping.
          if (
            Number(stockFallback?.pagination?.total || 0) >
            Number(json?.pagination?.total || 0)
          ) {
            json = {
              ...json,
              facets: stockFallback.facets || json.facets,
              pagination: stockFallback.pagination || json.pagination,
            };
          }
        }
      }

      // Aventador fallback is allowed only when Aventador Wheels is actually
      // the active brand (or no brand was explicitly selected). It must never
      // inject Aventador products into Concaver/Japan Racing/Vesser results.
      const activeBrandForResults = this._getSingleSelectedBrand().toLowerCase();
      const allowAventadorFallback =
        !activeBrandForResults || activeBrandForResults === 'aventador wheels';

      if (
        products.length === 0 &&
        !this._selectedDiameterIsCustom &&
        allowAventadorFallback
      ) {
        const aventadorFallback = await this._fetchAventadorOemFallback(params);
        if (aventadorFallback) {
          json = aventadorFallback;
          products = json.data || [];
        }
      }

      if (searchRequestId !== this._searchRequestId) return;

      // Defensive brand isolation:
      // even if the API returns mixed brands, render only the exact brand the
      // customer selected in the checkbox/mobile selector.
      const exactSelectedBrand = this._getSingleSelectedBrand();
      if (exactSelectedBrand) {
        const selectedBrandKey = exactSelectedBrand.trim().toLowerCase();
        products = products.filter((product) =>
          String(product?.brand || '').trim().toLowerCase() === selectedBrandKey
        );
      }

      // The main fitment query can omit Custom Finish because its representative
      // row may use a different PCD/ET/bore. Fetch Custom Finish separately and
      // merge it only into wheel models that are already valid and visible.
      if (
        exactSelectedBrand &&
        exactSelectedBrand !== 'Aventador Wheels' &&
        !colorVals.length
      ) {
        const customRows = await this._fetchSupplementalCustomFinishes(
          params,
          products
        );

        if (searchRequestId !== this._searchRequestId) return;
        products = this._mergeSupplementalCustomFinishes(products, customRows);
      }

      this._loading.hidden = true;

      const pagination = json.pagination || {};

      // The API counts finish rows. In Aventador mode the grid groups those
      // rows into wheel models, so the final model count is updated after render.
      const visibleResultCount = exactSelectedBrand
        ? products.length
        : (pagination.total || products.length || 0);
      const countText = this._queryText
        ? `${visibleResultCount} resultater for "${this._queryText}"`
        : `${visibleResultCount} Resultater`;
      this._countEl.textContent = countText;
      if (this._sidebarCount) this._sidebarCount.textContent = `${visibleResultCount} Resultater`;

      // Always update facets so filters stay usable even with 0 results
      this._updateFacets(json.facets);

      const aventadorMode = this._isAventadorOnlySearch();
      this._setAventadorMode(aventadorMode);

      if (products.length === 0) {
        const selectedBrandsNow = this._getCheckedFilterValues('brand');
        const explicitNonAventadorBrand =
          selectedBrandsNow.length > 0 &&
          !selectedBrandsNow.includes('Aventador Wheels');

        // For stock brands, diameter availability has already been resolved
        // from the broad size facet. Do not recursively hide every OEM size;
        // that was the cause of Concaver ending with only 23"/24" Custom.
        if (!explicitNonAventadorBrand) {
          const convertedToRecommended = this._markSelectedOemSetAsZeroResult();
          if (convertedToRecommended) {
            this._page = 1;
            this._search();
            return;
          }
          if (this._tryNextOemDiameterAfterEmpty()) {
            this._page = 1;
            this._search();
            return;
          }
        }

        this._empty.hidden = false;
        this._pagination.innerHTML = '';
        return;
      }

      products = await this._filterHybridForgedFinishesByShopifyAvailability(products);
      products = await this._hydrateHybridForgedPrices(products);
      this._renderGrid(products);

      // All brands are loaded on one page. Pagination is intentionally removed.
      this._pagination.innerHTML = '';
    } catch (e) {
      if (searchRequestId !== this._searchRequestId) return;
      console.error('Search failed', e);
      this._loading.hidden = true;
      this._empty.hidden = false;
      this._empty.querySelector('p').textContent =
        'Noe gikk galt. Pr\u00f8v igjen senere.';
    }
  }

  // ── Render product grid ──────────────────────────────────────────────

  async _hydrateHybridForgedPrices(products) {
    const groups = Array.isArray(products) ? products : [];
    const targets = groups.filter((g) => {
      const method = String(g.production_method || '');
      const model = String(g.wheel_model || g.model || '');
      return /hybrid[\s_-]*forged/i.test(`${method} ${model}`);
    });

    if (!targets.length) return groups;

    const handleFor = (g) => {
      const brand = String(g.brand || '').trim();
      const model = String(g.wheel_model || g.model || '').trim();
      const colour = String(g.colour || g.color || '').trim();
      return this._slugify(`${brand}-${model}-${colour}`);
    };

    const uniqueHandles = [...new Set(targets.map(handleFor).filter(Boolean))];
    const priceMap = new Map();

    await Promise.all(uniqueHandles.map(async (handle) => {
      try {
        const response = await fetch(
          `${this._api}/api/wheels/product-data?handle=${encodeURIComponent(handle)}`
        );
        if (!response.ok) return;

        const json = await response.json();
        const prices = (json?.variants || [])
          .map((variant) => Number(variant?.price) / 100)
          .filter((price) => Number.isFinite(price) && price > 0);

        if (prices.length) {
          priceMap.set(handle, {
            min: Math.min(...prices),
            max: Math.max(...prices),
          });
        }
      } catch (error) {
        console.warn(`Could not load live Hybrid-Forged price for ${handle}`, error);
      }
    }));

    for (const group of targets) {
      const live = priceMap.get(handleFor(group));
      if (!live) continue;

      group.min_price_nok = live.min;
      group.max_price_nok = live.max;
    }

    return groups;
  }

  _slugify(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  _setAventadorMode(active) {
    const isActive = !!active;
    this.classList.toggle('vs-aventador-mode', isActive);
    if (this._aventadorHero) this._aventadorHero.hidden = true;
  }

  _formatPrice(group) {
    const brand = String(group?.brand || '').trim().toLowerCase();
    const methodText = `${group?.production_method || ''} ${group?.wheel_model || group?.model || ''}`;
    const isAventador = brand === 'aventador wheels';
    const isHybridForged = /hybrid[\s_-]*forged/i.test(methodText);

    // Hybrid-Forged Stealth is stocked and has a fixed listing "from" price
    // in the SBF search results. The live Shopify variant minimum does not
    // reflect the configurator's agreed fitment price.
    if (isAventador && isHybridForged) {
      return 'fra 3 400 kr';
    }

    // Aventador Wheels Fully Forged has the same diameter-based price structure
    // across all models. The API group price can otherwise fall back to the
    // cheapest variant across all sizes, which gives a wrong card price.
    // Use the actual "fra"-price for the selected diameter, regardless of
    // whether that diameter is OEM or Custom.
    if (isAventador && !isHybridForged) {
      const diameterFromPrices = {
        19: 5999,
        20: 7099,
        21: 8499,
        22: 8799,
        23: 9699,
        24: 10499,
      };

      const diameter = Math.round(Number(this._selectedDiameter));
      const diameterFromPrice = diameterFromPrices[diameter];

      if (Number.isFinite(diameterFromPrice)) {
        return `fra ${diameterFromPrice.toLocaleString('nb-NO')} kr`;
      }
    }

    if (group?.min_price_nok == null) return '';
    const min = Math.round(Number(group.min_price_nok));
    const max = group?.max_price_nok == null ? min : Math.round(Number(group.max_price_nok));
    if (!Number.isFinite(min)) return '';
    if (Number.isFinite(max) && max !== min) {
      return `fra ${min.toLocaleString('nb-NO')} kr`;
    }
    return `${min.toLocaleString('nb-NO')} kr`;
  }

  _productHref(group) {
    let href = group?.group_handle ? `/products/${group.group_handle}` : '#';
    const isHybridForged = /hybrid[\s_-]*forged/i.test(
      `${group?.production_method || group?.method || ''} ${group?.wheel_model || group?.model || ''} ${group?.name || ''}`
    );

    if (this._selectedDiameter && group?.group_handle) {
      href += `?diameter=${this._selectedDiameter}`;

      // Hybrid-Forged has fixed, vehicle-compatible sizes and ET values.
      // It must always open the recommended fitment menu, never Custom Fit.
      if (this._selectedDiameterIsCustom && !isHybridForged) {
        href += '&custom_fit=1';
      } else {
        const activeOemButton = this._diameterBar?.querySelector('.vs__diam-btn--active[data-custom="0"]');
        const rearDiameter = parseInt(activeOemButton?.dataset.rearDiam || '', 10);
        if (Number.isFinite(rearDiameter) && rearDiameter > 0) {
          href += `&rear_diameter=${rearDiameter}`;
        }
        if (this._selectedOemSetKey) href += `&oem_set=${encodeURIComponent(this._selectedOemSetKey)}`;
        if (isHybridForged) href += '&recommended_fit=1';
      }
    }
    return href;
  }

  _finishSwatchBackground(name) {
    const normalized = String(name || '').trim().toLowerCase();

    const swatches = {
      'double tinted black':
        'radial-gradient(circle at 32% 28%, #5a5a5a 0%, #242424 34%, #0b0b0b 68%, #000 100%)',
      'platinum black':
        'radial-gradient(circle at 32% 28%, #8a8f96 0%, #4b5158 34%, #23272c 68%, #0c0e11 100%)',
      'black diamond cut':
        'conic-gradient(from 20deg, #080808 0deg, #8d939a 42deg, #151515 84deg, #d7dbe0 126deg, #111 168deg, #999fa6 210deg, #0b0b0b 252deg, #d3d7dc 300deg, #080808 360deg)',
      'gloss black':
        'radial-gradient(circle at 32% 28%, #575757 0%, #202020 36%, #070707 72%, #000 100%)',
      'black gloss polished':
        'conic-gradient(from 0deg, #090909 0deg, #686868 58deg, #0a0a0a 116deg, #858585 174deg, #101010 232deg, #666 290deg, #090909 360deg)',
      'carbon graphite':
        'radial-gradient(circle at 32% 28%, #969ba1 0%, #5b6067 38%, #30343a 70%, #17191d 100%)',
      'carbon gloss dark':
        'radial-gradient(circle at 32% 28%, #767d86 0%, #3f454d 42%, #191c21 100%)',
      'carbon gloss polished':
        'radial-gradient(circle at 32% 28%, #d5d8df 0%, #979da8 38%, #555d6b 72%, #252a31 100%)',
      'gloss carbon':
        'radial-gradient(circle at 32% 28%, #747a82 0%, #41464e 42%, #1b1e23 100%)',
      'brushed titanium':
        'linear-gradient(135deg, #fafafa 0%, #d7dbe0 22%, #aeb4bc 44%, #eceef1 62%, #8f969e 82%, #d5d9de 100%)',
      'brushed silver':
        'linear-gradient(135deg, #ffffff 0%, #e4e7ea 24%, #b7bdc3 46%, #f1f3f5 64%, #9fa6ad 84%, #dce0e4 100%)',
      'gloss satin silver':
        'radial-gradient(circle at 32% 28%, #ffffff 0%, #e6e9ec 36%, #b5bbc2 70%, #8f969d 100%)',
      'silver':
        'radial-gradient(circle at 32% 28%, #ffffff 0%, #e0e4e8 38%, #aeb5bc 72%, #8b9299 100%)',
      'brushed bronze':
        'linear-gradient(135deg, #f1d0a8 0%, #c6925d 28%, #8f5b2d 52%, #e4b77e 74%, #6f421f 100%)',
      'gloss bronze':
        'radial-gradient(circle at 32% 28%, #e2b582 0%, #b47843 42%, #74421e 74%, #48250f 100%)',
      'bronze':
        'radial-gradient(circle at 32% 28%, #d7a56e 0%, #a76836 44%, #653817 78%, #3f210c 100%)',
      'gunmetal':
        'radial-gradient(circle at 32% 28%, #939aa1 0%, #5d646c 42%, #30353b 74%, #171a1e 100%)',
      'candy red':
        'radial-gradient(circle at 32% 28%, #ff7a73 0%, #db1f18 42%, #8f0905 76%, #4a0200 100%)',
      'gold':
        'radial-gradient(circle at 32% 28%, #fff1a8 0%, #e5bd4f 34%, #b57a14 68%, #6f4305 100%)',
      'white':
        'radial-gradient(circle at 32% 28%, #ffffff 0%, #f7f7f7 42%, #dedede 72%, #bdbdbd 100%)',
    };

    if (normalized.includes('custom')) {
      return 'conic-gradient(from 0deg, #ff3b30 0deg 45deg, #ff9500 45deg 90deg, #ffcc00 90deg 135deg, #34c759 135deg 180deg, #00c7be 180deg 225deg, #007aff 225deg 270deg, #5856d6 270deg 315deg, #ff2d55 315deg 360deg)';
    }

    return swatches[normalized] ||
      'radial-gradient(circle at 32% 28%, #777 0%, #3b3b3b 50%, #151515 100%)';
  }

  _finishSortRank(finish) {
    const name = String(finish?.colour || finish?.color || '').trim().toLowerCase();
    if (name === 'double tinted black') return 0;
    if (name === 'platinum black') return 1;
    if (name === 'black diamond cut') return 2;
    if (name === 'gloss black') return 3;
    if (name === 'black gloss polished') return 4;
    if (name.includes('black')) return 5;
    if (name === 'carbon gloss dark') return 6;
    if (name === 'carbon graphite') return 7;
    if (name === 'carbon gloss polished') return 8;
    if (
      name === 'gloss satin silver' ||
      name === 'brushed titanium' ||
      name === 'brushed silver' ||
      name === 'silver'
    ) return 9;
    if (name === 'gloss bronze' || name === 'brushed bronze' || name === 'bronze') return 10;
    if (name === 'gloss carbon' || name === 'gunmetal') return 11;
    if (name.includes('custom')) return 99;
    return 50;
  }

  async _fetchSupplementalCustomFinishes(baseParams, visibleProducts) {
    const selectedBrand = this._getSingleSelectedBrand();
    if (!selectedBrand || selectedBrand === 'Aventador Wheels') return [];

    const visibleModels = new Set(
      (visibleProducts || [])
        .map((product) =>
          String(product?.wheel_model || product?.model || '').trim().toLowerCase()
        )
        .filter(Boolean)
    );

    if (visibleModels.size === 0) return [];

    const params = new URLSearchParams(baseParams);
    params.set('brand', selectedBrand);
    params.set('color', 'Custom Finish');
    params.set('limit', '500');
    params.set('page', '1');

    // Custom Finish is a finish option for an already visible model. It must
    // not disappear because its representative database row has another PCD,
    // ET, hub bore or load value than the row selected for the vehicle.
    [
      'make',
      'model',
      'trim',
      'axle',
      'pcd',
      'min_et',
      'max_et',
      'min_bore',
      'max_bore',
      'required_load',
      'min_load',
      'q',
      'wheel_model',
    ].forEach((key) => params.delete(key));

    const cacheKey = params.toString();
    if (!this._customFinishRowsCache) {
      this._customFinishRowsCache = new Map();
    }

    if (!this._customFinishRowsCache.has(cacheKey)) {
      const request = fetch(`${this._api}/api/v2/wheels?${params}`)
        .then((response) => response.ok ? response.json() : null)
        .then((json) => Array.isArray(json?.data) ? json.data : [])
        .catch(() => []);

      this._customFinishRowsCache.set(cacheKey, request);
    }

    const rows = await this._customFinishRowsCache.get(cacheKey);

    return rows.filter((product) => {
      const model = String(
        product?.wheel_model || product?.model || ''
      ).trim().toLowerCase();

      const colour = String(
        product?.colour || product?.color || ''
      ).trim().toLowerCase();

      return visibleModels.has(model) && colour.includes('custom');
    });
  }

  _mergeSupplementalCustomFinishes(products, customRows) {
    const merged = [...(products || [])];
    const existing = new Set(
      merged.map((product) => [
        String(product?.brand || '').trim().toLowerCase(),
        String(product?.wheel_model || product?.model || '').trim().toLowerCase(),
        String(product?.colour || product?.color || '').trim().toLowerCase(),
      ].join('|'))
    );

    for (const product of customRows || []) {
      const key = [
        String(product?.brand || '').trim().toLowerCase(),
        String(product?.wheel_model || product?.model || '').trim().toLowerCase(),
        String(product?.colour || product?.color || '').trim().toLowerCase(),
      ].join('|');

      if (!existing.has(key)) {
        existing.add(key);
        merged.push(product);
      }
    }

    return merged;
  }

  async _filterHybridForgedFinishesByShopifyAvailability(products) {
    const list = Array.isArray(products) ? products : [];
    const selectedDiameter = Math.round(Number(this._selectedDiameter) || 0);

    const make = String(this._vehicle?.makeSlug || '').trim();
    const model = String(this._vehicle?.modelSlug || '').trim();
    const trim = String(this._vehicle?.trimId || '').trim();

    // Without a complete vehicle selection, do not remove valid API results.
    if (!make || !model || !trim || !selectedDiameter) return list;

    if (!this._hybridFinishAvailabilityCache) {
      this._hybridFinishAvailabilityCache = new Map();
    }

    const isHybrid = (product) =>
      /hybrid[\s_-]*forged/i.test(
        `${product?.production_method || ''} ${product?.wheel_model || product?.model || ''} ${product?.name || ''}`
      );

    const checks = await Promise.all(
      list.map(async (product) => {
        if (!isHybrid(product)) return true;

        const brand = String(product?.brand || 'Aventador Wheels').trim();
        const wheelModel = String(
          product?.wheel_model || product?.model || ''
        ).trim();
        const colour = String(
          product?.colour || product?.color || ''
        ).trim();

        if (!wheelModel) return true;

        const cacheKey = [
          make,
          model,
          trim,
          selectedDiameter,
          brand,
          wheelModel,
          colour,
        ].join('|');

        if (!this._hybridFinishAvailabilityCache.has(cacheKey)) {
          const request = (async () => {
            try {
              const params = new URLSearchParams({
                limit: '1',
                page: '1',
                make,
                model,
                trim,
                min_size: String(selectedDiameter),
                max_size: String(selectedDiameter),
                brand,
                wheel_model: wheelModel,
              });

              if (colour) params.set('color', colour);

              const response = await fetch(
                `${this._api}/api/v2/wheels?${params.toString()}`
              );

              // A temporary API error must never hide a valid wheel card.
              if (!response.ok) return true;

              const json = await response.json();
              return (
                Number(json?.pagination?.total || 0) > 0 ||
                (Array.isArray(json?.data) && json.data.length > 0)
              );
            } catch (error) {
              console.warn(
                `Could not validate Hybrid-Forged fitment for ${wheelModel} ${colour}`,
                error
              );
              return true;
            }
          })();

          this._hybridFinishAvailabilityCache.set(cacheKey, request);
        }

        return await this._hybridFinishAvailabilityCache.get(cacheKey);
      })
    );

    return list.filter((_, index) => checks[index]);
  }

  _renderGrid(products) {
    // Group finish variants into one card for every brand so the customer gets
    // the same colour-button experience as Aventador Wheels.
    const selectedBrand = this._getSingleSelectedBrand().trim().toLowerCase();
    const isolatedProducts = selectedBrand
      ? (products || []).filter((product) =>
          String(product?.brand || '').trim().toLowerCase() === selectedBrand
        )
      : (products || []);

    const grouped = new Map();

    for (const product of isolatedProducts) {
      const brand = String(product.brand || '').trim();
      const model = String(product.wheel_model || product.model || product.name || 'Wheel').trim();
      const method = String(product.production_method || '').trim();
      const key = `${brand.toLowerCase()}|${model.toLowerCase()}|${method.toLowerCase()}`;
      if (!grouped.has(key)) grouped.set(key, { brand, model, method, finishes: [] });
      grouped.get(key).finishes.push(product);
    }

    const groups = [...grouped.values()]
      .map((group) => ({
        ...group,
        finishes: group.finishes.slice().sort((a, b) => {
          const rankDiff = this._finishSortRank(a) - this._finishSortRank(b);
          if (rankDiff !== 0) return rankDiff;
          return String(a.colour || a.color || '').localeCompare(
            String(b.colour || b.color || ''),
            'nb'
          );
        }),
      }));

    if (this._isAventadorOnlySearch()) {
      // Hybrid-Forged should be the first Aventador Wheels card whenever it
      // survives the active vehicle/diameter/PCD/ET/load filters. If it does not
      // fit the selected vehicle, it is not present in `groups` and therefore
      // cannot be promoted or rendered.
      this._aventadorCardGroups = groups
        .map((group, originalIndex) => ({ ...group, originalIndex }))
        .sort((a, b) => {
          const modelRank = (group) => {
            const model = String(group.model || '').trim().toLowerCase();

            // Hybrid-Forged Stealth is a stocked product and must always be
            // shown first whenever the existing compatibility filters say it fits.
            if (/hybrid[\s_-]*forged/i.test(`${group.method || ''} ${group.model || ''}`)) {
              return 0;
            }

            // Featured Fully Forged bestseller order follows after Hybrid-Forged.
            if (/^(forged\s+)?apex$/.test(model)) return 1;
            if (/^(forged\s+)?vantage$/.test(model)) return 2;
            if (/^(forged\s+)?titan$/.test(model)) return 3;

            return 10;
          };

          const rankDiff = modelRank(a) - modelRank(b);
          if (rankDiff !== 0) return rankDiff;

          // Preserve the API order for all other models.
          return a.originalIndex - b.originalIndex;
        });

      this._grid.innerHTML = this._aventadorCardGroups
        .map((group, index) => this._aventadorCardHtml(group, index))
        .join('');

      const modelCount = this._aventadorCardGroups.length;
      if (this._countEl) this._countEl.textContent = `${modelCount} modeller`;
      if (this._sidebarCount) this._sidebarCount.textContent = `${modelCount} modeller`;

      this._bindAventadorFinishSwitchers();
      return;
    }

    this._brandCardGroups = groups;
    this._grid.innerHTML = this._brandCardGroups
      .map((group, index) => this._brandFinishCardHtml(group, index))
      .join('');

    this._bindBrandFinishSwitchers();
  }


  _aventadorStatusHtml(method) {
    const isHybrid = /hybrid[\s_-]*forged/i.test(String(method || ''));
    const label = isHybrid ? 'På lager' : 'Bestillingsvare';
    const modifier = isHybrid ? 'in' : 'order';
    return `<span class="vs-aventador-card__status vs-aventador-card__status--${modifier}">${this._esc(label)}</span>`;
  }

  _aventadorCardHtml(group, index) {
    const first = group.finishes[0] || {};
    const img = first.main_image || '';
    const href = this._productHref(first);
    const method = group.method || 'Fully Forged';
    const finishName = first.colour || first.color || 'Standard Finish';
    const price = this._formatPrice(first);
    const statusHtml = this._aventadorStatusHtml(method);
    const isNewModel = /^(forged\s+)?(apex|vantage)$/i.test(String(group.model || '').trim());
    const newBadgeHtml = isNewModel
      ? '<span class="vs-aventador-card__new-badge">Nyhet</span>'
      : '';

    const finishButtons = group.finishes.map((finish, finishIndex) => {
      const name = finish.colour || finish.color || `Finish ${finishIndex + 1}`;
      const swatch = this._finishSwatchBackground(name);
      return `
        <button type="button"
          class="vs-aventador-finish${finishIndex === 0 ? ' is-active' : ''}"
          data-av-card="${index}"
          data-av-finish="${finishIndex}"
          aria-label="Vis ${this._esc(name)}"
          title="${this._esc(name)}">
          <span class="vs-aventador-finish__swatch" style="background:${swatch} !important"></span>
        </button>`;
    }).join('');

    return `
      <article class="vs-aventador-card" data-av-card-root="${index}">
        <a class="vs-aventador-card__media" href="${this._esc(href)}" data-av-link>
          ${newBadgeHtml}
          <img class="vs-aventador-card__image" src="${this._esc(img)}" alt="${this._esc(`Aventador Wheels ${group.model}`)}" loading="lazy" data-av-image>
        </a>
        <div class="vs-aventador-card__content">
          <div class="vs-aventador-card__method">${this._esc(method)}</div>
          <a class="vs-aventador-card__name" href="${this._esc(href)}" data-av-link>${this._esc(group.model)}</a>
          <div class="vs-aventador-card__finish" data-av-finish-name>${this._esc(finishName)}</div>
          <div class="vs-aventador-card__finish-label">VELG FARGE</div>
          <div class="vs-aventador-card__finishes" aria-label="Velg farge">${finishButtons}</div>
          <div class="vs-aventador-card__bottom">
            <span class="vs-aventador-card__price" data-av-price>${this._esc(price)}</span>
            ${statusHtml}
          </div>
        </div>
      </article>`;
  }

  _bindAventadorFinishSwitchers() {
    // If the selected finish image fails, automatically try the next available
    // image in the same model group instead of leaving an empty card.
    this._grid.querySelectorAll('[data-av-image]').forEach((image) => {
      image.addEventListener('load', () => {
        image.style.visibility = 'visible';
        image.style.opacity = '1';
        image.dataset.cacheRetried = '0';
      });

      image.addEventListener('error', () => {
        const card = image.closest('[data-av-card-root]');
        const cardIndex = Number(card?.dataset.avCardRoot);
        const group = this._aventadorCardGroups?.[cardIndex];
        if (!group) {
          image.style.visibility = 'hidden';
          return;
        }

        const currentUrl = image.currentSrc || image.src || '';
        const cleanCurrentUrl = currentUrl.replace(/([?&])__img_retry=\d+(&|$)/, '$1').replace(/[?&]$/, '');

        // Retry the same Shopify CDN image once with a cache-busting parameter.
        // This fixes stale browser/CDN cache entries that otherwise produce
        // complete=true with naturalWidth=0.
        if (image.dataset.cacheRetried !== '1' && cleanCurrentUrl) {
          image.dataset.cacheRetried = '1';
          image.style.visibility = 'visible';
          const separator = cleanCurrentUrl.includes('?') ? '&' : '?';
          image.src = `${cleanCurrentUrl}${separator}__img_retry=${Date.now()}`;
          return;
        }

        const failed = new Set(JSON.parse(image.dataset.failedImages || '[]'));
        if (cleanCurrentUrl) failed.add(cleanCurrentUrl);

        const fallback = group.finishes
          .map((finish) => finish.main_image)
          .filter(Boolean)
          .find((url) => !failed.has(new URL(url, window.location.href).href));

        image.dataset.failedImages = JSON.stringify([...failed]);
        image.dataset.cacheRetried = '0';

        if (fallback) {
          image.style.visibility = 'visible';
          const separator = fallback.includes('?') ? '&' : '?';
          image.src = `${fallback}${separator}__img_retry=${Date.now()}`;
        } else {
          image.style.visibility = 'hidden';
        }
      });
    });

    this._grid.querySelectorAll('[data-av-finish]').forEach((button) => {
      button.addEventListener('click', () => {
        const cardIndex = Number(button.dataset.avCard);
        const finishIndex = Number(button.dataset.avFinish);
        const group = this._aventadorCardGroups?.[cardIndex];
        const finish = group?.finishes?.[finishIndex];
        const card = this._grid.querySelector(`[data-av-card-root="${cardIndex}"]`);
        if (!finish || !card) return;

        card.querySelectorAll('[data-av-finish]').forEach((item) => {
          item.classList.toggle('is-active', item === button);
        });

        const image = card.querySelector('[data-av-image]');
        if (image && finish.main_image) {
          image.dataset.failedImages = '[]';
          image.style.visibility = 'visible';
          image.style.opacity = '0.35';
          window.setTimeout(() => {
            image.src = finish.main_image;
            image.alt = `Aventador Wheels ${group.model} ${finish.colour || finish.color || ''}`.trim();
            image.style.opacity = '1';
          }, 90);
        }

        const finishLabel = card.querySelector('[data-av-finish-name]');
        if (finishLabel) finishLabel.textContent = finish.colour || finish.color || 'Standard Finish';

        const price = card.querySelector('[data-av-price]');
        if (price) price.textContent = this._formatPrice(finish);

        const href = this._productHref(finish);
        card.querySelectorAll('[data-av-link]').forEach((link) => { link.href = href; });
      });
    });
  }

  _brandFinishImageUrl(group, finish) {
    const brand = String(group?.brand || '').trim().toLowerCase();
    const model = String(group?.model || '').trim().toUpperCase();
    const colour = String(finish?.colour || finish?.color || '').trim().toLowerCase();

    if (
      brand === 'concaver' &&
      model === 'CVR4' &&
      colour === 'double tinted black'
    ) {
      return 'https://sbfwheels.no/cdn/shop/files/40953_0_27758_dd9df604-2dab-4593-a05c-af0e5214edf6.jpg?v=1782367364&width=832';
    }

    return String(finish?.main_image || '').trim();
  }

  async _resolveExactFinishImage(finish) {
    const handle = String(finish?.group_handle || '').trim();
    if (!handle) return '';

    if (!this._exactFinishImageCache) {
      this._exactFinishImageCache = new Map();
    }

    if (this._exactFinishImageCache.has(handle)) {
      return this._exactFinishImageCache.get(handle);
    }

    const request = (async () => {
      try {
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) return '';

        const product = await response.json();
        return String(
          product?.featured_image ||
          product?.images?.[0] ||
          ''
        ).trim();
      } catch (_) {
        return '';
      }
    })();

    this._exactFinishImageCache.set(handle, request);
    return request;
  }

  _setExactBrandFinishImage(image, group, finish) {
    if (!image || !finish) return;

    const initialUrl = this._brandFinishImageUrl(group, finish);
    const token = `${Date.now()}-${Math.random()}`;
    const absoluteInitialUrl = initialUrl
      ? new URL(initialUrl, window.location.href).href
      : '';

    image.dataset.loadToken = token;
    image.dataset.storefrontResolved = '0';

    image.onload = () => {
      if (image.dataset.loadToken !== token) return;
      image.style.visibility = 'visible';
      image.style.opacity = '1';
    };

    image.onerror = async () => {
      if (image.dataset.loadToken !== token) return;

      if (image.dataset.storefrontResolved !== '1') {
        image.dataset.storefrontResolved = '1';
        const storefrontImage = await this._resolveExactFinishImage(finish);

        if (
          storefrontImage &&
          image.dataset.loadToken === token &&
          new URL(storefrontImage, window.location.href).href !== image.src
        ) {
          image.style.visibility = 'visible';
          image.src = storefrontImage;
          return;
        }
      }

      if (image.dataset.loadToken === token) {
        image.style.visibility = 'hidden';
        image.style.opacity = '1';
      }
    };

    image.alt = `${group?.brand || ''} ${group?.model || ''} ${
      finish?.colour || finish?.color || ''
    }`.trim();

    if (!initialUrl) {
      image.style.visibility = 'hidden';
      image.style.opacity = '1';
      image.onerror();
      return;
    }

    image.style.visibility = 'visible';

    // The card HTML already contains the correct initial src. Do not assign the
    // same URL again, because that restarts image loading and doubles requests.
    if (image.src === absoluteInitialUrl) {
      if (image.complete && image.naturalWidth > 0) {
        image.style.opacity = '1';
      } else {
        image.style.opacity = '0.35';
      }
      return;
    }

    image.style.opacity = '0.35';
    image.src = initialUrl;
  }

  _brandFinishCardHtml(group, index) {
    const first = group.finishes[0] || {};
    const img = this._brandFinishImageUrl(group, first);
    const href = this._productHref(first);
    const finishName = first.colour || first.color || 'Standard Finish';
    const price = this._formatPrice(first);
    const badge = group.brand || group.method || 'Wheel';
    const statusHtml = this._aventadorStatusHtml(group.method || '');

    const finishButtons = group.finishes.map((finish, finishIndex) => {
      const name = finish.colour || finish.color || `Finish ${finishIndex + 1}`;
      const swatch = this._finishSwatchBackground(name);
      return `
        <button type="button"
          class="vs-aventador-finish${finishIndex === 0 ? ' is-active' : ''}"
          data-brand-card="${index}"
          data-brand-finish="${finishIndex}"
          aria-label="Vis ${this._esc(name)}"
          title="${this._esc(name)}">
          <span class="vs-aventador-finish__swatch" style="background:${swatch} !important"></span>
        </button>`;
    }).join('');

    return `
      <article class="vs-aventador-card" data-brand-card-root="${index}">
        <a class="vs-aventador-card__media" href="${this._esc(href)}" data-brand-link>
          <img class="vs-aventador-card__image" src="${this._esc(img)}" alt="${this._esc(`${group.brand} ${group.model}`)}" loading="lazy" data-brand-image>
        </a>
        <div class="vs-aventador-card__content">
          <div class="vs-aventador-card__method">${this._esc(badge)}</div>
          <a class="vs-aventador-card__name" href="${this._esc(href)}" data-brand-link>${this._esc(group.model)}</a>
          <div class="vs-aventador-card__finish" data-brand-finish-name>${this._esc(finishName)}</div>
          <div class="vs-aventador-card__finish-label">VELG FARGE</div>
          <div class="vs-aventador-card__finishes" aria-label="Velg farge">${finishButtons}</div>
          <div class="vs-aventador-card__bottom">
            <span class="vs-aventador-card__price" data-brand-price>${this._esc(price)}</span>
            ${statusHtml}
          </div>
        </div>
      </article>`;
  }

  _bindBrandFinishSwitchers() {
    this._grid.querySelectorAll('[data-brand-card-root]').forEach((card) => {
      const cardIndex = Number(card.dataset.brandCardRoot);
      const group = this._brandCardGroups?.[cardIndex];
      const finish = group?.finishes?.[0];
      const image = card.querySelector('[data-brand-image]');

      if (group && finish && image) {
        this._setExactBrandFinishImage(image, group, finish);
      }
    });

    this._grid.querySelectorAll('[data-brand-finish]').forEach((button) => {
      button.addEventListener('click', () => {
        const cardIndex = Number(button.dataset.brandCard);
        const finishIndex = Number(button.dataset.brandFinish);
        const group = this._brandCardGroups?.[cardIndex];
        const finish = group?.finishes?.[finishIndex];
        const card = this._grid.querySelector(
          `[data-brand-card-root="${cardIndex}"]`
        );

        if (!finish || !group || !card) return;

        card.querySelectorAll('[data-brand-finish]').forEach((item) => {
          item.classList.toggle('is-active', item === button);
        });

        const image = card.querySelector('[data-brand-image]');
        if (image) {
          this._setExactBrandFinishImage(image, group, finish);
        }

        const finishLabel = card.querySelector('[data-brand-finish-name]');
        if (finishLabel) {
          finishLabel.textContent =
            finish.colour || finish.color || 'Standard Finish';
        }

        const price = card.querySelector('[data-brand-price]');
        if (price) {
          price.textContent = this._formatPrice(finish);
        }

        const href = this._productHref(finish);
        card.querySelectorAll('[data-brand-link]').forEach((link) => {
          link.href = href;
        });
      });
    });
  }

  _cardHtml(g) {
    let href = g.group_handle ? `/products/${g.group_handle}` : '#';
    const cardIsHybridForged = /hybrid[\s_-]*forged/i.test(
      `${g.production_method || g.method || ''} ${g.wheel_model || g.model || ''} ${g.name || ''}`
    );

    if (this._selectedDiameter && g.group_handle) {
      href += `?diameter=${this._selectedDiameter}`;
      if (this._selectedDiameterIsCustom && !cardIsHybridForged) {
        href += '&custom_fit=1';
      } else {
        const activeOemButton = this._diameterBar?.querySelector('.vs__diam-btn--active[data-custom="0"]');
        const rearDiameter = parseInt(activeOemButton?.dataset.rearDiam || '', 10);
        if (Number.isFinite(rearDiameter) && rearDiameter > 0) {
          href += `&rear_diameter=${rearDiameter}`;
        }
        if (this._selectedOemSetKey) href += `&oem_set=${encodeURIComponent(this._selectedOemSetKey)}`;
        if (cardIsHybridForged) href += '&recommended_fit=1';
      }
    }
    const img = g.main_image || '';

    // Price: range or single value
    let priceStr = '';
    if (g.min_price_nok != null) {
      if (g.max_price_nok != null && g.max_price_nok !== g.min_price_nok) {
        priceStr = `fra ${Math.round(g.min_price_nok).toLocaleString('nb-NO')} kr`;
      } else {
        priceStr = `${Math.round(g.min_price_nok).toLocaleString('nb-NO')} kr`;
      }
    }

    let beforePriceHtml = '';
    if (g.before_price_nok != null && g.before_price_nok > 0) {
      beforePriceHtml = `<span style="font-size:11px;color:#9ca3af;text-decoration:line-through;display:block;line-height:1.4;margin-bottom:1px;">Før: ${Math.round(g.before_price_nok).toLocaleString('nb-NO')} kr</span>`;
    }

    const isAventador = (g.brand || '').toLowerCase().includes('aventador');
    const isHybridForged = /hybrid[\s_-]*forged/i.test(
      `${g.production_method || ''} ${g.wheel_model || g.model || ''} ${g.name || ''}`
    );

    let stockBadge;
    if (isHybridForged) {
      stockBadge = '<span class="vs__card-stock vs__card-stock--in">Lagervare</span>';
    } else if (isAventador && !g.has_stock) {
      stockBadge = '<span class="vs__card-stock vs__card-stock--order">Bestillingsvare</span>';
    } else if (!g.has_stock) {
      stockBadge = '<span class="vs__card-stock vs__card-stock--out">Utsolgt</span>';
    } else if (g.total_stock <= 4) {
      stockBadge = `<span class="vs__card-stock vs__card-stock--low">Kun ${g.total_stock} igjen!</span>`;
    } else {
      stockBadge = '<span class="vs__card-stock vs__card-stock--in">På lager</span>';
    }

    // Available sizes (e.g. 18" 19" 20")
    const sizesStr = g.available_sizes?.length
      ? g.available_sizes.map((s) => s + '"').join('  ')
      : '';

    // Available ET values — full list if ≤5, range if more
    let etsStr = '';
    if (g.available_ets?.length > 1) {
      const ets = g.available_ets;
      if (ets.length <= 5) {
        etsStr = ets.map((e) => 'ET' + e).join('  ');
      } else {
        etsStr = 'ET' + ets[0] + '\u2013' + ets[ets.length - 1];
      }
    } else if (g.available_ets?.length === 1) {
      etsStr = 'ET' + g.available_ets[0];
    }

    // Variant count badge
    const variantBadge = g.variant_count > 1
      ? `<span class="vs__card-variants">${g.variant_count} varianter</span>`
      : '';

    return `
      <a class="vs__card" href="${this._esc(href)}">
        <img class="vs__card-img" src="${this._esc(img)}" alt="${this._esc(g.brand + ' ' + g.wheel_model)}" loading="lazy">
        <div class="vs__card-body">
          <span class="vs__card-brand">${this._esc(g.brand || '')}</span>
          <span class="vs__card-name">${this._esc(g.wheel_model || '')}</span>
          <span class="vs__card-colour">${this._esc(g.colour || '')}</span>
          <span class="vs__card-specs">${this._esc(sizesStr)}${etsStr ? '  \u00b7  ' + this._esc(etsStr) : ''}</span>
          <div class="vs__card-footer">
            <div>${beforePriceHtml}<span class="vs__card-price">${priceStr}</span></div>
            ${stockBadge}
          </div>
          <div class="vs__card-meta">${variantBadge}${stockBadge}</div>
        </div>
      </a>`;
  }

  _esc(str) {
    const d = document.createElement('div');
    d.textContent = String(str);
    return d.innerHTML;
  }

  _resetAccordions() {
    Object.entries(this._accordions).forEach(([key, acc]) => {
      if (!acc) return;
      acc.querySelectorAll('input[type="checkbox"]').forEach(cb => { cb.checked = false; });
      const search = acc.querySelector('[data-role="accordion-search"]');
      if (search) search.value = '';
      this._applyCheckboxFilter(key);
      // Collapse all except brand
      const body = acc.querySelector('.vs__accordion-body');
      const chevron = acc.querySelector('.vs__accordion-chevron');
      if (key === 'brand') {
        if (body) body.classList.remove('is-collapsed');
        if (chevron) chevron.classList.add('is-rotated');
      } else {
        if (body) body.classList.add('is-collapsed');
        if (chevron) chevron.classList.remove('is-rotated');
      }
    });
    // Also collapse slider accordions
    this.querySelectorAll('[data-slider-accordion]').forEach(acc => {
      const body = acc.querySelector('.vs__accordion-body');
      const chevron = acc.querySelector('.vs__accordion-chevron');
      if (body) body.classList.add('is-collapsed');
      if (chevron) chevron.classList.remove('is-rotated');
    });
  }

  // ── Pagination ───────────────────────────────────────────────────────

  _renderPagination(pagination) {
    const { page, total_pages } = pagination;
    if (total_pages <= 1) {
      this._pagination.innerHTML = '';
      return;
    }

    const buttons = [];
    buttons.push(
      `<button class="vs__page-btn" data-page="${page - 1}" ${page <= 1 ? 'disabled' : ''}>&larr;</button>`
    );

    const start = Math.max(1, page - 3);
    const end = Math.min(total_pages, page + 3);
    if (start > 1) {
      buttons.push(`<button class="vs__page-btn" data-page="1">1</button>`);
      if (start > 2) buttons.push(`<span style="padding:0.35rem">\u2026</span>`);
    }
    for (let i = start; i <= end; i++) {
      const active = i === page ? ' vs__page-btn--active' : '';
      buttons.push(`<button class="vs__page-btn${active}" data-page="${i}">${i}</button>`);
    }
    if (end < total_pages) {
      if (end < total_pages - 1) buttons.push(`<span style="padding:0.35rem">\u2026</span>`);
      buttons.push(`<button class="vs__page-btn" data-page="${total_pages}">${total_pages}</button>`);
    }

    buttons.push(
      `<button class="vs__page-btn" data-page="${page + 1}" ${page >= total_pages ? 'disabled' : ''}>&rarr;</button>`
    );

    this._pagination.innerHTML = buttons.join('');
    this._pagination.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-page]');
      if (!btn || btn.disabled) return;
      this._page = parseInt(btn.dataset.page, 10);
      this._search();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // ── Update sidebar filters from API facets ──────────────────────────

  _updateFacets(facets) {
    if (!facets) return;
    const hasVehicle = !!this._vehicle?.specs;
    this._updateFacetSelect(this._filters.size, facets.size, (v) => `${v}"`, (v) => hasVehicle ? `${v}" (OEM)` : `${v}"`);
    // Sync size dropdown with diameter button selection
    if (this._selectedDiameter && this._filters.size) {
      this._filters.size.value = String(this._selectedDiameter);
    }
    this._updateFacetSelect(this._filters.width, facets.width, (v) => `${v}J`);
    this._updateFacetCheckboxes('color', facets.colour);
    this._updateFacetCheckboxes('brand', facets.brand);
    this._updateFacetCheckboxes('wheel_model', facets.wheel_model);
    // Filter PCD facet to vehicle PCD when a vehicle is active
    let pcdItems = facets.pcd;
    if (this._vehicle?.specs?.pcd) {
      pcdItems = pcdItems?.filter((item) => item.value === this._vehicle.specs.pcd);
    }
    this._updateFacetSelect(this._filters.pcd, pcdItems);
    if (this._vehicle?.specs?.pcd && this._filters.pcd) {
      this._filters.pcd.value = this._vehicle.specs.pcd;
    }
    // Auto-set bore slider and ET label from vehicle specs — only on first load
    if (this._vehicle?.specs && !this._slidersInitialized) {
      this._slidersInitialized = true;
      const sp = this._vehicle.specs;

      let bore;
      if (this._axle === 'rear') {
        bore = sp.center_bore_rear || sp.center_bore;
      } else if (this._axle === 'front') {
        bore = sp.center_bore;
      } else {
        bore = Math.min(sp.center_bore || 999, sp.center_bore_rear || 999);
        if (bore === 999) bore = 0;
      }
      if (bore > 0) {
        this._setSlider('bore', bore, null);
        this._rangeInputs.min_bore.value = bore;
        const boreSlider = this._sliders.bore;
        if (boreSlider?.rec) {
          boreSlider.recRange.textContent = `${bore} mm`;
          boreSlider.rec.hidden = false;
        }
      }
    }

    // Update ET label per selected diameter (runs every search)
    this._updateEtLabel();
  }

  _updateFacetSelect(select, facetItems, _unused, labelFn) {
    if (!select || !facetItems) return;
    const current = select.value;
    // Preserve the placeholder text from the first option (e.g. "Diameter", "Bredde")
    const placeholder = select.options[0]?.textContent || 'Alle';

    const options = [`<option value="">${this._esc(placeholder)}</option>`];
    for (const item of facetItems) {
      const label = labelFn ? labelFn(item.value, item.count) : item.value;
      options.push(`<option value="${item.value}">${this._esc(String(label))}</option>`);
    }
    select.innerHTML = options.join('');

    // Restore selection if the value still exists in the facet list
    if (current) {
      const exists = facetItems.some((item) => String(item.value) === String(current));
      if (exists) {
        select.value = current;
      }
    }
  }


  _updateFacetCheckboxes(key, facetItems) {
    if (key === 'brand' && Array.isArray(facetItems)) {
      this._brandFacetItems = facetItems;
      if (!this._selectedDiameterIsCustom && facetItems.length > 1) {
        this._allBrandFacetItems = facetItems;
      }
    }
    if (key === 'wheel_model' && Array.isArray(facetItems)) {
      this._modelFacetItems = facetItems;
      this._pendingBrandSheetModels = false;
    }
    const acc = this._accordions[key];
    if (!acc || !facetItems) return;
    const list = acc.querySelector('[data-role="checkbox-list"]');
    if (!list) return;


    // Brand is strictly single-select. Only the current selected brand may
    // survive when facets are rebuilt.
    const checked = key === 'brand'
      ? new Set(this._brandSelection ? [this._brandSelection] : [])
      : new Set([...(list.querySelectorAll('input:checked') || [])].map(cb => cb.value));

    // Apply restored session state
    if (this._restoredAccordions && this._restoredAccordions[key]) {
      if (key === 'brand') {
        const restoredBrand =
          this._brandSelection ||
          this._restoredAccordions[key][0] ||
          '';
        checked.clear();
        if (restoredBrand) {
          checked.add(restoredBrand);
          this._brandSelection = restoredBrand;
        }
      } else {
        this._restoredAccordions[key].forEach(val => checked.add(val));
      }
      delete this._restoredAccordions[key];
    }

    if (key === 'brand' && this._brandSelection && checked.size === 0) {
      checked.add(this._brandSelection);
    }

    // Render all items (visibility handled by _applyCheckboxFilter)
    const items = [];
    for (const item of facetItems) {
      const isChecked = checked.has(String(item.value)) ? ' checked' : '';
      items.push(
        `<label class="vs__checkbox-item" data-index="${items.length}">` +
        `<input type="checkbox" value="${this._esc(String(item.value))}"${isChecked}>` +
        `<span class="vs__checkbox-name">${this._esc(String(item.value))}</span>` +
        `<span class="vs__checkbox-count">${item.count}</span>` +
        `</label>`
      );
    }
    list.innerHTML = items.join('');

    // Apply visibility (respects search query + show-more state)
    this._applyCheckboxFilter(key);
    if (key === 'brand') {
      this._updateMobileBrandTrigger();
      this._renderMobileBrandSheet();
    }
    if (key === 'wheel_model' && this._brandSheet?.classList.contains('is-open')) {
      this._renderMobileBrandSheet();
    }
  }

  _applyCheckboxFilter(key) {
    const acc = this._accordions[key];
    if (!acc) return;
    const list = acc.querySelector('[data-role="checkbox-list"]');
    const searchInput = acc.querySelector('[data-role="accordion-search"]');
    const q = (searchInput?.value || '').trim().toLowerCase();
    if (!list) return;

    list.querySelectorAll('.vs__checkbox-item').forEach(item => {
      if (!q) {
        item.style.display = '';
        return;
      }
      const name = item.querySelector('.vs__checkbox-name');
      const text = (name ? name.textContent : item.textContent).toLowerCase();
      item.style.display = text.includes(q) ? '' : 'none';
    });
  }

  _updateEtLabel() {
    const etSlider = this._sliders.et;
    if (!etSlider?.rec) return;
    const sp = this._vehicle?.specs;
    if (!sp?.options?.length) { etSlider.rec.hidden = true; return; }

    const diam = this._selectedDiameter;
    const axle = this._axle || 'all';
    const makeName = this._vehicle?.makeName || this._vehicle?.makeSlug || '';

    if (!diam) {
      etSlider.recRange.textContent = 'Velg diameter for OEM innpress';
      etSlider.rec.hidden = false;
      return;
    }

    const opt = sp.options.find(o => Math.round(o.diameter_front) === diam || Math.round(o.diameter_rear) === diam);
    if (opt) {
      let et;
      if (axle === 'rear') et = opt.et_rear || opt.et_front;
      else et = opt.et_front;
      if (et) {
        etSlider.recRange.textContent = `${makeName} ${diam}" bruker ET${Math.round(et)}`;
        etSlider.rec.hidden = false;
        return;
      }
    }

    etSlider.rec.hidden = true;
  }

  // ── Inline vehicle selector ────────────────────────────────────────

  _initInlineSelector() {
    if (!this._hMake) return;

    this._hMake.addEventListener('change', () => this._onMakeChange());
    this._hModel.addEventListener('change', () => this._onModelChange());
    this._hYear.addEventListener('change', () => this._onYearChange());
    this._hTrim.addEventListener('change', () => {
      if (this._hSearch) this._hSearch.disabled = !this._hTrim.value;
      this._updateMobileSteps();
    });
    if (this._hSearch) {
      this._hSearch.addEventListener('click', () => this._onSelectorSearch());
    }
  }

  _updateMobileSteps() {
    const hasMake = !!this._hMake?.value;
    if (hasMake) this.dataset.makeSelected = '';
    else delete this.dataset.makeSelected;

    const reveal = (el, show) => {
      if (!el) return;
      if (show) el.dataset.revealed = '';
      else delete el.dataset.revealed;
    };
    reveal(this._hModel, !this._hModel?.disabled);
    reveal(this._hYear, !this._hYear?.disabled);
    reveal(this._hTrim, !this._hTrim?.disabled);
    reveal(this._hSearch, !this._hSearch?.disabled);
  }

  async _loadMakes() {
    if (this._makes.length > 0) return;
    try {
      this._makes = (await this._fetchJson('/api/vehicles/makes')) || [];
      this._populateSelect(this._hMake, this._makes, 'Merke', (m) => ({
        value: m.slug,
        label: m.name,
      }));
      this._hMake.disabled = false;
      this._updateMobileSteps();
    } catch (e) {
      console.error('Failed to load makes', e);
    }
  }

  async _onMakeChange() {
    const slug = this._hMake.value;
    // Toggle vehicle SØK vs regnr SØK visibility
    if (slug) this.dataset.makeSelected = '';
    else delete this.dataset.makeSelected;
    this._resetSelect(this._hModel, 'Modell');
    this._resetSelect(this._hYear, '\u00c5r');
    this._resetSelect(this._hTrim, 'Type');
    if (this._hSearch) this._hSearch.disabled = true;
    // Clear regnr when make selected
    if (slug && this._hRegnr) {
      this._hRegnr.value = '';
      if (this._hRegnrSearch) {
        this._hRegnrSearch.disabled = true;
        delete this._hRegnrSearch.dataset.revealed;
      }
    }
    this._updateMobileSteps();
    if (!slug) return;
    try {
      this._models = (await this._fetchJson(`/api/vehicles/makes/${slug}`)) || [];
      this._populateSelect(this._hModel, this._models, 'Modell', (m) => {
        return { value: m.slug, label: m.ModelName || m.modelName || m.name || '' };
      });
      this._hModel.disabled = false;
      this._updateMobileSteps();
    } catch (e) {
      console.error('Failed to load models', e);
    }
  }

  async _onModelChange() {
    const makeSlug = this._hMake.value;
    const modelSlug = this._hModel.value;
    this._resetSelect(this._hYear, '\u00c5r');
    this._resetSelect(this._hTrim, 'Type');
    if (this._hSearch) this._hSearch.disabled = true;
    this._updateMobileSteps();
    if (!makeSlug || !modelSlug) return;
    try {
      this._years =
        (await this._fetchJson(`/api/vehicles/makes/${makeSlug}/models/${modelSlug}`)) || [];
      this._populateSelect(this._hYear, this._years, '\u00c5r', (y) => ({ value: y, label: y }));
      this._hYear.disabled = false;
      this._updateMobileSteps();
    } catch (e) {
      console.error('Failed to load years', e);
    }
  }

  async _onYearChange() {
    const makeSlug = this._hMake.value;
    const modelSlug = this._hModel.value;
    const year = this._hYear.value;
    this._resetSelect(this._hTrim, 'Type');
    if (this._hSearch) this._hSearch.disabled = true;
    this._updateMobileSteps();
    if (!makeSlug || !modelSlug || !year) return;
    try {
      this._trims =
        (await this._fetchJson(`/api/vehicles/makes/${makeSlug}/models/${modelSlug}/years/${year}`)) || [];
      this._populateSelect(this._hTrim, this._trims, 'Type', (t) => {
        const name = t.CarName || t.carName || t.name || '';
        return { value: String(t.CarID || t.carId || t.id || ''), label: name };
      });
      this._hTrim.disabled = false;
      this._updateMobileSteps();
    } catch (e) {
      console.error('Failed to load trims', e);
    }
  }

  _onSelectorSearch() {
    const makeSlug = this._hMake.value;
    const modelSlug = this._hModel.value;
    const trimId = this._hTrim.value;
    if (!makeSlug || !modelSlug || !trimId) return;

    const make = this._makes.find((m) => m.slug === makeSlug);
    const model = this._models.find((m) => m.slug === modelSlug);
    const year = this._hYear.value;
    const trim = this._trims.find(
      (t) => String(t.CarID || t.carId || t.id || '') === trimId
    );

    this._vehicle = {
      makeSlug,
      makeName: make?.name || makeSlug,
      modelSlug,
      modelName: (model?.ModelName || model?.modelName || model?.name || modelSlug),
      year,
      trimId,
      trimName: trim?.CarName || trim?.carName || trim?.name || trimId,
    };
    saveVehicle(this._vehicle);

    const urlParams = new URLSearchParams({ make: makeSlug, model: modelSlug, trim: trimId });
    if (this._queryText) urlParams.set('q', this._queryText);
    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);

    this._showVehicleHeader();
    this._selectedDiameter = null;
    this._selectedDiameterIsCustom = false;
    this._selectedOemSetKey = '';
    this._resetOemFallbackState();
    this._slidersInitialized = false;
    this._page = 1;
    // Reset filters for fresh vehicle search
    Object.values(this._filters).forEach((sel) => { if (sel) sel.value = ''; });
    this._resetAccordions();
    this._brandSelection = this._defaultBrand;
    this._modelSelection = '';
    Object.values(this._rangeInputs).forEach((input) => { if (input) input.value = ''; });
    this._resetSlider('et');
    this._resetSlider('bore');
    this._resetSlider('price');
    this._fetchVehicleSpecsThenSearch();
  }

  // ── Shared helpers ───────────────────────────────────────────────────

  async _fetchJson(path) {
    const res = await fetch(`${this._api}${path}`);
    if (!res.ok) throw new Error(`API ${res.status}`);
    const json = await res.json();
    return json.data ?? json;
  }

  _populateSelect(select, items, placeholder, mapFn) {
    const options = [`<option value="">${placeholder}</option>`];
    for (const item of items) {
      const { value, label } = mapFn(item);
      options.push(`<option value="${this._esc(String(value))}">${this._esc(label)}</option>`);
    }
    select.innerHTML = options.join('');
  }

  _resetSelect(select, placeholder) {
    select.innerHTML = `<option value="">${placeholder}</option>`;
    select.disabled = true;
  }
}

if (!customElements.get('vehicle-search')) {
  customElements.define('vehicle-search', VehicleSearchElement);
}